/**
 * Shared Error Catalog — the single source of honest, plain-English user-facing
 * copy for every error the platform knows how to translate.
 *
 * This catalog lives in `@order-up/common` (which depends ONLY on
 * `@order-up/models`). It MUST NOT import `firebase`, `@order-up/core`, or
 * `@order-up/client`. Firebase errors are matched STRUCTURALLY by their string
 * `code` — never via `instanceof FirebaseError`. See `translateError.ts`.
 *
 * Authoring rules:
 *  - `message` is ONE honest, plain-English sentence (occasionally two) — the
 *    primary user-facing copy. It must NEVER contain a raw machine code
 *    (`auth/...`, `appCheck/...`) or a `Firebase:` prefix.
 *  - `title` is a short headline. May be empty string only for suppressed
 *    entries (the UI renders nothing for those).
 *  - `severity` buckets the error for styling/routing.
 *  - `retryable` drives whether a "Try again" affordance is offered.
 *  - `suppress: true` means the UI renders NOTHING (e.g. user closed a popup).
 */

import {
  CASH_AMOUNT_MAX_CENTS,
  TAX_EXEMPTION_DOCUMENT_MAX,
  TAX_EXEMPTION_DOCUMENT_MAX_BYTES,
  formatCents,
} from "@order-up/models";

import type { ErrorSeverity } from "./translateError";

export interface CatalogEntry {
  title: string;
  /** One honest, plain-English sentence — the primary user-facing copy. */
  message: string;
  severity: ErrorSeverity;
  retryable: boolean;
  nextSteps?: string[];
  /** When true the UI renders NOTHING. */
  suppress?: boolean;
  /**
   * When true, a server-supplied message REPLACES `message` if one is present.
   *
   * For most codes the catalog sentence is the better copy: the server says
   * "constraint violation on column x" and the guest needs "that email is
   * already registered". For a VALIDATION REFUSAL the relationship inverts —
   * the server's sentence is the only thing carrying the specifics, and the
   * catalog can only ever say something generic.
   *
   * A menu-board publish refusal is the worked example: the API names WHICH
   * connectors the target appliance actually reported, or WHICH zone slot the
   * chosen template does not render. Collapsing that into "Please check your
   * input and try again" leaves an owner standing in front of a board they
   * cannot publish with no idea why — which is the exact failure the refusals
   * exist to prevent.
   *
   * `message` here is still required and must be honest on its own: it is what
   * renders when the server sent nothing usable.
   */
  preferServerMessage?: boolean;
}

/**
 * Suppressed entry shared by all "user closed/cancelled the popup" codes. The
 * UI renders nothing for these — they are not failures the user needs to see.
 */
const SUPPRESSED: CatalogEntry = {
  title: "",
  message: "",
  severity: "user_input",
  retryable: true,
  suppress: true,
};

const ENTRIES: Record<string, CatalogEntry> = {
  // ── ONLINE CHECKOUT AVAILABILITY ────────────────────────────────────────────
  // Both are answered BEFORE any card is touched, so each says the card was
  // not charged: a guest who reads "failed" at checkout assumes the worst.
  // ITEM_UNAVAILABLE's server sentence names what sold out, so it wins.
  ITEM_UNAVAILABLE: {
    severity: "user_input",
    title: "Something in your cart just sold out",
    message:
      "One or more items are no longer available. Your card has not been charged — remove or swap them and place your order again.",
    retryable: false,
    preferServerMessage: true,
  },
  // COURIER QUOTE REFUSAL — delivery is refused when the location has no
  // complete courier link or OrderUp cannot collect from it. Answered before
  // any card is touched; pickup is unaffected.
  COURIER_UNAVAILABLE: {
    severity: "user_input",
    title: "Delivery isn't available",
    message: "Delivery isn't available from this location right now. Pickup is still available.",
    retryable: false,
    nextSteps: ["Choose pickup instead"],
  },
  // ── ORDERUP COURIERS (merchant: integrations card, dispatch) ────────────────
  // What a manager reads when linking or using OrderUp couriers fails. Never
  // names the courier network (public-copy rule).
  COURIER_BILLING_NOT_IN_STANDING: {
    severity: "user_input",
    title: "OrderUp couriers are paused",
    message:
      "OrderUp couriers are paused for this location until its OrderUp billing is up to date. Nothing was changed.",
    retryable: false,
    nextSteps: ["Update the payment method in Billing", "Contact OrderUp support if the account is already current"],
  },
  COURIER_NOT_LINKED: {
    severity: "user_input",
    title: "OrderUp couriers aren't set up here",
    message: "This location isn't set up for OrderUp couriers yet, so no courier was requested.",
    retryable: false,
    nextSteps: ["Open Integrations and set up OrderUp couriers for this location"],
  },
  COURIER_ACCOUNT_PROVISIONING: {
    severity: "service",
    title: "OrderUp couriers are still being set up",
    message:
      "Your OrderUp couriers account is still being created. Wait a minute and try again — if this keeps happening, OrderUp support has been told.",
    retryable: true,
  },
  COURIER_LOCATION_PHONE_MISSING: {
    severity: "user_input",
    title: "Add the location's phone number",
    message:
      "OrderUp couriers need this location's phone number so drivers can reach the store. Nothing was changed.",
    retryable: false,
    nextSteps: ["Add a phone number in the location's settings, then set up OrderUp couriers again"],
  },
  DRIVE_LINK_FAILED: {
    severity: "service",
    title: "Couldn't set up OrderUp couriers",
    message: "We couldn't set up OrderUp couriers for this location. Nothing was charged — please try again.",
    retryable: true,
    nextSteps: ["Try again in a few minutes", "Contact OrderUp support if it keeps failing"],
  },
  DRIVE_RECONCILE_FAILED: {
    severity: "service",
    title: "Couldn't set up OrderUp couriers",
    message: "We couldn't set up OrderUp couriers for this location just now. Your existing setup was left as it was — please try again.",
    retryable: true,
    nextSteps: ["Try again in a few minutes", "Contact OrderUp support if it keeps failing"],
  },
  // ── ONLINE CHECKOUT PRICING (spec A1) ───────────────────────────────────────
  // The checkout prices the cart from the live menu, never from the prices the
  // browser stored. All three are answered before any card is touched.
  // PRICE_CHANGED: the server total is not the one the guest last saw — the
  // checkout page refreshes its preview and asks them to review it.
  PRICE_CHANGED: {
    severity: "user_input",
    title: "Your total changed",
    message:
      "The order total has changed. Your card has not been charged — please review the updated pricing and try again.",
    retryable: true,
  },
  // CART_ITEM_CHANGED: a line the menu can no longer price (the item, its size
  // or one of its options is gone). The server sentence names the line.
  CART_ITEM_CHANGED: {
    severity: "user_input",
    title: "Something in your cart changed",
    message:
      "An item in your cart has changed on the menu. Your card has not been charged — remove it, add it again, and place your order.",
    retryable: false,
    preferServerMessage: true,
  },
  // SCHEDULED MENU PRICING (Bar v2 Part D2) — the server prices each line on
  // the route that prices it NOW (an open Happy Hour, or not). Both name the
  // line and the reason in the server sentence, so it wins; the checkout lists
  // every line from `details.lines` and sends the guest back to the menu, where
  // the cart is re-priced (or the line removed) on their confirmation.
  SCHEDULED_PRICE_CHANGED: {
    severity: "user_input",
    title: "Some prices have changed",
    message:
      "A price in your cart has changed since you added it. Your card has not been charged — review the new price and place your order again.",
    retryable: false,
    preferServerMessage: true,
  },
  ITEM_NOT_AVAILABLE_NOW: {
    severity: "user_input",
    title: "Something in your cart isn't available right now",
    message:
      "An item in your cart can't be ordered at this time. Your card has not been charged — remove it and place your order again.",
    retryable: false,
    preferServerMessage: true,
  },
  PRICE_UNVERIFIED: {
    severity: "service",
    title: "We couldn't confirm your order",
    message:
      "We couldn't confirm your order's prices right now. Your card has not been charged — please try again.",
    retryable: true,
  },
  AVAILABILITY_UNVERIFIED: {
    severity: "service",
    title: "We couldn't confirm your order",
    message:
      "We couldn't confirm your items are available right now. Your card has not been charged — please try again.",
    retryable: true,
  },
  // ── MENU BOARDS (digital signage) ───────────────────────────────────────────
  // All three set `preferServerMessage`: the API's sentence is the only thing
  // that says WHICH connectors the appliance reported, or WHICH zone slot the
  // template does not render. See docs/MENU_BOARD_ARCHITECTURE.md §9 — a refusal
  // has to be visible, and generic copy moves the confusion rather than removing
  // it. The messages below are the honest fallback when the server sent nothing.
  INVALID_MENU_BOARD: {
    severity: "user_input",
    title: "This board cannot be published yet",
    message:
      "Something in this board is not ready to go on a screen. Open the board to see which part.",
    // A validation refusal is not transient. A "Try again" button here invites
    // an owner to click it until they conclude the product is broken.
    retryable: false,
    preferServerMessage: true,
  },
  MENU_BOARD_CONFLICT: {
    severity: "user_input",
    title: "This board changed while you were editing",
    message:
      "Someone else saved this board while you had it open. Reload to pick up their changes before publishing.",
    retryable: false,
    preferServerMessage: true,
  },
  // ── PRINTED MENUS (docs/PRINTED_MENU_ARCHITECTURE.md) ───────────────────────
  // Refusals from /api/v1/printed-menus. The server's sentence names what to do
  // (which field, that the menu moved), so most prefer it.
  INVALID_PRINTED_MENU: {
    severity: "user_input",
    title: "This printed menu needs a change",
    message: "Something in this printed menu is not ready. Check the title and what it includes, then try again.",
    retryable: false,
    preferServerMessage: true,
  },
  PRINTED_MENU_NOT_FOUND: {
    severity: "user_input",
    title: "Printed menu not found",
    message: "This printed menu or version is not at this location. It may have been deleted.",
    retryable: false,
  },
  PRINTED_MENU_STALE_PREVIEW: {
    severity: "user_input",
    title: "The menu just changed",
    message:
      "The menu changed while you were editing. The preview has been refreshed — check it and try again.",
    retryable: false,
    preferServerMessage: true,
  },
  PRINTED_MENU_CONFLICT: {
    severity: "user_input",
    title: "This printed menu changed",
    message: "Someone else saved this printed menu while you had it open. Reload to pick up their changes.",
    retryable: false,
    preferServerMessage: true,
  },
  PDF_UNAVAILABLE: {
    severity: "service",
    title: "The PDF could not be made",
    message: "Making the PDF is not available right now. Try again in a minute.",
    retryable: true,
  },
  PRINTED_MENU_OPERATION_FAILED: {
    severity: "service",
    title: "Something went wrong",
    message: "The printed menu could not be saved or sent. Try again in a minute.",
    retryable: true,
  },
  // ── POS MENU EDITOR (docs/POS_MENU_EDITOR.md) ─────────────────────────────────
  // Refusals from /api/v1/menu-edit. None is retryable as-is: each refuses again
  // until something changes (a reload, another record, back office). The ones
  // whose server sentence names WHICH record or field prefer it — "used by
  // Dinner, Lunch" is the whole point of the refusal.
  MENU_EDIT_CONFLICT: {
    severity: "user_input",
    title: "Changed elsewhere",
    message:
      "Someone changed this while you had it open. Review the latest values and save again.",
    retryable: false,
  },
  MENU_EDIT_REFERENCED: {
    severity: "user_input",
    title: "Still in use",
    message:
      "This is still used elsewhere on the menu. Remove it from those places first, then delete it.",
    retryable: false,
    preferServerMessage: true,
  },
  MENU_EDIT_NAME_TAKEN: {
    severity: "user_input",
    title: "That name is taken",
    message: "Another record already has this name. Choose a different name.",
    retryable: false,
    preferServerMessage: true,
  },
  MENU_EDIT_FIELD_FORBIDDEN: {
    severity: "user_input",
    title: "Not editable here",
    message: "That setting cannot be changed from the POS. Use back office for it.",
    retryable: false,
    preferServerMessage: true,
  },
  MENU_EDIT_UNSUPPORTED_STRATEGY: {
    severity: "user_input",
    title: "Edit this price in back office",
    message:
      "This uses a pricing setup the POS cannot edit (variant, size-based or menu-specific). Change it in back office.",
    retryable: false,
    preferServerMessage: true,
  },
  MENU_EDIT_SYSTEM_ITEM: {
    severity: "user_input",
    title: "Built-in item",
    message:
      "Open Food and Open Drink are built in: they cannot be renamed, switched off or deleted.",
    retryable: false,
    preferServerMessage: true,
  },
  MENU_EDIT_BAR_LINKED: {
    severity: "user_input",
    title: "Managed by the OrderUp bar catalog",
    message:
      "This drink comes from the OrderUp bar catalog. Disable it instead, or change it in back office.",
    retryable: false,
    preferServerMessage: true,
  },
  MENU_EDIT_INVALID: {
    severity: "user_input",
    title: "Check the values",
    message: "One of these values cannot be saved. Correct it and try again.",
    retryable: false,
    preferServerMessage: true,
  },
  // A drink adopted from the OrderUp bar catalog refused a change OrderUp owns
  // (its recipe fields, or being archived / deleted). The refusal's own sentence
  // names what was refused and the way out ("Disable it instead"), so it wins.
  BAR_CATALOG_ITEM_REFUSED: {
    severity: "user_input",
    title: "Managed by the OrderUp bar catalog",
    message:
      "This drink comes from the OrderUp bar catalog, so that change isn't allowed here. You can still price it and disable it.",
    retryable: false,
    preferServerMessage: true,
  },
  // The bottle import (site admin) could not read one of the public state
  // lists. The server sentence NAMES the list (Oregon / Montgomery County) and
  // says nothing was written; the generic 503 rung would say neither.
  BAR_IMPORT_SOURCE_UNAVAILABLE: {
    severity: "service",
    title: "A state product list is unavailable",
    message:
      "One of the state product lists could not be read, so nothing was written. Try again in a few minutes.",
    retryable: true,
    preferServerMessage: true,
  },
  // Two bottle imports wrote the same rows at once. The step wrote nothing and
  // Resume re-plans from the catalog as it now stands, so retrying is correct.
  BAR_IMPORT_CONCURRENT_WRITE: {
    severity: "service",
    title: "Another import is running",
    message:
      "Another import wrote the same bottles at the same moment, so this step wrote nothing. Press Resume to continue.",
    retryable: true,
    preferServerMessage: true,
  },
  // A per-item bar price save COMMITTED (and was audited), then attaching the
  // spirit lists to the mixed-drink items failed. The generic 503 rung reads
  // as "your save failed" and sends the owner to re-type prices that are
  // already saved; this says they were saved and that saving again retries the
  // attach (the service reconciles on every save, also an unchanged one).
  BAR_PRICES_SAVED_ATTACH_FAILED: {
    severity: "service",
    title: "Prices saved — mixed drinks not updated",
    message:
      "Your prices were saved, but attaching the lists to your mixed drinks failed — try again.",
    retryable: true,
    preferServerMessage: true,
  },
  // Bar v2 spirit-list settings (design 2026-09-30 A1/A3). Both sentences
  // name exactly what to change — the style whose pour is past the order-close
  // multiplier cap, or the spirit whose house bottle is not set — so the
  // server's words win. A plain VALIDATION_ERROR stays generic: it can carry a
  // raw schema path dump no manager should read.
  // Every refusal the bar services write FOR the manager (owner, 2026-10-03:
  // House pours answered "Please check your input and try again" while the
  // real sentence — what to do — sat in the console). The services raise them
  // through `barRefusal` / `barConflict` / `barGone`
  // (`packages/core/src/services/bar/barErrors.ts`), so the class shows its
  // own words; a plain VALIDATION_ERROR stays generic because it can be a
  // schema path dump. A more specific bar code (below) still wins.
  BAR_REFUSED: {
    severity: "user_input",
    title: "That bar change can't be saved",
    message: "That bar change can't be saved as it is. Check it and try again.",
    retryable: true,
    preferServerMessage: true,
  },
  BAR_CONFLICT: {
    severity: "user_input",
    title: "That clashes with another bar setting",
    message: "That clashes with another bar setting. Reload the page and try again.",
    retryable: true,
    preferServerMessage: true,
  },
  BAR_GONE: {
    severity: "user_input",
    title: "That is no longer there",
    message: "That is no longer there. Reload the page and try again.",
    retryable: true,
    preferServerMessage: true,
  },
  // Specific bar refusals the services already raised with their own code,
  // but which had no catalog entry — so each rendered the generic copy.
  BAR_OWN_BOTTLE_STOCK_UNIT: {
    severity: "user_input",
    title: "Stock this bottle like a catalog bottle",
    message: "This bottle is stocked by the ounce like a catalog bottle of its kind. Pick that unit and save again.",
    retryable: true,
    preferServerMessage: true,
  },
  BAR_OWN_BOTTLE_KIND_REQUIRED: {
    severity: "user_input",
    title: "Say what kind of bottle it is",
    message: "Pick the bottle's Kind — a spirit, a wine, a beer — or what it is used as in your drinks, and save again.",
    retryable: true,
    preferServerMessage: true,
  },
  BAR_WINE_UNPRICED: {
    severity: "user_input",
    title: "Price it before switching it on",
    message: "Price a glass or a bottle of this wine before switching it on.",
    retryable: true,
    preferServerMessage: true,
  },
  BAR_WINE_NEEDS_PLACEMENT: {
    severity: "user_input",
    title: "Choose where the Wine group goes",
    message: "Choose the menu the \"Wine\" group belongs under, then add the wines again.",
    retryable: true,
    preferServerMessage: true,
  },
  BAR_WINE_ORDER_STALE: {
    severity: "user_input",
    title: "The wine list changed",
    message: "The wine list changed since this page loaded. Reload it and drag the wines again.",
    retryable: true,
    preferServerMessage: true,
  },
  BAR_WINE_SPLIT_IS_A_GLASS: {
    severity: "user_input",
    title: "This split is another wine's Glass",
    message: "This split is sold as another wine's Glass. Set that wine's Glass back to a pour first to sell it on its own.",
    retryable: false,
    preferServerMessage: true,
  },
  BAR_WINE_UPGRADE_PENDING: {
    severity: "user_input",
    title: "The wine list is being switched over",
    message:
      "This wine list is being switched to one shared Glass and Bottle for every wine. Prices can be changed again once that is done — nothing was saved.",
    retryable: true,
    preferServerMessage: true,
  },
  BAR_WINE_MENU_PRICE_SERVING_OFF: {
    severity: "user_input",
    title: "Price it for All menus first",
    message: "That serving is not sold yet, so a menu price for it would never ring. Price it for All menus first.",
    retryable: true,
    preferServerMessage: true,
  },
  BAR_LIST_ORDER_STALE: {
    severity: "user_input",
    title: "The list changed",
    message: "This list changed since the page loaded. Reload it and drag the options again.",
    retryable: true,
    preferServerMessage: true,
  },
  BAR_HOUSE_POUR_CONTENDED: {
    severity: "user_input",
    title: "Someone changed this house pour",
    message: "Someone changed this house pour at the same moment — reload the page and try again.",
    retryable: true,
    preferServerMessage: true,
  },
  BAR_HOUSE_POUR_MOVES_ROLE: {
    severity: "user_input",
    title: "This changes what your cocktails pour",
    message: "This also changes what some cocktails pour. Confirm to continue.",
    retryable: true,
    preferServerMessage: true,
  },
  BAR_HOUSE_POUR_SAVED_LISTS_FAILED: {
    severity: "service",
    title: "House pour saved — lists not updated",
    message: "House pour saved; updating your spirit lists failed — try again.",
    retryable: true,
    preferServerMessage: true,
  },
  // Bar → House pours (2026-10-03, Montezuma Tequila): an own bottle with no
  // role needs one before it can be the house — the page asks inline.
  BAR_HOUSE_POUR_ROLE_NEEDED: {
    severity: "user_input",
    title: "Say what this bottle pours as",
    message: "Choose what this bottle pours as, then make it the house pour.",
    retryable: true,
    preferServerMessage: true,
  },
  BAR_HOUSE_POUR_ROLE_HELD: {
    severity: "user_input",
    title: "Another bottle pours as that",
    message: "Another bottle is already the house pour for that. Clear it first, or choose another.",
    retryable: true,
    preferServerMessage: true,
  },
  BAR_MENU_PRICE_MIXER_DERIVED: {
    severity: "user_input",
    title: "Set this menu's Mixed price instead",
    message: "A mixer rings this menu's Mixed price plus its upcharge — set the Mixed price for this menu instead.",
    retryable: true,
    preferServerMessage: true,
  },
  BAR_COUNT_BAD_SHELF: {
    severity: "user_input",
    title: "Not a shelf a count can cover",
    message: "That is not a shelf a count can cover. Choose another and try again.",
    retryable: true,
    preferServerMessage: true,
  },
  BAR_COUNT_DUPLICATE_LINE: {
    severity: "user_input",
    title: "An item appears twice",
    message: "An item appears twice on this count. Remove one line and submit again.",
    retryable: true,
    preferServerMessage: true,
  },
  BAR_POUR_CAP_EXCEEDED: {
    severity: "user_input",
    title: "That pour is too large for the base pour",
    message:
      "A style pours more than 4× the base pour — raise the base pour or lower that style's pour.",
    retryable: true,
    preferServerMessage: true,
  },
  // Bar v2 stock levels (design 2026-09-30 §C3). The first names the item
  // and where its levels belong; the second is the post-commit Books sync —
  // the levels ARE saved, and saving again re-syncs.
  BAR_LEVELS_RECIPEOPS_ITEM: {
    severity: "user_input",
    title: "Set this item's levels in RecipeOps",
    message: "This item was added in RecipeOps — set its par and reorder point there.",
    retryable: false,
    preferServerMessage: true,
  },
  BAR_LEVELS_BOOKS_SYNC_FAILED: {
    severity: "service",
    title: "Levels saved — purchase guide not updated",
    message: "The levels are saved, but the purchase guide did not update. Save again to retry.",
    retryable: true,
    preferServerMessage: true,
  },
  // Physical bar counts (bar v2 design 2026-09-30 §C2). Stock is set by the
  // count either way; these say what happened to BOOKS, and what to do.
  BAR_COUNT_INVALID_LINES: {
    severity: "user_input",
    title: "Some lines on this count need fixing",
    message: "Some lines on this count can't be read as a quantity. Fix the lines marked in red and submit again.",
    retryable: false,
    preferServerMessage: true,
  },
  BAR_COUNT_ID_REUSED: {
    severity: "user_input",
    title: "This count was already posted",
    message: "This count was already posted with different items. Start a new count to record these.",
    retryable: false,
  },
  // Stock adjustments (POS bar inventory, 2026-10-19): one bottle, a known
  // reason, and a correction says which way. The schema refuses each first;
  // the service holds them for every caller.
  BAR_ADJUSTMENT_BAD_REASON: {
    severity: "user_input",
    title: "Choose why the stock moved",
    message: "That is not a reason stock can move for. Choose Received, Returned, Broken, Spilled, Spoiled or Correction.",
    retryable: true,
    preferServerMessage: true,
  },
  BAR_ADJUSTMENT_ONE_LINE: {
    severity: "user_input",
    title: "Adjust one bottle at a time",
    message: "An adjustment moves exactly one item. Adjust each bottle on its own, or take a count.",
    retryable: true,
    preferServerMessage: true,
  },
  BAR_ADJUSTMENT_DIRECTION: {
    severity: "user_input",
    title: "Say which way the correction goes",
    message: "Say whether the correction adds or removes stock, then save again.",
    retryable: true,
    preferServerMessage: true,
  },
  // Paper supplies (design 2026-10-26 §A). The route's schema refuses each
  // first; the service holds them for every caller.
  PAPER_SUPPLIES_DISABLED: {
    severity: "user_input",
    title: "Paper tracking isn't available yet",
    message: "Paper tracking is being rolled out business by business and isn't on for yours yet.",
    retryable: false,
    preferServerMessage: true,
  },
  PAPER_NOT_TRACKED: {
    severity: "user_input",
    title: "Count this paper first",
    message: "Count the unopened rolls of this paper first, then you can record what you receive or change its settings.",
    retryable: false,
    preferServerMessage: true,
  },
  PAPER_ROLLS_INVALID: {
    severity: "user_input",
    title: "Check the number of rolls",
    message: "Enter a whole number of rolls.",
    retryable: true,
    preferServerMessage: true,
  },
  PAPER_WIDTH_INVALID: {
    severity: "user_input",
    title: "Unknown paper width",
    message: "Paper width must be 58, 76 or 80 mm.",
    retryable: false,
    preferServerMessage: true,
  },
  PAPER_ALERT_DAYS_INVALID: {
    severity: "user_input",
    title: "Check the alert days",
    message: "Alert days must be a whole number of days, or turned off.",
    retryable: true,
    preferServerMessage: true,
  },
  PAPER_ROLL_LENGTH_INVALID: {
    severity: "user_input",
    title: "Check the roll length",
    message: "Enter the roll length printed on the box, in feet.",
    retryable: true,
    preferServerMessage: true,
  },
  // Paper types and ribbons (design 2026-10-26 §C).
  PAPER_PLY_REQUIRED: {
    severity: "user_input",
    title: "Choose the paper",
    message: "Say whether this impact paper is 1-ply or 2-ply. 2-ply rolls are shorter, so days left depend on it.",
    retryable: true,
    preferServerMessage: true,
  },
  PAPER_PLY_INVALID: {
    severity: "user_input",
    title: "Choose the paper",
    message: "Impact paper is 1-ply or 2-ply.",
    retryable: true,
    preferServerMessage: true,
  },
  RIBBON_NOT_TRACKED: {
    severity: "user_input",
    title: "Count your ribbons first",
    message: "Count the unopened printer ribbons first, then you can record what you receive or change their settings.",
    retryable: false,
    preferServerMessage: true,
  },
  RIBBONS_INVALID: {
    severity: "user_input",
    title: "Check the number of ribbons",
    message: "Enter a whole number of ribbons.",
    retryable: true,
    preferServerMessage: true,
  },
  RIBBON_TYPE_INVALID: {
    severity: "user_input",
    title: "Choose the ribbon",
    message: "The ribbon is black/red (ERC-38 B/R) or black (ERC-38 B).",
    retryable: true,
    preferServerMessage: true,
  },
  // Supply orders (paper supplies design 2026-10-26 §B).
  SUPPLY_ORDER_PRICE_CHANGED: {
    severity: "user_input",
    title: "The price changed",
    message: "The price changed since this page loaded. Review the new price and order again.",
    retryable: true,
    preferServerMessage: true,
  },
  SUPPLY_ORDER_ALREADY_OPEN: {
    severity: "user_input",
    title: "An order is already on its way",
    message: "An order of this item is already on its way. Confirm you want another to place a second.",
    retryable: false,
    preferServerMessage: true,
  },
  SUPPLY_SKU_NOT_OFFERED: {
    severity: "user_input",
    title: "Not available to order",
    message: "This item is not available to order from OrderUp right now.",
    retryable: false,
    preferServerMessage: true,
  },
  SUPPLY_SHIP_TO_INCOMPLETE: {
    severity: "user_input",
    title: "Add your address",
    message: "Add this location's full street address under Settings → Location so we know where to ship.",
    retryable: false,
    preferServerMessage: true,
  },
  SUPPLY_NO_BILLING_ACCOUNT: {
    severity: "user_input",
    title: "No OrderUp billing account",
    message: "Your business has no OrderUp billing account yet. Contact OrderUp support to set one up.",
    retryable: false,
    preferServerMessage: true,
  },
  SUPPLY_ORDER_INVALID_QUANTITY: {
    severity: "user_input",
    title: "Check the number of cases",
    message: "Order between 1 and 20 cases.",
    retryable: true,
    preferServerMessage: true,
  },
  SUPPLY_ORDER_STATUS_CHANGED: {
    severity: "user_input",
    title: "This order changed",
    message: "This order changed in the meantime. Reload to see where it is.",
    retryable: true,
    preferServerMessage: true,
  },
  SUPPLY_CANCEL_VIA_BILLING: {
    severity: "user_input",
    title: "Cancel it in Billing",
    message: "Void this order's invoice in Billing (Refund and void if it was paid). The order is cancelled automatically once the invoice is void.",
    retryable: false,
    preferServerMessage: true,
  },
  SUPPLY_ORDER_TOTAL_CHANGED: {
    severity: "user_input",
    title: "The total changed",
    message: "The total changed since this page loaded (the price or the sales tax). Review it and order again.",
    retryable: true,
    preferServerMessage: true,
  },
  SUPPLY_ORDER_NOT_PAID: {
    severity: "user_input",
    title: "Not paid yet",
    message: "This order is not paid. Release it unpaid with a reason, or wait for payment.",
    retryable: false,
    preferServerMessage: true,
  },
  SUPPLY_RELEASE_REASON_REQUIRED: {
    severity: "user_input",
    title: "Say why",
    message: "Say why this order ships before it is paid.",
    retryable: true,
    preferServerMessage: true,
  },
  PAPER_EVENT_GUID_INVALID: {
    severity: "user_input",
    title: "Something went wrong saving",
    message: "Reload the page and try again.",
    retryable: true,
  },
  BAR_COUNT_BOOKS_POSTING_FAILED: {
    severity: "service",
    title: "Stock updated — Books posting failed",
    message:
      "Your count set the stock, but its variance could not be posted to Books after several tries. Use Retry Books posting; if it fails again, contact support with this count's date and time.",
    retryable: true,
  },
  BAR_COUNT_BOOKS_NEEDS_COST: {
    severity: "user_input",
    title: "Books needs a cost for some items",
    message:
      "Books has no cost for some items on this count, so it can't value the variance. Record a purchase with its cost in Books → Inventory, then use Retry Books posting.",
    retryable: true,
    preferServerMessage: true,
  },
  // Already booked at $0 — a retry would replay that same row, so the fix is a
  // correcting adjustment in Books by hand. The server names the lines.
  BAR_COUNT_BOOKED_AT_ZERO: {
    severity: "user_input",
    title: "Correct this line in Books",
    message: "Books recorded this line at $0 — open the item's Books record and post a correcting adjustment.",
    retryable: false,
    preferServerMessage: true,
  },
  BAR_COUNT_BOOKS_ACCOUNT_UNMAPPED: {
    severity: "user_input",
    title: "Books needs inventory accounts",
    message:
      "Books has no inventory or cost-of-goods account for some items on this count. Set the default inventory accounts in Books → Settings, then use Retry Books posting.",
    retryable: true,
  },
  BAR_COUNT_NOTHING_TO_RETRY: {
    severity: "user_input",
    title: "Nothing to retry",
    message: "This count's Books posting isn't stuck — it is posted or already being retried.",
    retryable: false,
  },
  // "Same as <type>" house pours (2026-10-06): a chain, a cycle, itself, or a
  // target with no house. The server's sentence names the types and the fix.
  // (`BAR_HOUSE_POUR_NOT_SET` is retired: a type with no house still sells.)
  BAR_HOUSE_POUR_SAME_AS_REFUSED: {
    severity: "user_input",
    title: "Pick another house pour",
    message: "That spirit cannot pour the same as that one — pick a bottle, or another spirit to pour the same as.",
    retryable: true,
    preferServerMessage: true,
  },
  // A menu editor opened before the bar saved a per-menu price, then saved its
  // older `modifierGroups` (design 2026-09-30 B5). The save was refused and
  // nothing was written; reloading shows the bar's prices, and the operator's
  // own edits must be made again on top of them.
  MENU_GROUP_BAR_PRICES_STALE: {
    severity: "user_input",
    title: "This menu changed",
    message:
      "This menu's modifier groups changed while you had it open — reload to keep the newer version.",
    retryable: false,
    nextSteps: [
      "Reload the menu, then make your change again — your save was not applied.",
    ],
  },
  // Per-menu bar prices (design 2026-09-30 Part B). Every one of these
  // sentences names the menu and what to do — the menu above that wins, the
  // menu that lists items, the option that changed — so the server's words
  // win over the generic copy here, which is only the fallback.
  BAR_MENU_PRICE_SHADOWED: {
    severity: "user_input",
    title: "A menu above this one has these prices",
    message:
      "A menu above this one already prices this list, and its prices win on every order here. Clear them there first, or price the list there.",
    retryable: false,
    preferServerMessage: true,
  },
  BAR_MENU_PRICE_MENU_NOT_OFFERED: {
    severity: "user_input",
    title: "Choose another menu",
    message:
      "No drink with a bar list sits under this menu, so a price there would never ring. Choose one of the menus offered under \"Prices for\".",
    retryable: false,
    preferServerMessage: true,
  },
  BAR_MENU_PRICES_EMPTY: {
    severity: "user_input",
    title: "Nothing to save",
    message: "Enter at least one price.",
    retryable: false,
    preferServerMessage: true,
  },
  BAR_PRICE_NOT_WHOLE_CENTS: {
    severity: "user_input",
    title: "Check that price",
    message: "A price is 0 to 1000 dollars, in whole cents.",
    retryable: false,
    preferServerMessage: true,
  },
  BAR_MENU_PRICE_STALE: {
    severity: "user_input",
    title: "The bar lists changed",
    message: "A menu or bottle on this page changed since it loaded. Reload the page and try again.",
    retryable: false,
    preferServerMessage: true,
  },
  // B3: a menu group carrying a bar price reference attaches those lists to
  // every item it lists directly — the server sentence names the group and
  // where to put the items (or the prices) instead.
  MENU_GROUP_BAR_PRICES_BLOCK_ITEMS: {
    severity: "user_input",
    title: "This menu lists items directly",
    message:
      "Bar prices on a menu that lists items would put the bar lists on those items too. Price the menu above it, or put the items in a group under it.",
    retryable: false,
    preferServerMessage: true,
  },
  MENU_BOARD_NOT_FOUND: {
    severity: "user_input",
    title: "That board is gone",
    message:
      "This menu board no longer exists — someone else may have deleted it.",
    retryable: false,
    // Like its siblings, the server sentence NAMES the board id. The shared
    // 404 rung renders "We couldn't find what you were looking for", which
    // tells an operator staring at a board list exactly nothing about WHICH
    // board vanished. See docs/MENU_BOARD_ARCHITECTURE.md §9.
    preferServerMessage: true,
  },
  MENU_BOARD_OPERATION_FAILED: {
    severity: "unknown",
    title: "We could not finish that",
    message:
      "Something went wrong saving this board. Nothing on your screens has changed.",
    // The one board code that IS transient — it is the 500 path.
    retryable: true,
  },

  // ── ORDERUP STUDIO (the design canvas) ─────────────────────────────────────
  // The same shape as the menu-board codes above, for the same reason: these
  // are REFUSALS whose specifics live in the server's sentence, and a design
  // ends up on paper, where a vague message costs a print run rather than a
  // page reload.
  DESIGN_CONFLICT: {
    severity: "user_input",
    title: "This design changed while you were editing",
    message:
      "Someone else saved this design while you had it open. Nothing you did was saved. Reload to pick up their changes.",
    retryable: false,
    // The server names BOTH version numbers — the version the editor loaded and
    // the one now stored. That is the only thing distinguishing "a colleague
    // saved" from "my connection dropped", and the two call for opposite
    // actions.
    preferServerMessage: true,
  },
  INVALID_DESIGN: {
    severity: "user_input",
    title: "This design cannot be saved yet",
    message: "Something on the canvas is not valid.",
    retryable: false,
    // The server sentence names WHICH thing — most usefully, how many menu
    // blocks have no menu chosen, which is invisible on screen because the box
    // is right there and merely empty.
    preferServerMessage: true,
  },
  DESIGN_NOT_FOUND: {
    severity: "user_input",
    title: "That design is gone",
    message: "This design no longer exists — someone else may have deleted it.",
    retryable: false,
    preferServerMessage: true,
  },
  // A report that names people (guests, employees) or their pay or tips is
  // made only once its export is recorded (SOC 2 C1.1, R51). Raised in the
  // browser by `exportReportCsv` / `exportReportText` when
  // `POST /api/v1/report-exports` did not answer; the API's own 503 carries
  // the same code.
  REPORT_EXPORT_NOT_RECORDED: {
    severity: "service",
    title: "Export not made",
    message:
      "This report has personal information in it, and we could not record the export, so the file was not made. Nothing on the page changed.",
    retryable: true,
    nextSteps: ["Try the export again in a minute.", "If it keeps happening, contact support with the reference below."],
  },
  DESIGN_EXPORT_UNAVAILABLE: {
    severity: "service",
    title: "Export is unavailable right now",
    message:
      "We could not render your design to a file just now. Your design is saved and nothing was lost.",
    // Genuinely transient: this is the 503 raised when no Chromium is available
    // on the host, not a fault in the artwork.
    retryable: true,
    nextSteps: ["Try the download again in a minute."],
  },
  DESIGN_OPERATION_FAILED: {
    severity: "unknown",
    title: "We could not finish that",
    message: "Something went wrong with this design. Your last saved version is safe.",
    retryable: true,
  },

  // ── SERVICE ────────────────────────────────────────────────────────────────
  "auth/firebase-app-check-token-is-invalid": {
    severity: "service",
    title: "We couldn’t verify your browser",
    message:
      "Something on our end couldn’t verify this browser session. This is on us, not your password. Please try again in a few minutes — and contact support if it keeps happening.",
    retryable: true,
  },
  "appCheck/throttled": {
    severity: "service",
    title: "Just a moment",
    message:
      "Too many verification attempts. Please wait a moment and try again.",
    retryable: true,
  },
  "appCheck/initial-throttle": {
    severity: "service",
    title: "Verification temporarily unavailable",
    message:
      "Browser verification is temporarily unavailable (this can last up to a day after a setup change). Please try again later or contact support.",
    retryable: false,
  },
  "auth/captcha-check-failed": {
    severity: "service",
    title: "Verification failed",
    message:
      "We couldn’t complete a security check. Reload the page and try again.",
    retryable: true,
  },
  "auth/network-request-failed": {
    severity: "service",
    title: "Connection problem",
    message:
      "We couldn’t reach our servers. Check your internet connection and try again.",
    retryable: true,
  },
  "auth/too-many-requests": {
    severity: "service",
    title: "Too many attempts",
    message:
      "Too many attempts. Please wait a few minutes before trying again.",
    retryable: false,
  },
  "auth/quota-exceeded": {
    severity: "service",
    title: "Try again shortly",
    message: "We’re a bit busy right now. Please try again in a few minutes.",
    retryable: true,
  },

  // ── CREDENTIALS / USER INPUT ────────────────────────────────────────────────
  "auth/invalid-credential": {
    severity: "user_input",
    title: "Incorrect email or password",
    message: "That email and password don’t match. Please try again.",
    retryable: true,
  },
  "auth/wrong-password": {
    severity: "user_input",
    title: "Incorrect email or password",
    message: "That email and password don’t match. Please try again.",
    retryable: true,
  },
  "auth/user-not-found": {
    severity: "user_input",
    title: "Account not found",
    message: "We couldn’t find an account with that email.",
    retryable: true,
  },
  "auth/invalid-email": {
    severity: "user_input",
    title: "Check your email",
    message: "That email address doesn’t look right.",
    retryable: true,
  },
  "auth/email-already-in-use": {
    severity: "user_input",
    title: "Email already in use",
    message:
      "An account already exists with this email. Try signing in instead.",
    retryable: false,
  },
  "auth/weak-password": {
    severity: "user_input",
    title: "Choose a stronger password",
    message:
      "Your password needs to be at least 6 characters. Please try a longer one.",
    retryable: true,
  },
  "auth/operation-not-allowed": {
    severity: "service",
    title: "Sign-up unavailable",
    message:
      "Sign-up is temporarily unavailable. Please try again later or contact support.",
    retryable: false,
  },
  "auth/invalid-verification-code": {
    severity: "user_input",
    title: "Check the code",
    message: "That verification code isn’t right. Please re-enter it.",
    retryable: true,
  },
  "auth/missing-verification-code": {
    severity: "user_input",
    title: "Check the code",
    message: "That verification code isn’t right. Please re-enter it.",
    retryable: true,
  },
  "auth/code-expired": {
    severity: "user_input",
    title: "Code expired",
    message: "That code expired. Request a new one and try again.",
    retryable: true,
  },

  // ── AUTH ────────────────────────────────────────────────────────────────────
  "auth/user-disabled": {
    severity: "auth",
    title: "Account deactivated",
    message:
      "This account has been deactivated. If you think this is a mistake, contact your employer or support.",
    retryable: false,
  },
  "auth/user-token-expired": {
    severity: "auth",
    title: "Session expired",
    message: "Your session expired. Please sign in again.",
    retryable: true,
  },
  "auth/user-mismatch": {
    severity: "auth",
    title: "Different account",
    message: "Those credentials are for a different account.",
    retryable: true,
  },
  "auth/account-exists-with-different-credential": {
    severity: "auth",
    title: "Already registered",
    message:
      "An account already exists with this email using a different sign-in method.",
    retryable: false,
  },
  "auth/credential-already-in-use": {
    severity: "auth",
    title: "Already linked",
    message: "These credentials are already linked to another account.",
    retryable: false,
  },
  // Our own mailer refused the password-reset message. This is deliberately a
  // SERVICE fault and says nothing about whether the account exists — the
  // enumeration-safe answer is the 200, and this code is only ever reached
  // after a reset link was successfully generated. It exists because the
  // alternative, staying silent, is what left an owner watching a green
  // "email sent" for a message that was never dispatched.
  EMAIL_DELIVERY_FAILED: {
    severity: "service",
    title: "We could not send that email",
    message:
      "The reset link was created but our email service would not accept it, so nothing has reached your inbox. This is a fault on our side, not with your account.",
    retryable: true,
    nextSteps: [
      "Wait a minute and try again — this is usually brief.",
      "If it keeps failing, contact support and mention that password reset email is failing to send.",
    ],
  },

  // ── ENV / POPUP ─────────────────────────────────────────────────────────────
  "auth/popup-blocked": {
    severity: "user_input",
    title: "Popup blocked",
    message:
      "Your browser blocked the sign-in popup. Allow popups for this site and try again.",
    retryable: true,
  },
  "auth/web-storage-unsupported": {
    severity: "service",
    title: "Browser storage blocked",
    message:
      "Your browser is blocking storage we need to sign you in. Adjust your privacy settings or try another browser.",
    retryable: false,
  },
  "auth/operation-not-supported-in-this-environment": {
    severity: "service",
    title: "Unsupported here",
    message: "This sign-in method isn’t supported in your current browser.",
    retryable: false,
  },
  "auth/popup-closed-by-user": SUPPRESSED,
  "auth/cancelled-popup-request": SUPPRESSED,
  // Legacy thrown string code (see apps/auth getDisplayError POPUP_CANCELLED filter).
  POPUP_CANCELLED: SUPPRESSED,

  // ── BOOKS — AR / AP & PAYMENTS ──────────────────────────────────────────────
  // ── Attachment upload: the five refusals the pipeline can raise ─────────
  //
  // Every one of these was reaching the user as "Something went wrong on our
  // end. Please try again." — the generic fallback for an uncatalogued code.
  // That sentence is wrong in four of the five cases and unhelpful in the
  // fifth: an infected file, an oversized file and a rejected file type are
  // all things the OPERATOR can act on, and telling them it is our fault sends
  // them to support for a problem they could fix in ten seconds.
  //
  // Measured 2026-08-19: a merchant on dev could not upload an onboarding
  // document. The API correctly answered 503
  // BOOKS_ATTACHMENT_SCANNER_UNAVAILABLE — the scanner genuinely was
  // unreachable — and the screen said "Something went wrong on our end",
  // which is true but useless: it does not say the file was not saved, and it
  // does not say retrying later is the fix.
  //
  // The statuses these map to are already registered in `STATUS_BY_ERROR`
  // (413/415/422/422/503); this is only the human half.
  //
  // `retryable` is the honest distinction: the scanner being down is transient
  // and worth retrying, a 40 MB PDF is not going to shrink on a second attempt.
  BOOKS_ATTACHMENT_SCANNER_UNAVAILABLE: {
    severity: "service",
    title: "Virus scanning is temporarily unavailable",
    message:
      "Every upload is virus-scanned before it is stored, and the scanner did not respond. Your file was not uploaded — nothing was saved, so no partial or unscanned copy is sitting anywhere.",
    retryable: true,
    nextSteps: [
      "Wait a minute and upload again — this is usually brief.",
      "If it keeps failing, contact support: the scanning service is ours, not something you can fix from here.",
    ],
  },
  BOOKS_ATTACHMENT_INFECTED: {
    severity: "user_input",
    title: "That file failed the virus scan",
    message:
      "The scanner flagged this file as containing malware, so it was rejected and never stored.",
    retryable: false,
    nextSteps: [
      "Scan the file on your own machine before trying again.",
      "If you believe this is wrong, re-export or re-scan the document and upload the fresh copy — a clean export usually passes.",
    ],
  },
  BOOKS_ATTACHMENT_INVALID_TYPE: {
    severity: "user_input",
    title: "That file type is not accepted",
    message:
      "The file's actual contents are not a type we accept. We check the bytes rather than the file name, so renaming a file to .pdf will not get it through.",
    retryable: false,
    nextSteps: [
      "Upload a PDF, PNG, JPEG or HEIC.",
      "If it should already be one of those, re-export or re-save it from the app that created it — the file may be corrupt or mislabelled.",
    ],
  },
  BOOKS_ATTACHMENT_TOO_LARGE: {
    severity: "user_input",
    title: "That file is too large",
    message:
      "Attachments are capped at 50 MB and this one is over the limit, so it was not uploaded.",
    retryable: false,
    nextSteps: [
      "Compress the file, or export it at a lower resolution.",
      "For a long scan, split it and upload the parts separately.",
    ],
  },
  BOOKS_ATTACHMENT_NOT_AVAILABLE: {
    severity: "service",
    title: "That attachment cannot be opened",
    message:
      "The file is not available to download — it may still be processing, or it may have been removed.",
    retryable: true,
    nextSteps: [
      "Refresh the page and try again.",
      "If it stays unavailable, upload the document again.",
    ],
  },
  // ── TAX-EXEMPT ORDERS (docs/TAX_EXEMPTION_ARCHITECTURE.md) ─────────────────
  // Emitted by apps/api/components/routes/tax-exemptions.ts. Every one of these
  // is something the operator can act on, so each says what happened, that
  // nothing half-saved, and what to do instead — never the generic "check your
  // input", which is what an uncatalogued 409/413/415 falls through to.
  //
  // The two ATTACHMENT_* codes are the certificate upload's scanner outcomes;
  // they are phrased for any upload, not only a certificate.
  // ── Late auto-gratuity (docs/LATE_AUTO_GRATUITY_ARCHITECTURE.md §4) ──────
  // The till's Collect / Waive on a gratuity that became owed after the bill
  // was paid. Every refusal changed nothing, and none is worth an automatic
  // retry: each needs the operator to look again first.
  LATE_GRATUITY_OWED_CHANGED: {
    severity: "user_input",
    title: "The gratuity owed has changed",
    message:
      "The amount owed is no longer what was shown — another till may have collected or waived it, or the order changed. Nothing was charged or waived.",
    retryable: false,
    // The server's sentence names the amount owed NOW.
    preferServerMessage: true,
    nextSteps: ["Check the new amount on the order, then collect or waive it again if it is still owed."],
  },
  LATE_GRATUITY_NOT_OWED: {
    severity: "user_input",
    title: "No gratuity is owed",
    message: "This order does not owe a late auto-gratuity any more. Nothing was changed.",
    retryable: false,
    nextSteps: ["Refresh the order to see its current state."],
  },
  LATE_GRATUITY_ORDER_NOT_OPEN: {
    severity: "user_input",
    title: "The order can't take this now",
    message:
      "A late gratuity can be collected only on an open order, and waived only on an order that was not voided or cancelled. Nothing was changed.",
    retryable: false,
    nextSteps: [
      "On a closed order, a manager can still waive it.",
      "To collect it, a manager reopens the order first.",
    ],
  },
  LATE_GRATUITY_NO_PAYABLE_CHECK: {
    severity: "user_input",
    title: "No check can take the gratuity",
    message: "Every check on this order is settled and closed, so the gratuity has nowhere to go. Nothing was charged.",
    retryable: false,
    nextSteps: ["A manager can reopen a check to collect it, or waive it with a reason."],
  },
  LATE_GRATUITY_IDEMPOTENCY_KEY_REUSED: {
    severity: "user_input",
    title: "That request was already used",
    message: "This request was already used for a different action on the gratuity, so it was not repeated. Nothing was changed.",
    retryable: false,
    nextSteps: ["Close the gratuity notice, reopen it and choose the action again."],
  },
  LATE_GRATUITY_REAPPLY_REFUSED: {
    severity: "user_input",
    title: "The gratuity can't be put back on that check",
    message:
      "A check that is missing its collected gratuity is already closed or paid, so the gratuity can't be put back on it. Nothing was changed.",
    retryable: false,
    nextSteps: ["A manager settles it: collect the difference on a reopened check, or record it for the owner's review."],
  },
  LATE_GRATUITY_REPORT_PREPARING: {
    severity: "service",
    title: "This report is being prepared",
    message: "The waived-gratuity report is being prepared for this restaurant. Try again shortly.",
    retryable: true,
    nextSteps: ["Try again in a minute."],
  },
  LATE_GRATUITY_APPROVAL_REQUIRED: {
    severity: "user_input",
    title: "A manager's PIN is needed",
    message: "Waiving a gratuity needs a manager to approve it with their PIN on this till. Nothing was waived.",
    retryable: false,
    nextSteps: ["Ask a manager to enter their PIN when prompted, then waive it again."],
  },
  LATE_GRATUITY_REASON_REQUIRED: {
    severity: "user_input",
    title: "Say why it is being waived",
    message: "A waiver needs a reason of 3 to 200 characters. Nothing was waived.",
    retryable: false,
    nextSteps: ["Enter a short reason — for example \"regular guests, owner's call\" — and waive it again."],
  },
  TAX_EXEMPTION_IDEMPOTENCY_KEY_REUSED: {
    severity: "user_input",
    title: "That request was already used",
    message: "This request was already used for a different tax exemption change on this order, so it was not repeated. Nothing was changed.",
    retryable: false,
    nextSteps: ["Refresh the order, then make the change again."],
  },
  TAX_EXEMPTION_NOT_ACTIVE: {
    severity: "user_input",
    title: "This order is no longer tax-exempt",
    message:
      "The tax exemption on this order was removed, so its details and certificate can no longer be changed. The record is kept as it was. Nothing was changed.",
    retryable: false,
    nextSteps: [
      "Refresh the order to see its current state.",
      "If the order should be exempt again, a manager re-applies the exemption on the POS.",
    ],
  },
  TAX_EXEMPTION_DOCUMENT_LIMIT: {
    severity: "user_input",
    title: "No room for more certificate photos",
    message: `This exemption already holds the most certificate photos it can keep (${TAX_EXEMPTION_DOCUMENT_MAX}), so no photo was added.`,
    retryable: false,
    // The server's sentence says exactly how many more it can take.
    preferServerMessage: true,
    nextSteps: [
      "Earlier photos are never deleted — the newest photo of each side is already the certificate on record.",
      "If a side is still missing, upload only that side.",
    ],
  },
  TAX_EXEMPTION_NOT_A_PHOTO: {
    severity: "user_input",
    title: "That file isn't a photo we can read",
    message:
      "Certificate photos must be a JPEG, PNG, WebP or HEIC image. The file was not saved.",
    retryable: false,
    // The server names WHICH side was refused.
    preferServerMessage: true,
    nextSteps: [
      "Take a photo of the certificate, or export a scan as a JPEG or PNG, and upload that.",
      "A PDF will not work here — photograph or screenshot the page instead.",
    ],
  },
  TAX_EXEMPTION_PHOTO_TOO_LARGE: {
    severity: "user_input",
    title: "That photo is too large",
    message: `Each certificate photo must be ${TAX_EXEMPTION_DOCUMENT_MAX_BYTES / (1024 * 1024)} MB or smaller. Nothing was saved.`,
    retryable: false,
    nextSteps: [
      "Retake the photo at a lower resolution, or crop it to just the certificate, and upload it again.",
    ],
  },
  TAX_EXEMPTION_ORDER_NOT_OPEN: {
    severity: "user_input",
    title: "The order is closed",
    message:
      "Tax can only be added to or removed from an open order. Nothing was changed.",
    retryable: false,
    nextSteps: [
      "Details and certificate photos can still be added to an exemption that is already on the order.",
    ],
  },
  TAX_EXEMPTION_PAYMENT_EXISTS: {
    severity: "user_input",
    title: "A payment was already taken",
    message:
      "Tax can't change on an order that has a payment on it — the check would be left owing or overpaid. Nothing was changed.",
    retryable: false,
    nextSteps: ["Void or refund the payment first, then change the tax, then take payment again."],
  },
  TAX_EXEMPTION_NOT_SUPPORTED: {
    severity: "user_input",
    title: "Marketplace orders can't be made tax-exempt",
    message:
      "The delivery marketplace charged and owns the tax on this order, so it can't be removed here. Nothing was changed.",
    retryable: false,
  },
  ATTACHMENT_INFECTED: {
    severity: "user_input",
    title: "That file failed the virus scan",
    message:
      "The scanner flagged this file as containing malware, so it was rejected and never stored.",
    retryable: false,
    nextSteps: [
      "Take a fresh photo or re-export the file on a device you trust, and upload that instead.",
    ],
  },
  ATTACHMENT_SCANNER_UNAVAILABLE: {
    severity: "service",
    title: "Virus scanning is temporarily unavailable",
    message:
      "Every upload is virus-scanned before it is stored, and the scanner did not respond. Nothing was saved.",
    retryable: true,
    nextSteps: [
      "Wait a minute and upload again — this is usually brief.",
      "If it keeps failing, contact support: the scanning service is ours, not something you can fix from here.",
    ],
  },
  BOOKS_NO_BANK_ACCOUNT_FOR_PAYMENT_METHOD: {
    severity: "user_input",
    title: "No deposit account for this payment method",
    message:
      "This payment method has no bank or clearing account to route to, so the payment can’t be recorded yet.",
    retryable: false,
    nextSteps: [
      "Pick a Bank / clearing account in this payment dialog, or",
      "Map this method to an account under Books → Settings → Payment Method Deposit Accounts, or",
      "Set a Default Bank under Books → Settings → Default Accounts (used when a method isn’t mapped).",
    ],
  },
  // ── Voiding an invoice that we actually charged a card for ──────────────
  //
  // `void_invoice` used to cascade-void the payment silently and tell the
  // processor nothing, so a voided invoice could leave a cardholder charged.
  // The database now refuses, and this is the operator-facing half: it has to
  // say what is blocking and what to do, because "not allowed" on a money
  // screen sends people to support.
  // The DATABASE safety net, not the normal path. Voiding through the app
  // refunds the card as part of the void, so an operator should never meet
  // this. It fires when something reaches `void_invoice` without going through
  // that orchestration — a direct RPC call, a script, a future caller that
  // forgets — and its job is to stop a charged customer being left with no
  // invoice. The copy is written for whoever DOES hit it, which is why it
  // names the app path rather than describing manual steps: telling an
  // operator to reverse by hand is what the product used to do wrong.
  BOOKS_INVOICE_HAS_GATEWAY_PAYMENT: {
    severity: "service",
    title: "This invoice still has a live card charge",
    message:
      "A card payment on this invoice is still holding the customer's money at the processor. Voiding now would leave them charged with no invoice explaining it, so it was refused.",
    retryable: true,
    nextSteps: [
      "Void the invoice from the invoice page — that refunds the card as part of the void.",
      "If it keeps failing, the processor may be mid-settlement. Check the batch and try again shortly.",
    ],
  },
  BOOKS_INVOICE_NOT_VOID: {
    severity: "user_input",
    title: "This invoice isn’t voided",
    message:
      "Only a voided invoice can be restored, and this one is in a different state.",
    retryable: false,
    nextSteps: ["Reload the invoice to see its current status."],
  },
  // --- Document numbers ------------------------------------------------------
  // Two documents claiming one number. `create_and_post_invoice` and the
  // rename path RAISE the invoice code by name for a typed number that is
  // taken; every code here is ALSO what `mapPgErrorToBooksError` turns a raw
  // unique violation on that document's number index into
  // (`BOOKS_DOCUMENT_NUMBER_UNIQUE_INDEXES`). BOOKS_INVOICE_NUMBER_CONFLICT was
  // status-mapped (409) with no copy, so a clash rendered the generic fallback.
  BOOKS_INVOICE_NUMBER_CONFLICT: {
    severity: "user_input",
    title: "That invoice number is already used",
    message:
      "Another invoice in this location already carries that number, and two invoices with the same number cannot be told apart.",
    retryable: false,
    nextSteps: [
      "Pick a different number, or clear the field to take the next free one in sequence.",
    ],
  },
  BOOKS_CREDIT_MEMO_NUMBER_CONFLICT: {
    severity: "user_input",
    title: "That credit memo number is already used",
    message:
      "Another credit memo in this location already carries that number.",
    retryable: false,
    nextSteps: [
      "Pick a different number, or leave it blank to take the next free one in sequence.",
    ],
  },
  BOOKS_VENDOR_CREDIT_MEMO_NUMBER_CONFLICT: {
    severity: "service",
    title: "Two vendor credits reached the same number at once",
    message:
      "Another vendor credit took this number at the same moment, so this one was not saved.",
    retryable: true,
    nextSteps: ["Try again — the next attempt takes the next free number."],
  },
  BOOKS_JOURNAL_ENTRY_NUMBER_CONFLICT: {
    severity: "service",
    title: "Two entries reached the same number at once",
    message:
      "Another journal entry took this entry number at the same moment, so this one was not saved.",
    retryable: true,
    nextSteps: ["Try again — the next attempt takes the next free number."],
  },
  BOOKS_PAYMENT_NUMBER_CONFLICT: {
    severity: "service",
    title: "Two payments reached the same number at once",
    message:
      "Another payment took this payment number at the same moment, so this one was not recorded.",
    retryable: true,
    nextSteps: [
      "Check the payments list first so the payment is not recorded twice, then try again.",
    ],
  },
  BOOKS_SETTLEMENT_NUMBER_CONFLICT: {
    severity: "service",
    title: "Two payouts reached the same number at once",
    message:
      "Another processor payout took this number at the same moment, so this one was not saved.",
    retryable: true,
    nextSteps: [
      "Check the bank transaction first so the payout is not matched twice, then try again.",
    ],
  },
  // --- Estimates -----------------------------------------------------------
  // These classes have existed and been status-mapped since Books shipped, but
  // none of them had catalog copy — so EVERY failed estimate save rendered the
  // fallback "Please check your input and try again.", which tells an operator
  // nothing about which line is wrong or why. Closed here because job costing
  // adds a ninth refusal to the same screen, and shipping one good message
  // beside eight useless ones is not a fix.
  BOOKS_ESTIMATE_NO_LINES: {
    severity: "user_input",
    title: "An estimate needs at least one line",
    message: "This estimate has no line items, so there is nothing to quote.",
    retryable: false,
    nextSteps: ["Add a line with a product or service, then save again."],
  },
  BOOKS_ESTIMATE_LINE_NO_ACCOUNT: {
    severity: "user_input",
    title: "A line has no product or service",
    message:
      "Every line needs a product or service so the sale lands in the right income account. One of these lines has neither.",
    retryable: false,
    nextSteps: [
      "Pick a product/service on the flagged line — the income account comes with it.",
    ],
  },
  BOOKS_ESTIMATE_LINE_INVALID_AMOUNTS: {
    severity: "user_input",
    title: "A line’s numbers don’t add up",
    message:
      "A line has a quantity of zero or below, or a negative price or tax. None of those can be quoted.",
    retryable: false,
    nextSteps: [
      "Check each line: quantity must be above zero, and prices cannot be negative.",
    ],
  },
  BOOKS_ESTIMATE_NOT_EDITABLE: {
    severity: "user_input",
    title: "This estimate has already been invoiced",
    message:
      "Once an estimate has been converted or partly invoiced its lines are locked, because the invoice raised from it is what the customer owes.",
    retryable: false,
    nextSteps: [
      "Edit the invoice instead, or retract it first if the whole job changed.",
    ],
  },
  BOOKS_ESTIMATE_NUMBER_CONFLICT: {
    severity: "user_input",
    title: "That estimate number is already used",
    message:
      "Another estimate in this location already carries that number, and two documents with the same number cannot be told apart.",
    retryable: false,
    nextSteps: [
      "Pick a different number, or leave it blank to take the next one in sequence.",
    ],
  },
  BOOKS_ESTIMATE_MISSING_REQUIRED_FIELDS: {
    severity: "user_input",
    title: "Some required details are missing",
    message:
      "The estimate is missing a customer or a date it cannot be saved without.",
    retryable: false,
    nextSteps: ["Fill in the customer and both dates, then save again."],
  },
  BOOKS_ESTIMATE_BAD_STATE: {
    severity: "user_input",
    title: "This estimate isn’t in a state that allows that",
    message:
      "The action doesn’t apply to an estimate in its current status — someone may have changed it in another tab.",
    retryable: false,
    nextSteps: ["Reload the estimate to see where it actually stands."],
  },
  BOOKS_ESTIMATE_NOT_FOUND: {
    severity: "user_input",
    title: "That estimate no longer exists",
    message: "This estimate has been archived or deleted.",
    retryable: false,
    nextSteps: ["Go back to the estimates list."],
  },
  BOOKS_INVOICE_NO_LINES: {
    severity: "user_input",
    title: "An invoice needs at least one line",
    message: "This invoice has no line items, so there is nothing to bill.",
    retryable: false,
    nextSteps: ["Add a line with a product or service, then save again."],
  },

  // --- Internal job costing ------------------------------------------------
  BOOKS_LINE_COST_WOULD_BE_DESTROYED: {
    severity: "user_input",
    title: "Saving now would erase this job’s costs",
    message:
      "This document has internal cost rows recorded against its lines, but the save that was attempted didn’t include them — so going ahead would have wiped what the job actually costs you. Nothing was changed.",
    retryable: true,
    nextSteps: [
      "Reload the page and make your edit again — the costs will come along with it.",
      "If you meant to remove the costs, clear them on the line first, then save.",
    ],
  },
  BOOKS_LINE_COST_INVALID_AMOUNTS: {
    severity: "user_input",
    title: "A cost row’s numbers don’t add up",
    message:
      "A cost row has a quantity of zero or below, a negative unit cost, or no description. None of those can be costed.",
    retryable: false,
    nextSteps: [
      "Open the line’s cost breakdown and check every row has a description, a quantity above zero, and a cost of zero or more.",
    ],
  },
  BOOKS_COST_KIT_NOT_FOUND: {
    severity: "user_input",
    title: "That cost kit no longer exists",
    message:
      "This cost kit has been archived or deleted since the page was loaded.",
    retryable: false,
    nextSteps: ["Reload the page to see the current kits."],
  },
  BOOKS_COST_KIT_NAME_CONFLICT: {
    severity: "user_input",
    title: "A cost kit already has that name",
    message:
      "Another kit in this location is already called that, and two kits with the same name are impossible to tell apart when you go to apply one.",
    retryable: false,
    nextSteps: ["Give this kit a different name."],
  },
  BOOKS_COST_KIT_MISSING_REQUIRED_FIELDS: {
    severity: "user_input",
    title: "This cost kit is missing a name",
    message: "A kit needs a name so it can be found and applied later.",
    retryable: false,
    nextSteps: ["Give the kit a name, then save again."],
  },

  BOOKS_UNVOID_REASON_REQUIRED: {
    severity: "user_input",
    title: "A reason is required",
    message:
      "Restoring a voided invoice changes the books, so the reason is recorded on the audit trail.",
    retryable: true,
    nextSteps: ["Enter why the invoice is being restored, then try again."],
  },
  // Raised by void_invoice (and other reversing entries) when no fiscal
  // period is open within the lookback window to book the reversal into —
  // every candidate period back to the original posting is closed.
  BOOKS_NO_OPEN_PERIOD_FOR_REVERSAL: {
    severity: "user_input",
    title: "No open period for this reversal",
    message:
      "This can't be voided because every accounting period it could be reversed into is closed.",
    retryable: false,
    nextSteps: [
      "Reopen the relevant fiscal period, then try the void again.",
    ],
  },
  // ── Multi-document payment application ───────────────────────────────
  //
  // The 409s and the 422s need DIFFERENT copy, and the difference is what the
  // operator should do next. A 409 means the world moved underneath them and
  // reloading fixes it; a 422 means their own arithmetic is wrong and reloading
  // would change nothing. Telling someone to "refresh" when their split is off
  // by a dollar sends them round a loop that never resolves.
  BOOKS_ALLOCATION_EXCEEDS_AMOUNT_DUE: {
    severity: "user_input",
    title: "One invoice’s share is more than it still owes",
    message:
      "The amount applied to one of these documents is larger than its remaining balance. Its balance may have changed since this screen loaded — someone else may have recorded a payment against it.",
    retryable: true,
    nextSteps: [
      "Reload to pull the current balances, then re-apply.",
      "Or lower that line to at most the balance shown.",
    ],
  },
  BOOKS_ALLOCATIONS_EXCEED_PAYMENT: {
    severity: "user_input",
    title: "You’ve applied more than you received",
    message:
      "The amounts applied across these documents add up to more than the payment itself. Applying less than the payment is fine — the remainder stays as a credit — but it can never total more.",
    retryable: false,
    nextSteps: [
      "Lower one or more lines until the total is at most the amount received.",
      "Or raise the amount received if it was entered short.",
    ],
  },
  BOOKS_APPLY_EXCEEDS_UNAPPLIED: {
    severity: "user_input",
    title: "That credit doesn’t have this much left",
    message:
      "You’re applying more than remains unapplied on this payment. Part of it may have been applied elsewhere since this screen loaded.",
    retryable: true,
    nextSteps: [
      "Reload to see the credit’s current unapplied balance.",
      "Then apply at most that amount.",
    ],
  },
  // ── Plaid bank feed (connect / mapping / re-auth refusals) ────────────
  PLAID_NOT_CONFIGURED: {
    severity: "service",
    title: "Bank connections aren’t available yet",
    message:
      "This environment isn’t connected to the bank-feed provider, so a bank can’t be linked right now.",
    retryable: false,
    nextSteps: [
      "Import a bank statement instead, or",
      "Ask OrderUp support to enable bank connections for this environment.",
    ],
  },
  PLAID_WEBHOOK_URL_MISSING: {
    severity: "service",
    title: "Bank connections aren’t available yet",
    message:
      "The bank-feed provider has nowhere to send updates in this environment, so linking is paused rather than creating a connection that would never sync.",
    retryable: false,
    nextSteps: ["Ask OrderUp support to finish configuring bank connections."],
  },
  // Legacy alias of BANK_PROVIDER_ACCOUNT_UNKNOWN — this code no longer raises
  // it (plan 1 Task 8); kept one release so a client talking to an API
  // deployment from before the switch still translates the refusal.
  PLAID_ACCOUNT_UNKNOWN: {
    severity: "user_input",
    title: "That account isn’t on this bank login",
    message:
      "The bank didn’t return one of the accounts you selected, so nothing was linked.",
    retryable: true,
    nextSteps: [
      "Start Connect bank again and pick from the accounts the bank shows.",
    ],
  },
  BANK_CONNECTION_UNAVAILABLE: {
    severity: "user_input",
    title: "Your bank needs to be reconnected",
    message:
      "This bank connection isn’t active right now, so nothing was sent to or read from the bank.",
    retryable: true,
    nextSteps: ["Reconnect the bank from Connect bank, then try again."],
  },
  BANK_PROVIDER_UNAVAILABLE: {
    severity: "service",
    title: "Bank connections aren’t available",
    message:
      "We can’t reach that bank-connection service right now. Nothing was changed.",
    retryable: true,
    nextSteps: ["Try again in a few minutes."],
  },
  BANK_PROVIDER_NOT_CONFIGURED: {
    severity: "service",
    title: "Bank connections aren’t set up yet",
    message:
      "This environment isn’t configured for that bank-connection service, so a bank can’t be linked right now.",
    retryable: false,
    nextSteps: ["Ask OrderUp support to enable bank connections for this environment."],
  },
  BANK_CONNECTION_IDENTITY_MISMATCH: {
    severity: "user_input",
    title: "That’s a different bank login",
    message:
      "The sign-in you completed belongs to a different bank login than the one being reconnected, so nothing was changed.",
    retryable: true,
    nextSteps: ["Reconnect using the same bank login you linked originally."],
  },
  BANK_CONNECTION_SCOPE_MISSING: {
    severity: "auth",
    title: "This connection can’t do that",
    message:
      "The access you granted to this bank connection doesn’t include this action.",
    retryable: false,
    nextSteps: ["Reconnect the bank and grant the access this action needs."],
  },
  BANK_PROVIDER_ACCOUNT_UNKNOWN: {
    severity: "user_input",
    title: "That account isn’t on this bank login",
    message:
      "The bank didn’t return one of the accounts you selected, so nothing was linked.",
    retryable: true,
    nextSteps: [
      "Start Connect bank again and pick from the accounts the bank shows.",
    ],
  },
  BANK_CONNECTION_REAUTH_INCOMPLETE: {
    severity: "user_input",
    title: "The bank still needs you to sign in",
    message:
      "The bank hasn’t accepted the new sign-in yet, so this connection is still paused.",
    retryable: true,
    nextSteps: [
      "Finish the bank’s sign-in prompts, then choose Reconnect again.",
    ],
  },
  BANK_CONNECTION_ALREADY_LINKED: {
    severity: "user_input",
    title: "This bank is already connected",
    message:
      "That bank sign-in is already connected here, so nothing new was linked and the existing connection was left as it is.",
    retryable: false,
    nextSteps: [
      "Use Add accounts or Reconnect on the existing connection instead.",
    ],
  },
  BANK_LINK_SCOPE_FORBIDDEN: {
    severity: "auth",
    title: "You can’t connect a bank here",
    message:
      "Connecting a bank for the whole business takes the owner or an admin, and connecting one for a location takes the Connect Bank Accounts permission at that location. Nothing was linked.",
    retryable: false,
    nextSteps: ["Ask the business owner or an admin to connect this bank, or to grant you Connect Bank Accounts here."],
  },
  BANK_PAYMENT_MODE_UNPROVEN: {
    severity: "user_input",
    title: "Direct payments aren’t proven yet",
    message:
      "This bank login hasn’t been proven able to send payments directly, so it was left as it was.",
    retryable: true,
    nextSteps: [
      "Allow the address shown on this page on the bank key, then choose Verify again.",
    ],
  },
  BANK_PAYMENT_MODE_REQUEST_UNAVAILABLE: {
    severity: "user_input",
    title: "Payments can’t wait for approval at the bank yet",
    message:
      "Add a second administrator in your bank before payments can wait for approval there.",
    retryable: true,
    nextSteps: [
      "Invite a second administrator in your bank, then choose Verify again.",
    ],
  },
  BANK_CARDHOLDER_NOT_FOUND: {
    severity: "user_input",
    title: "That person can’t hold a card yet",
    message:
      "Your bank doesn’t list that person as someone who can hold a company card on this login, so nobody was linked.",
    retryable: true,
    nextSteps: ["Invite them in your bank first, then refresh this page and link them."],
  },
  BANK_CARDHOLDER_EMPLOYEE_NOT_FOUND: {
    severity: "user_input",
    title: "That employee wasn’t found",
    message:
      "That employee isn’t on the staff list of the location you chose, so nobody was linked.",
    retryable: true,
    nextSteps: ["Pick an employee from the list again."],
  },
  BANK_CARDHOLDER_LOCATION_MISMATCH: {
    severity: "user_input",
    title: "That employee works somewhere else",
    message:
      "This bank login belongs to one location, so only that location’s employees can be linked to it. Nobody was linked.",
    retryable: true,
    nextSteps: ["Pick an employee who works at this login’s location."],
  },
  BANK_CARDHOLDER_ALREADY_LINKED: {
    severity: "user_input",
    title: "Already linked",
    message:
      "Either that person at your bank or that employee is already linked on this login, so nothing was changed.",
    retryable: false,
    nextSteps: ["Unlink the existing pair first if it is wrong, then link again."],
  },
  BANK_CARDHOLDER_DIRECTORY_UNAVAILABLE: {
    severity: "service",
    title: "Company cards aren’t ready yet",
    message:
      "Linking people to company cards isn’t available yet, so nothing was changed.",
    retryable: true,
    nextSteps: ["Try again later, or contact OrderUp support if this keeps happening."],
  },
  MERCURY_USER_CANNOT_HOLD_CARDS: {
    severity: "user_input",
    title: "That person can’t hold a card yet",
    message:
      "Mercury doesn’t let that person hold a company card yet, so no card was issued.",
    retryable: true,
    nextSteps: ["Invite them in Mercury first, then issue the card again."],
  },
  MERCURY_LIMIT_REQUIRED: {
    severity: "user_input",
    title: "Set a spend limit",
    message: "Mercury needs every company card to have a spend limit, so nothing was changed.",
    retryable: true,
    nextSteps: ["Enter a spend limit and a period, then try again."],
  },
  MERCURY_CANCEL_UNSUPPORTED: {
    severity: "user_input",
    title: "Cancel it in Mercury",
    message: "This can’t be cancelled from OrderUp. Cancel it in Mercury.",
    retryable: false,
    nextSteps: ["Open Mercury and cancel it there; OrderUp picks up the change."],
  },
  BANK_FEED_VIEW_FORBIDDEN: {
    severity: "auth",
    title: "You can’t see this bank feed",
    message:
      "Seeing the bank accounts that feed a location’s books takes the View Bank Feed permission at that location.",
    retryable: false,
    nextSteps: ["Ask the business owner or an admin to grant you View Bank Feed here."],
  },
  BANK_FEED_LOCATION_REQUIRED: {
    severity: "user_input",
    title: "Choose a location for each account",
    message:
      "This bank login is for the whole business, so each account has to feed one location’s books. Nothing was linked.",
    retryable: true,
    nextSteps: ["Pick the location each account feeds, or skip the account."],
  },
  BANK_FEED_LOCATION_MISMATCH: {
    severity: "user_input",
    title: "That account belongs to this location",
    message:
      "This bank login is this location’s own, so its accounts can only feed this location’s books. Nothing was linked.",
    retryable: true,
    nextSteps: ["Connect the bank for the whole business instead if its accounts feed several locations."],
  },
  BANK_PROVIDER_ACCESS_ROUTE_DISABLED: {
    severity: "service",
    title: "This way of connecting isn’t available yet",
    message:
      "Connecting this bank this way (with your own key, or by signing in to it) isn’t switched on for businesses yet, so nothing was linked.",
    retryable: false,
    nextSteps: ["Contact OrderUp support if you need this bank connected."],
  },
  FEATURE_FLAG_DISABLED: {
    severity: "service",
    title: "This isn’t available for your account yet",
    message: "This feature hasn’t been switched on for your business yet, so nothing was changed.",
    retryable: false,
    nextSteps: ["Contact OrderUp support if you need it turned on."],
  },
  MERCURY_TOKEN_INVALID: {
    severity: "user_input",
    title: "Mercury didn’t accept that token",
    message:
      "That API token didn’t work. It may be mistyped, missing the part that starts with secret-token:, or created for Mercury’s test environment. Nothing was linked.",
    retryable: true,
    nextSteps: [
      "Paste the whole token, starting with secret-token:",
      "In Mercury, check the token is still active and has the access you chose.",
    ],
  },
  BANK_PROVIDER_CREDENTIAL_REFUSED: {
    severity: "user_input",
    title: "The bank didn’t accept that key",
    message:
      "The bank refused the key for this login, so nothing was read or changed. The key may be mistyped, revoked, or limited to addresses that don’t include ours.",
    retryable: true,
    nextSteps: [
      "At the bank, check the token is correct and still active.",
      "If the token has an IP allowlist, add the address shown when you connected the bank.",
    ],
  },
  BANK_LINK_SCOPE_UNSUPPORTED: {
    severity: "user_input",
    title: "This bank connects one location at a time",
    message:
      "This bank can’t be linked for the whole business at once, so nothing was linked.",
    retryable: true,
    nextSteps: ["Choose a location and connect the bank there."],
  },
  BANK_ACCOUNT_ALREADY_FED: {
    severity: "user_input",
    title: "That account is already connected",
    message:
      "This bank account already feeds your books through another bank login, so it wasn’t added a second time.",
    retryable: false,
    nextSteps: ["Use the existing connection, or remove it first if you want this login to feed the account instead."],
  },
  BANK_ACCOUNT_HAS_HISTORY: {
    severity: "user_input",
    title: "This account already has history",
    message:
      "This bank account already has history in one of your ledger accounts, so a new account would stay empty. Map it to that account to continue the feed.",
    retryable: false,
    nextSteps: ["Choose “Map to an existing account” and pick the account it fed before. Nothing was linked."],
  },
  BANK_ACCOUNT_HISTORY_OTHER_LOCATION: {
    severity: "user_input",
    title: "This account's history is at another location",
    message:
      "This bank account’s history is kept in another location’s books. Connect it from that location, or ask the owner to connect it business-wide.",
    retryable: false,
    nextSteps: ["Nothing was linked. Leave this account out here and connect it where its history is kept."],
  },
  BANK_ACCOUNT_HISTORY_INACTIVE: {
    severity: "user_input",
    title: "This account's ledger account is switched off",
    message:
      "This bank account’s history is in a ledger account that is switched off. Reactivate it in Chart of accounts to continue the feed.",
    retryable: false,
    nextSteps: ["Nothing was linked. Reactivate the ledger account, then connect this bank account again."],
  },
  BANK_LINK_DECLINED: {
    severity: "user_input",
    title: "The bank sign-in was cancelled",
    message: "The sign-in at the bank wasn’t completed, so nothing was linked.",
    retryable: true,
    nextSteps: ["Start connecting the bank again when you’re ready."],
  },
  BANK_LINK_STATE_INVALID: {
    severity: "user_input",
    title: "That bank sign-in has expired",
    message: "The bank sign-in took too long, or was started somewhere else, so nothing was linked.",
    retryable: true,
    nextSteps: ["Start connecting the bank again and finish the sign-in within a few minutes."],
  },
  PLAID_ITEM_UNHEALTHY: {
    severity: "user_input",
    title: "The bank still needs you to sign in",
    message:
      "The bank hasn’t accepted the new sign-in yet, so this connection is still paused.",
    retryable: true,
    nextSteps: [
      "Finish the bank’s sign-in prompts, then choose Reconnect again.",
    ],
  },
  PLAID_SANDBOX_ONLY: {
    severity: "service",
    title: "Test-only action",
    message: "That action only exists for the sandbox bank environment.",
    retryable: false,
  },
  BOOKS_GL_ACCOUNT_SIDE_MISMATCH: {
    severity: "user_input",
    title: "That account is the wrong type for this feed",
    message:
      "A credit card feed has to map onto a Credit Card account and a bank feed onto a Cash and Bank account.",
    retryable: true,
    nextSteps: [
      "Pick an account of the matching type, or choose Create new account.",
    ],
  },
  BOOKS_GL_ACCOUNT_ALREADY_LINKED: {
    severity: "user_input",
    title: "That account already has a bank feed",
    message:
      "Another connection already sends transactions into this account, so a second feed would double every transaction.",
    retryable: true,
    nextSteps: [
      "Pick a different account, or disconnect the existing feed first under Banking → Connections.",
    ],
  },
  BOOKS_CONNECTION_HAS_TRANSACTIONS: {
    severity: "user_input",
    title: "This connection has already posted transactions",
    message:
      "Its transactions are attached to the current account, so moving the feed now would leave them behind.",
    retryable: false,
    nextSteps: [
      "Keep the current account, or disconnect and connect the bank again onto the account you want (past transactions stay where they were posted).",
    ],
  },
  BOOKS_ACCOUNT_NOT_FOUND: {
    severity: "user_input",
    title: "Account not found",
    message: "The account you picked no longer exists or is inactive.",
    retryable: true,
    nextSteps: ["Refresh the list and pick again."],
  },
  BOOKS_ACCOUNT_IN_USE: {
    severity: "user_input",
    title: "This account can’t be deleted",
    message:
      "It’s a standard account, it has activity, or other accounts sit under it. Deleting it would break the history that points at it.",
    retryable: false,
    nextSteps: ["Archive it instead. It leaves your active chart and its history stays intact."],
  },
  BOOKS_ACCOUNT_CODE_CONFLICT: {
    severity: "user_input",
    title: "That account code is taken",
    message: "Another account in this chart already uses that code.",
    retryable: false,
    nextSteps: ["Pick a different code."],
  },
  BOOKS_DOCUMENT_NOT_OPEN: {
    severity: "user_input",
    title: "One of these documents is no longer open",
    message:
      "A document in this payment has been paid, voided, or written off since this screen loaded, so it can’t take a payment now.",
    retryable: true,
    nextSteps: [
      "Reload to see which documents are still open.",
      "Then re-apply the payment across the remaining ones.",
    ],
  },
  BOOKS_PAYMENT_MULTIPLE_PARTIES: {
    severity: "user_input",
    title: "One payment, one customer",
    message:
      "These documents belong to more than one customer or vendor. A single payment records who it came from, so it can’t span two of them — combining them would misstate who paid.",
    retryable: false,
    nextSteps: [
      "Remove the documents that belong to a different party.",
      "Record a separate payment for them.",
    ],
  },
  BOOKS_PAYMENT_DOCUMENT_DUPLICATED: {
    severity: "user_input",
    title: "The same document is listed twice",
    message:
      "One document appears more than once in this payment. Each document takes a single share.",
    retryable: false,
    nextSteps: ["Combine the duplicate rows into one and re-submit."],
  },
  BOOKS_PAYMENT_NO_APPLICATIONS: {
    severity: "user_input",
    title: "Nothing selected to pay",
    message: "A payment needs at least one document to apply to.",
    retryable: false,
    nextSteps: ["Select at least one open invoice or bill."],
  },
  BOOKS_PAYMENT_ALLOCATION_INVALID: {
    severity: "user_input",
    title: "One of the lines is incomplete",
    message:
      "A line is missing its document or its amount, or the amount isn’t greater than zero.",
    retryable: false,
    nextSteps: ["Check each line has a document and a positive amount."],
  },
  BOOKS_PAYMENT_NOT_APPLICABLE: {
    severity: "user_input",
    title: "This payment can’t be applied",
    message:
      "Only a completed payment carries a balance that can be applied. This one is pending confirmation or has been voided.",
    retryable: false,
    nextSteps: [
      "Confirm the payment first if it’s awaiting confirmation.",
      "A voided payment can’t be applied — record a new one.",
    ],
  },
  // BOOKS_PAYMENT_INVALID_DATA is NOT re-declared here. It already exists for
  // `create_standalone_payment`, and the new RPCs raise that same code
  // deliberately — one missing-required-field refusal, one piece of copy.

  BOOKS_PAYMENT_EXCEEDS_AMOUNT_DUE: {
    severity: "user_input",
    title: "Amount is more than what’s owed",
    message:
      "The payment is larger than the remaining balance due on this document.",
    retryable: false,
    nextSteps: ["Lower the amount to at most the balance due."],
  },
  // A payment already on its way (a bank payment inside its return window, a
  // card payment awaiting settlement) has a Pending receipt, so the balance
  // does not show it yet. Raised by the database and by the API's own checks
  // before a card is charged — the card is never charged when this is shown.
  BOOKS_PAYMENT_PENDING_ON_INVOICE: {
    severity: "user_input",
    title: "A payment is still processing",
    message:
      "A payment on this invoice is still processing, so it can’t be collected again yet.",
    retryable: false,
    nextSteps: [
      "Wait for that payment to clear — about 5 business days for a bank payment.",
      "Or cancel or refund that payment first, then collect the balance.",
    ],
  },
  BOOKS_PAYMENT_AMOUNT_INVALID: {
    severity: "user_input",
    title: "Enter a valid amount",
    message: "The payment amount must be a positive number.",
    retryable: false,
  },
  BOOKS_BILL_NOT_PAYABLE: {
    severity: "user_input",
    title: "This bill can’t take a payment",
    message:
      "This bill isn’t in a state that accepts payments (it may be a draft, void, or already paid).",
    retryable: false,
  },
  BOOKS_INVOICE_NOT_PAYABLE: {
    severity: "user_input",
    title: "This invoice can’t take a payment",
    message:
      "This invoice isn’t in a state that accepts payments (it may be a draft, void, or already paid).",
    retryable: false,
  },
  BOOKS_INVOICE_LOCKED_ACCEPTED: {
    severity: "user_input",
    title: "Invoice is locked",
    message:
      "The linked business already accepted this invoice, so its prices can’t change — add charges on a supplemental invoice.",
    retryable: false,
  },
  BOOKS_LINKED_BILL_PAID: {
    severity: "user_input",
    title: "Already paid — can’t retract",
    message:
      "The linked business already paid the mirrored bill, so this invoice can’t be retracted; issue a credit note and refund instead.",
    retryable: false,
  },
  BOOKS_BILL_HAS_PAYMENTS: {
    severity: "user_input",
    title: "This bill has payments",
    message:
      "This bill already has payments recorded against it; void those payments before editing or deleting it.",
    retryable: false,
  },
  BOOKS_INVOICE_HAS_PAYMENTS: {
    severity: "user_input",
    title: "This invoice has payments",
    message:
      "This invoice already has payments recorded against it; void those payments before editing or deleting it.",
    retryable: false,
  },
  BOOKS_BILL_NOT_EDITABLE: {
    severity: "user_input",
    title: "This bill can't be edited",
    message:
      "A voided bill is final and can't be changed. Enter a new bill instead.",
    retryable: false,
  },
  BOOKS_BILL_PO_LINKED_NOT_EDITABLE: {
    severity: "user_input",
    title: "This bill came from a purchase order",
    message:
      "Its lines are what tell the purchase order how much has been billed, so editing them here would put the two documents out of step.",
    retryable: false,
    nextSteps: [
      "Void this bill, then convert the purchase order again with the corrected amounts.",
    ],
  },
  BOOKS_BILL_MIRRORED_NOT_EDITABLE: {
    severity: "user_input",
    title: "This bill mirrors a linked invoice",
    message:
      "It's a copy of the invoice the other business sent you, so it changes only when they change theirs.",
    retryable: false,
    nextSteps: ["Ask them to revise the invoice, or void this bill."],
  },
  BOOKS_BILL_NUMBER_CONFLICT: {
    severity: "user_input",
    title: "That bill number is already used",
    message:
      "Another bill for this location already uses that number. Bill numbers have to be unique.",
    retryable: false,
  },
  // Raised by reverse_completed_payment when the payment is already applied,
  // partially refunded, voided, or in any other state that prevents reversal.
  // Also surfaced through the document-match path (match_bank_transaction_to_document)
  // when an unmatch triggers a payment reversal that can't proceed.
  BOOKS_PAYMENT_NOT_REVERSIBLE: {
    severity: "user_input",
    title: "This payment can't be reversed",
    message:
      "Only a fully posted, unallocated payment can be reversed — this one has already been applied, refunded, or voided.",
    retryable: false,
    nextSteps: [
      "Remove any allocations or refunds tied to this payment first, then reverse it.",
    ],
  },
  // The void refunded the card and then could not finish the books half. The
  // money IS back — this is the one failure where "try again" is the WRONG
  // instruction, because a second attempt would reverse a second time. The
  // copy exists to stop that, and to be unmistakably a support call.
  BOOKS_VOID_CASCADE_INCOMPLETE: {
    severity: "service",
    title: "The refund went through — the invoice did not",
    message:
      "The customer's money has been given back, but the invoice could not be updated afterwards. Nothing further is needed from the customer.",
    retryable: false,
    nextSteps: [
      "Do NOT void or refund again — the money has already been returned.",
      "Contact support with this invoice number so the books can be squared up.",
    ],
  },
  // The processor actively DECLINED the reversal. A real answer from them, so
  // the operator is told it was refused rather than that we broke.
  BOOKS_PAYMENT_REVERSAL_REJECTED: {
    severity: "user_input",
    title: "The processor declined the refund",
    message:
      "The payment processor refused to reverse this charge. Nothing was changed — the customer is still charged and the invoice is unchanged.",
    retryable: false,
    nextSteps: [
      "The reference below carries the processor's own code and wording — quote it if you call them.",
      "A charge that has just settled often cannot be reversed for a short window. Try again shortly.",
    ],
  },
  // The reversal did not come back approved for some other reason — a timeout,
  // an ambiguous answer, a gateway fault.
  //
  // UNCATALOGUED, THIS RENDERED AS "Something went wrong on our end", which is
  // what let a bug in our own reversal code look identical to a processor
  // problem for an operator watching real money. The nextSteps deliberately do
  // NOT say "try again" without qualification: if the reversal actually
  // succeeded and we misread it, a blind retry reverses twice.
  BOOKS_PAYMENT_REVERSAL_FAILED: {
    severity: "service",
    title: "The refund did not go through",
    message:
      "The payment processor did not confirm the reversal, so nothing was changed here — the invoice is unchanged and still locked.",
    retryable: true,
    nextSteps: [
      "Check the payment in the processor's portal BEFORE retrying — if it already shows as voided or refunded, do not reverse it again.",
      "The reference below carries the processor's own code and wording.",
    ],
  },
  // Nothing to reverse: the money was taken outside OrderUp.
  BOOKS_PAYMENT_NOT_GATEWAY_CHARGED: {
    severity: "user_input",
    title: "This payment wasn't taken through OrderUp",
    message:
      "It was recorded here for money collected somewhere else, so there is nothing for us to give back.",
    retryable: false,
    nextSteps: [
      "Void the invoice directly — no card reversal is needed.",
      "Refund the customer wherever the money was actually taken.",
    ],
  },
  // Raised by create_standalone_payment when the payload is missing required
  // fields (tenant, restaurant, method, or amount) or when the payment_type
  // is not 'Received' or 'Made'.
  BOOKS_PAYMENT_INVALID_DATA: {
    severity: "user_input",
    title: "Check the payment details",
    message:
      "The payment couldn't be saved because one or more required fields are missing or the payment type isn't recognized.",
    retryable: false,
    nextSteps: ["Check that the amount, payment method, and payment direction are all filled in."],
  },
  // Raised by void_payment, confirm_payment, reject_payment, and the banking
  // match RPC when a payment ID cannot be found.
  BOOKS_PAYMENT_NOT_FOUND: {
    severity: "user_input",
    title: "Payment not found",
    message:
      "We couldn't find that payment — it may have been deleted or the page is out of date.",
    retryable: false,
    nextSteps: ["Refresh the payments list and try again."],
  },
  // Raised by confirm_payment when the payment row has been soft-deleted.
  BOOKS_PAYMENT_DELETED: {
    severity: "user_input",
    title: "Payment was deleted",
    message:
      "This payment has been deleted and can no longer be confirmed.",
    retryable: false,
  },
  // Raised by confirm_payment when the payment isn't in 'Pending' status or
  // already has a posted journal entry — confirmation is not applicable.
  BOOKS_PAYMENT_NOT_CONFIRMABLE: {
    severity: "user_input",
    title: "This payment can't be confirmed",
    message:
      "Only a pending payment can be confirmed — this one has already been completed, voided, or is in a state that doesn't allow confirmation.",
    retryable: false,
  },
  // Raised by confirm_payment when the payment has no allocation to a bill or
  // invoice — the confirmation step requires knowing which document it settles.
  BOOKS_PAYMENT_NO_ALLOCATION: {
    severity: "user_input",
    title: "Payment isn't applied to anything",
    message:
      "This payment hasn't been applied to a bill or invoice yet, so it can't be confirmed.",
    retryable: false,
    nextSteps: ["Apply this payment to the relevant bill or invoice, then confirm it."],
  },
  // Raised by reject_payment when the payment isn't in 'Pending' status.
  // Only a payment that hasn't been confirmed yet can be disputed.
  BOOKS_PAYMENT_NOT_REJECTABLE: {
    severity: "user_input",
    title: "This payment can't be disputed",
    message:
      "Only a pending payment can be disputed — this one has already been confirmed or voided.",
    retryable: false,
  },
  // Raised by void_payment when the payment is already voided (status =
  // 'Voided' or soft-deleted).
  BOOKS_PAYMENT_ALREADY_VOIDED: {
    severity: "user_input",
    title: "Already voided",
    message:
      "This payment has already been voided — no further action is needed.",
    retryable: false,
  },
  // Raised by void_payment when the payment has one or more allocations to a
  // bill or invoice. Those allocations must be removed first so the document
  // balances aren't left inconsistent.
  BOOKS_PAYMENT_HAS_ALLOCATIONS: {
    severity: "user_input",
    title: "Remove the applied amounts first",
    message:
      "This payment has been applied to one or more bills or invoices — unapply it from each one before voiding.",
    retryable: false,
    nextSteps: ["Unapply this payment from its bill or invoice, then void it."],
  },
  // Raised by create_standalone_payment when the payment type is 'Received'
  // (money in) but no customer is specified.
  BOOKS_PAYMENT_CUSTOMER_REQUIRED: {
    severity: "user_input",
    title: "Select a customer",
    message:
      "A payment received from a customer needs a customer on the record.",
    retryable: false,
    nextSteps: ["Select the customer this payment came from and try again."],
  },
  // Raised by create_standalone_payment when the payment type is 'Made'
  // (money out) but no vendor is specified.
  BOOKS_PAYMENT_VENDOR_REQUIRED: {
    severity: "user_input",
    title: "Select a vendor",
    message:
      "A payment made to a vendor needs a vendor on the record.",
    retryable: false,
    nextSteps: ["Select the vendor this payment was made to and try again."],
  },

  // ── BOOKS — FISCAL PERIODS ────────────────────────────────────────────────
  // `create_journal_entry` and `post_journal_entry` raise this for ANY posting
  // whose entry date lands in a closed period — a closed period row, OR any
  // month of a fiscal year that has been closed (book_fiscal_period_is_closed).
  // A processor payout reconciled after a month-end close is the common way an
  // operator meets it. The subclass and the 409 mapping already existed; the
  // COPY did not, so the operator saw the raw code.
  //
  // The next steps used to say "Reopen that period under Books → Fiscal
  // Periods". Nothing reopens a period or a year — not that page, not an RPC —
  // so the first instruction sent people looking for a button that does not
  // exist. The accurate actions are: date it in an open period, or take it to
  // whoever closes the books.
  BOOKS_FISCAL_PERIOD_CLOSED: {
    severity: "user_input",
    title: "That accounting period is closed",
    message:
      "This entry is dated inside a period that has already been closed, so Books won’t post into it. Nothing was recorded.",
    retryable: false,
    nextSteps: [
      "Date the entry in a period that is still open — a correction to a closed month is posted as an adjusting entry dated in the current period.",
      "If it has to land in the closed period, talk to whoever closes your books. Books can’t reopen a closed period.",
    ],
  },
  // Raised by `book_create_fiscal_period` (compute_fiscal_period_range) for a
  // period number the location's calendar does not have — 13 on a twelve-period
  // calendar, or anything outside 1..13.
  BOOKS_INVALID_PERIOD_NUMBER: {
    severity: "user_input",
    title: "That period isn’t on your fiscal calendar",
    message:
      "Your fiscal calendar has 12 periods a year (13 on a four-week calendar), and this period number is outside it. Nothing was created.",
    retryable: false,
    nextSteps: ["Choose a period number your fiscal calendar has."],
  },
  // Raised by `book_create_fiscal_period` when the start/end dates sent for a
  // NEW period are not the range the fiscal calendar gives that period. Posting
  // places entries by the calendar, never by a period's stored dates, so a row
  // with other dates would misstate which entries it covers.
  BOOKS_FISCAL_PERIOD_RANGE_MISMATCH: {
    severity: "user_input",
    title: "Those dates don’t match the period",
    message:
      "A fiscal period’s dates come from your fiscal calendar, and the dates given don’t match it. Nothing was created.",
    retryable: false,
    nextSteps: ["Use the start and end dates shown for that period."],
  },
  // Raised by `book_create_fiscal_period` when the fiscal year or period number
  // is absent. The route's schema requires both, so reaching this means a
  // caller bypassed it.
  BOOKS_FISCAL_PERIOD_MISSING_REQUIRED_FIELDS: {
    severity: "user_input",
    title: "Choose a fiscal year and period",
    message:
      "A fiscal period needs both its fiscal year and its period number. Nothing was created.",
    retryable: false,
    nextSteps: ["Fill in the fiscal year and the period number, then try again."],
  },
  // Raised by the `enforce_fiscal_year_start_lock` trigger on `book_settings`
  // when the fiscal CALENDAR is changed after any journal entry exists: the
  // fiscal-year start month, or (since 2026-09-15) the period count — 12
  // months or 13 four-week periods. Every entry is stamped with a fiscal year
  // and period computed from both, so changing either would silently move
  // posted entries between years or periods. PUT /books/settings answers 409.
  BOOKS_FISCAL_YEAR_LOCKED: {
    severity: "user_input",
    title: "Your fiscal calendar can’t change now",
    message:
      "Journal entries already exist, and each one is placed in a fiscal year and period by the current fiscal year start and period layout. Changing either would move posted entries, so nothing was saved.",
    retryable: false,
    nextSteps: [
      "Put the fiscal year start (and period layout) back to what they were, then save your other changes.",
      "If the calendar was set up wrong, contact OrderUp support before posting anything else.",
    ],
  },

  // ── BOOKS — PAYMENT SOURCES & PAYOUT SETTLEMENT ───────────────────────────
  //
  // A "source" is a processor (Stripe, Square, PayPal) or the house Undeposited
  // Funds source. Receipts clear at FULL GROSS into the source's clearing
  // account; when the payout lands in the bank feed the operator settles it and
  // the fee is DERIVED (selected receipts − what actually landed), never typed.
  //
  // None of the copy below says "invalid selection" or names a code. Every
  // refusal on this screen interrupts someone reconciling real money, so each
  // one names the specific thing that changed and the specific next action.
  // ── The processor's statement, and the fee withdrawal it explains ────────
  //
  // A fee withdrawal is a bank line the operator used to have NO way to
  // reconcile: the payouts tab is hidden on a debit and both match sources are
  // empty for a processor fee, so Categorize by hand was the only action. The
  // copy below is written for someone who has just discovered a new one and
  // does not yet know what "reported fees" are.
  BOOKS_FEE_CHARGE_EVIDENCE_EXCEEDS_LINE: {
    severity: "user_input",
    title: "You've ticked more in fees than this withdrawal paid",
    message:
      "The fees you selected add up to more than the money that actually left the account, so some of them weren't paid by this withdrawal. Nothing was posted.",
    retryable: false,
    nextSteps: [
      "Untick the fees this withdrawal can't have covered — they stay on the list for the next one.",
      "If the processor pulled several days at once, tick only the days that fit inside this amount.",
    ],
  },
  BOOKS_FEE_CHARGE_AMOUNT_CHANGED: {
    severity: "user_input",
    title: "This withdrawal changed while you were working",
    message:
      "The bank line is no longer the amount shown when this screen loaded — it was edited or re-imported. Nothing was posted.",
    retryable: true,
    nextSteps: [
      "Reload the line and check the amount.",
      "Charge the fees again once the amount shown is the one on your statement.",
    ],
  },
  BOOKS_FEE_CHARGE_ENTRY_ALREADY_CHARGED: {
    severity: "user_input",
    title: "One of these fees was already paid by another withdrawal",
    message:
      "A fee you selected has already been matched to a different bank line. Charging it twice would expense the same money twice.",
    retryable: false,
    nextSteps: [
      "Deselect the fee that's already been paid — the list refreshes to show only unpaid ones.",
      "If the earlier match was wrong, undo it on that line first, then come back here.",
    ],
  },
  BOOKS_FEE_CHARGE_MIXED_PROCESSORS: {
    severity: "user_input",
    title: "These fees came from two different payment sources",
    message:
      "One withdrawal can only be posted to one source's fee account, and you've selected fees from more than one.",
    retryable: false,
    nextSteps: [
      "Charge each source's fees against its own withdrawal.",
      "If a single withdrawal really did cover both, categorize it manually instead.",
    ],
  },
  BOOKS_FEE_CHARGE_PROCESSOR_UNKNOWN: {
    severity: "user_input",
    title: "We don't know which source reported these fees",
    message:
      "These fees arrived before a payment source was set up in Books, so there's no fee account to post them to.",
    retryable: true,
    nextSteps: [
      "Set the payment source up under Books settings → Deposits.",
      "The next sync attaches these fees to it, and this withdrawal can then be charged.",
    ],
  },
  BOOKS_FEE_CHARGE_ENTRY_NOT_A_FEE: {
    severity: "user_input",
    title: "One of these lines isn't a fee",
    message:
      "A deposit, refund or chargeback was selected. A withdrawal pays fees; charging anything else here would take real money out of the books twice.",
    retryable: false,
    nextSteps: ["Select only the reported fees, then charge the withdrawal again."],
  },
  BOOKS_FEE_CHARGE_ENTRY_NOT_FOUND: {
    severity: "user_input",
    title: "One of these fees is no longer available",
    message:
      "A fee you selected has been removed or belongs to another business. Nothing was posted — the whole selection is refused rather than quietly narrowed, because a narrowed set changes what gets marked as paid.",
    retryable: true,
    nextSteps: ["Reload the line and select from the refreshed list."],
  },
  BOOKS_FEE_CHARGE_ENTRY_DUPLICATED: {
    severity: "user_input",
    title: "The same fee is selected twice",
    message: "One reported fee appears more than once in this selection.",
    retryable: false,
    nextSteps: ["Reload the line and select each fee once."],
  },
  BOOKS_FEE_CHARGE_CURRENCY_MISMATCH: {
    severity: "user_input",
    title: "These fees aren't all in the same currency",
    message:
      "A fee in a different currency was selected. Netting two currencies into one withdrawal would invent an exchange rate.",
    retryable: false,
    nextSteps: ["Charge each currency against its own withdrawal."],
  },
  BOOKS_FEE_CHARGE_ENTRIES_EMPTY: {
    severity: "user_input",
    title: "Select at least one reported fee",
    message: "No fees were selected, so there is nothing to charge this withdrawal against.",
    retryable: false,
    nextSteps: ["Tick the fees this withdrawal paid, then try again."],
  },
  BOOKS_FEE_CHARGE_EXPECTED_AMOUNT_REQUIRED: {
    severity: "service",
    title: "We couldn't confirm the amount you were shown",
    message:
      "The amount displayed on screen wasn't sent back with the request, so we can't be sure the bank line hasn't changed since. Nothing was posted.",
    retryable: true,
    nextSteps: ["Reload the line and try again.", "Contact support if it keeps happening."],
  },
  BOOKS_PROCESSOR_LEDGER_ENTRY_NOT_FOUND: {
    severity: "user_input",
    title: "That reported fee no longer exists",
    message:
      "The processor statement row referenced here has been removed or belongs to another business.",
    retryable: true,
    nextSteps: ["Reload the page and try again."],
  },
  BOOKS_PROCESSOR_LEDGER_ENTRIES_INVALID: {
    severity: "service",
    title: "The processor statement couldn't be read",
    message:
      "The statement data sent to Books wasn't in the expected shape, so nothing was stored.",
    retryable: true,
    nextSteps: ["The next sync retries automatically.", "Contact support if fees stay missing."],
  },
  BOOKS_PROCESSOR_LEDGER_TENANT_REQUIRED: {
    severity: "service",
    title: "The processor statement arrived without a business",
    message:
      "Books couldn't tell which restaurant this statement belongs to, so nothing was stored.",
    retryable: true,
    nextSteps: ["The next sync retries automatically.", "Contact support if fees stay missing."],
  },
  BOOKS_PROCESSOR_LEDGER_PROVIDER_REQUIRED: {
    severity: "service",
    title: "The processor statement arrived without a source",
    message:
      "Books couldn't tell which processor reported these lines, so nothing was stored — two processors can use the same reference numbers, and storing them together would mix up the fees.",
    retryable: true,
    nextSteps: ["The next sync retries automatically.", "Contact support if fees stay missing."],
  },
  BOOKS_PROCESSOR_FEE_SOURCE_INVALID: {
    severity: "service",
    title: "That fee has no recorded origin",
    message:
      "Books records where every per-transaction fee came from. A figure with no stated origin can't be told apart from one somebody typed, so it was refused.",
    retryable: false,
    nextSteps: ["Contact support — this is a wiring problem, not something you can fix here."],
  },
  BOOKS_PROCESSOR_FEE_NEGATIVE: {
    severity: "service",
    title: "That fee is a negative amount",
    message: "A processing fee below zero was refused.",
    retryable: false,
    nextSteps: ["Contact support if a payment shows a negative processing cost."],
  },
  BOOKS_SETTLEMENT_NET_EXCEEDS_GROSS: {
    severity: "user_input",
    title: "This deposit is larger than the payments you selected",
    message:
      "The deposit brought in more money than the receipts you picked add up to, which would mean a negative fee. Something in this payout isn’t recorded in Books yet.",
    retryable: false,
    nextSteps: [
      "Select the remaining receipts this payout covers, so the selected total is at least the deposit.",
      "If a payment is missing entirely, record it against its invoice first, then settle the deposit.",
    ],
  },
  BOOKS_SETTLEMENT_FEE_CHANGED: {
    severity: "user_input",
    title: "The fee changed while you were working",
    message:
      "A receipt in this payout was edited, voided, or settled by someone else since this screen loaded, so the fee is no longer the amount shown. Nothing was posted.",
    retryable: true,
    nextSteps: [
      "Reload the payout and check the selection — the fee recalculates.",
      "Settle again once the fee shown is the one you expect.",
    ],
  },
  BOOKS_SETTLEMENT_PROCESSOR_MISMATCH: {
    severity: "user_input",
    title: "One of these receipts came through a different source",
    message:
      "A receipt you selected was recorded against another payment source, so it isn’t part of this payout. Including it would change the fee this settlement books.",
    retryable: false,
    nextSteps: [
      "Deselect the receipts that don’t belong to this source.",
      "Settle that source’s own payout separately when its deposit arrives.",
    ],
  },
  BOOKS_SETTLEMENT_CURRENCY_MISMATCH: {
    severity: "user_input",
    title: "These receipts aren’t all in the deposit’s currency",
    message:
      "A payout can only settle receipts in the same currency as the deposit — mixing currencies would need an exchange rate nobody has entered.",
    retryable: false,
    nextSteps: [
      "Select only the receipts in the same currency as this deposit.",
      "Settle the other currency against its own deposit.",
    ],
  },
  BOOKS_SETTLEMENT_PAYMENT_NOT_ELIGIBLE: {
    severity: "user_input",
    title: "One of these receipts can’t be settled",
    message:
      "A receipt you selected is no longer available to settle — it may have been voided, already settled, or it isn’t being held in this source’s clearing account.",
    retryable: true,
    nextSteps: [
      "Reload the payout to refresh the list of receipts.",
      "Select from the refreshed list and settle again.",
    ],
  },
  BOOKS_SETTLEMENT_PAYMENT_ALREADY_MATCHED: {
    severity: "user_input",
    title: "One of these receipts is already matched to another bank line",
    message:
      "Settling it here would reconcile the same money twice, so the payout was refused and nothing was posted.",
    retryable: false,
    nextSteps: [
      "Deselect that receipt, or unmatch it from the other bank line first.",
      "Then settle this payout again.",
    ],
  },
  BOOKS_SETTLEMENT_PROCESSOR_NOT_FOUND: {
    severity: "user_input",
    title: "That payment source isn’t set up",
    message:
      "The source this payout was being settled against no longer exists for this business — it may have been archived.",
    retryable: false,
    nextSteps: [
      "Pick a different source for this payout, or",
      "Add or restore the source under Books → Settings → Deposits & payouts.",
    ],
  },
  // The SETTLEMENT this line was matched by is gone — raised by
  // `unmatch_bank_transaction` when `match_kind = 'settlement'` but the
  // settlement row it points at cannot be loaded. Reachable from the ONE
  // control that reverses money on this screen (Unsettle), and a `_NOT_FOUND`
  // suffix grades to 404 — a status `translateError` has no branch for, so
  // without an entry here the operator is told "Something went wrong" about a
  // journal entry that was NOT voided.
  BOOKS_SETTLEMENT_NOT_FOUND: {
    severity: "user_input",
    title: "That payout has already been unsettled",
    message:
      "The settlement behind this deposit isn’t on the books any more — someone else has most likely already unsettled it. Nothing was changed here.",
    retryable: false,
    nextSteps: [
      "Reload the page to see this line as it stands now.",
      "If the receipts are still waiting to clear, settle the deposit again under Deposits & payouts.",
    ],
  },
  BOOKS_PROCESSOR_INACTIVE: {
    severity: "user_input",
    title: "That payment source is switched off",
    message:
      "This source is marked inactive, so new receipts can’t be routed through it and its payouts can’t be settled.",
    retryable: false,
    nextSteps: [
      "Turn the source back on under Books → Settings → Deposits & payouts, or",
      "Choose a different source for this payment.",
    ],
  },
  // The sibling of BOOKS_PROCESSOR_INACTIVE on the SAME lookup: both are raised
  // by `resolve_payment_bank_account`, which is what turns the Source field on
  // the record-payment dialog into a clearing account. Archiving a source in one
  // tab and recording a payment against it in another is the ordinary way to
  // reach this, so it needs copy for exactly the reason the note below gives —
  // and it is NOT `BOOKS_PAYMENT_PROCESSOR_NOT_FOUND`, which the CRUD RPCs raise
  // for a source being edited rather than one being paid into.
  BOOKS_PROCESSOR_NOT_FOUND: {
    severity: "user_input",
    title: "That payment source no longer exists",
    message:
      "The source this payment was going to clear through isn’t on this business’s books any more — it may have been removed on another screen. The payment was not recorded.",
    retryable: false,
    nextSteps: [
      "Reload the page to see the sources as they are now, then pick one.",
      "Or record the payment straight to a bank account instead.",
    ],
  },
  // The three refusals `archive_payment_processor` raises. They have no typed
  // error CLASS (they fall through `mapPgErrorToBooksError` as generic
  // `BooksError`s, which the suffix ladder grades into a sane 4xx) but they
  // still need copy: Remove is the one control on the sources screen whose most
  // likely outcome IS a refusal, and without an entry here every one of them
  // reads "Something went wrong".
  BOOKS_PROCESSOR_HOUSE_NOT_ARCHIVABLE: {
    severity: "user_input",
    title: "This source is built in and can’t be removed",
    message:
      "Undeposited Funds is where every payment with no processor is held until you bank it. Removing it would leave that money with nowhere to sit.",
    retryable: false,
    nextSteps: [
      "If you don’t want it offered when recording a payment, clear its payment methods instead.",
    ],
  },
  BOOKS_PROCESSOR_HAS_UNSETTLED_PAYMENTS: {
    severity: "user_input",
    title: "Money is still waiting to clear through this source",
    message:
      "Payments recorded against this source haven’t been settled against a deposit yet. Removing it now would strand them in an account nothing can clear, so nothing was changed.",
    retryable: false,
    nextSteps: [
      "Open Banking, find the deposits those payments arrived in, and settle them under Deposits & payouts.",
      "If they will never arrive, reverse or void those payments first.",
      "Switch the source off instead — that stops new payments being routed to it while the old ones settle.",
    ],
  },
  BOOKS_PAYMENT_PROCESSOR_NOT_FOUND: {
    severity: "user_input",
    title: "That payment source no longer exists",
    message:
      "The source you were changing isn’t on this business’s books any more — it may have been removed on another screen.",
    retryable: false,
    nextSteps: [
      "Reload the page to see the sources as they are now.",
    ],
  },
  BOOKS_PAYMENT_SETTLED: {
    severity: "user_input",
    title: "This payment is part of a settled payout",
    message:
      "The deposit that brought this money into the bank has already been settled, so the payment can’t be voided, reversed, or unapplied on its own — that would leave a reconciled deposit pointing at money that no longer exists.",
    retryable: false,
    nextSteps: [
      "Open the bank deposit this receipt settled into and choose Unmatch — that releases every receipt in the payout.",
      "Void or reverse the payment, then settle the deposit again with the corrected receipts.",
    ],
  },
  BOOKS_INVOICE_HAS_SETTLED_PAYMENT: {
    severity: "user_input",
    title: "This invoice was paid through a settled payout",
    message:
      "Voiding it would destroy a receipt that a reconciled bank deposit depends on, so the void was refused and nothing was changed.",
    retryable: false,
    nextSteps: [
      "Open the bank deposit that settled this receipt and choose Unmatch.",
      "Void the invoice, then settle the deposit again with the corrected receipts.",
    ],
  },
  BOOKS_PROCESSOR_CLEARING_ACCOUNT_INVALID: {
    severity: "service",
    title: "We couldn’t set up this source’s accounts",
    message:
      "The holding or fee account for this payment source isn’t one Books can post through, so the source wasn’t saved.",
    retryable: true,
    nextSteps: [
      "Try saving again — Books creates these accounts for you.",
      "If it keeps failing, contact support with the source’s name.",
    ],
  },
  BOOKS_PROCESSOR_CLEARING_ACCOUNT_RESERVED: {
    severity: "service",
    title: "That holding account is shared with the rest of Books",
    message:
      "This source was pointed at the general clearing account other parts of Books also post into. Each source needs its own holding account, or its payouts could never be reconciled to zero.",
    retryable: true,
    nextSteps: [
      "Try saving again — Books creates a dedicated holding account per source.",
      "If it keeps failing, contact support with the source’s name.",
    ],
  },
  BOOKS_PROCESSOR_ACCOUNT_CROSS_TENANT: {
    severity: "service",
    title: "That account belongs to a different business",
    message:
      "The account named for this payment source isn’t on this business’s chart of accounts, so the source wasn’t saved.",
    retryable: false,
    nextSteps: [
      "Reload the page and set the source up again.",
      "If it keeps happening, contact support — don’t re-enter account details by hand.",
    ],
  },
  BOOKS_PAYMENT_ROUTE_CONFLICT: {
    severity: "user_input",
    title: "Choose a source or a bank account — not both",
    message:
      "A payment source holds this receipt in its clearing account until the payout settles; a bank account deposits it straight away. Naming both asks for two different destinations for the same money.",
    retryable: true,
    nextSteps: [
      "Pick a payment source if the money is still with a processor, or",
      "Pick a bank account if it landed in the bank directly.",
    ],
  },

  // ── BOOKS — MOVING A RECORDED PAYMENT TO A DIFFERENT DEPOSIT ACCOUNT ───────
  //
  // Every refusal here interrupts someone CORRECTING money that is already
  // collected, so none of them says "invalid selection" and none names a code.
  // The customer is never charged or refunded on this path; the copy says so
  // wherever an operator might fear otherwise.
  BOOKS_PAYMENT_DEPOSIT_ACCOUNT_INVALID: {
    severity: "user_input",
    title: "A payment can’t deposit into that account",
    message:
      "A received payment can only land in a bank or credit-card account, or in an account that holds money in transit — like Undeposited Funds or a processor’s clearing account. The account you picked is neither.",
    retryable: true,
    nextSteps: [
      "Pick a bank account, or a clearing account such as Undeposited Funds.",
      "If the account you want should be a clearing account, set its detail type to “Cash in Transit” in the chart of accounts first.",
    ],
  },
  BOOKS_PAYMENT_DEPOSIT_ACCOUNT_CROSS_TENANT: {
    severity: "user_input",
    title: "That account belongs to another business",
    message:
      "The deposit account you chose isn’t on this business’s chart of accounts, so nothing was changed.",
    retryable: false,
    nextSteps: [
      "Reload the page and choose again.",
      "If you switched businesses in another tab, make sure you’re on the right one.",
    ],
  },
  BOOKS_PAYMENT_DEPOSIT_TARGET_REQUIRED: {
    severity: "user_input",
    title: "Choose where this payment should deposit",
    message:
      "Nothing was selected, so there was no account to move the payment to.",
    retryable: true,
    nextSteps: [
      "Pick a payment source if the money is still with a processor, or a bank account if it’s already in the bank.",
    ],
  },
  BOOKS_PAYMENT_DEPOSIT_UNCHANGED: {
    severity: "user_input",
    title: "This payment already deposits there",
    message:
      "The account you picked is the one this payment already uses, so there was nothing to move.",
    retryable: true,
    nextSteps: ["Pick a different account, or close this dialog."],
  },
  BOOKS_PAYMENT_DEPOSIT_LEG_AMBIGUOUS: {
    severity: "service",
    title: "This payment’s journal entry isn’t a shape we can move safely",
    message:
      "Moving a payment means changing exactly one line of its journal entry. This entry doesn’t have exactly one line on the current deposit account, so we stopped rather than guess which line to change.",
    retryable: false,
    nextSteps: [
      "Contact support with this payment’s number — the entry needs looking at directly.",
      "Don’t void the payment to work around this; that removes the receipt instead of moving it.",
    ],
  },
  BOOKS_PAYMENT_NOT_COMPLETED: {
    severity: "user_input",
    title: "Only a completed payment can be moved",
    message:
      "This payment isn’t completed, so it has no posted journal entry to move. A pending payment gets its entry when you confirm it.",
    retryable: false,
    nextSteps: [
      "Confirm the payment first, then move it if it landed in the wrong account.",
    ],
  },
  BOOKS_PAYMENT_NOT_POSTED: {
    severity: "service",
    title: "This payment has no journal entry to move",
    message:
      "The payment record exists but its journal entry doesn’t, so there’s nothing to restate.",
    retryable: false,
    nextSteps: [
      "Contact support with this payment’s number — the record and the ledger disagree.",
    ],
  },
  BOOKS_PAYMENT_MATCHED_TO_BANK_TXN: {
    severity: "user_input",
    title: "This payment is matched to a bank line",
    message:
      "The match records that this money reached that specific account. Moving the payment somewhere else would leave the match describing something that didn’t happen.",
    retryable: false,
    nextSteps: [
      "Open the bank transaction and unmatch it, then move the payment, then match it again.",
      "Unmatching a payment link removes only the link — the payment and its journal entry stay where they are.",
    ],
  },
  BOOKS_PAYMENT_LINE_RECONCILED: {
    severity: "user_input",
    title: "This payment is cleared on a reconciliation",
    message:
      "Its bank line has already been reconciled against a statement. Moving it to another account now would change a period someone has signed off.",
    retryable: false,
    nextSteps: [
      "Undo that reconciliation, move the payment, then reconcile again.",
      "If the statement is already closed, record a correcting journal entry instead.",
    ],
  },

  BOOKS_PAYMENT_HEADER_UNCHANGED: {
    severity: "user_input",
    title: "Nothing changed on this payment",
    message:
      "The date, reference number and memo you submitted are the values already on the receipt.",
    retryable: false,
    nextSteps: [
      "Change at least one field before saving, or close the dialog to leave the receipt as it is.",
    ],
  },

  // ── BOOKS — EDITING A JOURNAL ENTRY ────────────────────────────────────────
  // Every one of these is a refusal an operator will meet while trying to
  // correct something. Without an entry here they surface as the generic
  // fallback, which names no cause and offers no way forward — and the way
  // forward is different for each (edit the document / reopen the period /
  // undo the reconciliation / start a new entry).
  BOOKS_JE_SOURCE_DERIVED_NOT_EDITABLE: {
    severity: "user_input",
    title: "This entry belongs to a document",
    message:
      "It was posted automatically from an invoice, bill, payment or bank transaction. Editing the entry on its own would leave the ledger saying something different from the document it came from, and nothing would flag the disagreement.",
    retryable: false,
    nextSteps: [
      "Open the source document shown at the top of this entry and edit it there — its journal entry is rewritten automatically when you save.",
      "If the document is right and only the ledger is wrong, contact support rather than editing by hand.",
    ],
  },
  BOOKS_JE_ALREADY_VOIDED: {
    severity: "user_input",
    title: "This entry is voided",
    message:
      "A voided entry is kept as history and can’t be changed — its reversal is already part of your balances.",
    retryable: false,
    nextSteps: [
      "Create a new journal entry with the correct figures.",
      "The voided entry stays visible in the register so the trail is complete.",
    ],
  },
  BOOKS_JE_REVERSAL_NOT_EDITABLE: {
    severity: "user_input",
    title: "This entry is a reversal",
    message:
      "It was generated automatically when another entry was voided, and it records what that void did. Editing it would break the pair that cancels out.",
    retryable: false,
    nextSteps: [
      "Open the entry it reverses (linked at the top of this page) to see the original.",
      "Post a new entry if you need to correct the figures.",
    ],
  },
  BOOKS_JE_LINE_RECONCILED: {
    severity: "user_input",
    title: "This entry is cleared on a reconciliation",
    message:
      "One of its lines has already been reconciled against a bank statement. Changing it now would alter a period someone has signed off.",
    retryable: false,
    nextSteps: [
      "Undo that reconciliation, make the correction, then reconcile again.",
      "If the statement is already closed, post a correcting entry in an open period instead.",
    ],
  },
  BOOKS_JE_MATCHED_TO_BANK_TXN: {
    severity: "user_input",
    title: "This entry is matched to a bank line",
    message:
      "The match records that this money moved through that account. Restating the entry would leave the bank line pointing at a voided entry.",
    retryable: false,
    nextSteps: [
      "Open the bank transaction, unmatch it, make the correction, then match it again.",
    ],
  },
  BOOKS_JE_NO_LINES: {
    severity: "user_input",
    title: "A journal entry needs at least two lines",
    message:
      "Every entry records where money came from and where it went, so it can’t be saved with no lines.",
    retryable: false,
    nextSteps: [
      "Add lines totalling the same amount in the Debit and Credit columns.",
    ],
  },
  BOOKS_JE_UNBALANCED: {
    severity: "user_input",
    title: "Debits and credits don’t match",
    message:
      "A posted entry has to balance — the total in the Debit column must equal the total in the Credit column.",
    retryable: false,
    nextSteps: [
      "Check the running totals under the lines and adjust an amount until the difference is zero.",
      "You can save it as a draft while it’s still unbalanced.",
    ],
  },
  BOOKS_JE_LINE_INVALID_AMOUNTS: {
    severity: "user_input",
    title: "A line has an amount that can’t be posted",
    message:
      "Each line carries either a debit or a credit — never both, never neither, and never a negative number.",
    retryable: false,
    nextSteps: [
      "Find the line with values in both columns (or with both blank) and clear one of them.",
      "To record the opposite direction, put the amount in the other column rather than using a minus sign.",
    ],
  },
  BOOKS_JE_NOT_FOUND: {
    severity: "user_input",
    title: "That journal entry no longer exists",
    message:
      "It may have been restated by someone else while this page was open — a restated entry is replaced by a new one.",
    retryable: false,
    nextSteps: [
      "Reload the register and open the entry again.",
    ],
  },

  // ── BOOKS — CARD FUNDING (our own rail's daily deposit) ────────────────────
  BOOKS_CARD_FUNDING_CLEARING_SHORT: {
    severity: "service",
    title: "A card deposit couldn’t be recorded",
    message:
      "The processor’s deposit is larger than the card money Books is currently holding in transit, so recording it would leave that account negative. Usually this means the sales behind the deposit haven’t reached Books yet.",
    retryable: true,
    nextSteps: [
      "Check that the card sales for those days have synced.",
      "If they have, contact support before recording the deposit by hand — the two sides need to be reconciled first.",
    ],
  },
  BOOKS_CARD_FUNDING_DEPOSIT_IS_CLEARING: {
    severity: "service",
    title: "Card deposits have nowhere to land",
    message:
      "Books is set up to deposit card money into the same account it’s already held in, which would cancel itself out and record nothing.",
    retryable: false,
    nextSteps: [
      "Open Books Settings and set a bank account for card deposits.",
    ],
  },

  // ── BOOKS — CHARGE RAILS (card + ACH) ──────────────────────────────────────
  //
  // These lived in a component-local override map (`BOOKS_CHARGE_ERROR_OVERRIDES`
  // in @order-up/client) that exactly ONE caller passed to `translateError`.
  // Every other surface got `GENERIC_FALLBACK` instead — including the app-wide
  // mutation error handler in `AppProviders`, which toasts
  // `getUserFriendlyMessage(error)` = `translateError(error)` with no overrides.
  //
  // On the money path that is not cosmetic. `BOOKS_CARD_CHARGE_UNCONFIRMED`,
  // `BOOKS_CARD_CHARGE_UNRESOLVED` and `BOOKS_CARD_CHARGE_OUTCOME_UNKNOWN` all
  // mean "the card may already be charged — do NOT charge it again", and all
  // three rendered as "Something went wrong. Please try again." in the toast:
  // copy that instructs the operator to do the one thing that double-charges
  // the customer, on top of a dialog telling them the opposite.
  //
  // They belong here. The catalog is the single source of truth, so a code is
  // legible to every surface — present and future — with no plumbing to forget.
  BOOKS_INVOICE_NOT_CHARGEABLE: {
    severity: "user_input",
    title: "This invoice can't be charged",
    message:
      "This invoice isn't in a state that accepts a charge — it may still be a draft, or it may be voided, written off, or already paid in full.",
    retryable: false,
    nextSteps: [
      "Send the invoice first if it is still a draft.",
      "Open the invoice to check its current balance and status.",
    ],
  },
  BOOKS_CARD_RAIL_NOT_APPROVED: {
    severity: "service",
    title: "Card payments are not activated yet",
    message:
      "This business isn't activated to accept card payments, so a card can't be charged.",
    retryable: false,
    nextSteps: [
      // NOT "finish merchant activation". The common case is a business whose
      // application is ALREADY submitted and whose credentials are already
      // configured — pointing them back at onboarding is a dead end.
      // Activation is an OrderUp action, so the next step names OrderUp.
      "Contact OrderUp support to activate card payments for this location.",
      "Record the payment manually if it was taken outside OrderUp.",
    ],
  },
  BOOKS_ACH_RAIL_NOT_APPROVED: {
    severity: "service",
    title: "Bank payments are not activated yet",
    message:
      "This business isn't activated to accept bank-account (ACH) payments, so they can't be switched on.",
    retryable: false,
    nextSteps: [
      "Contact OrderUp support to activate bank payments for this location.",
    ],
  },
  BOOKS_CARD_PROVIDER_MISMATCH: {
    severity: "user_input",
    title: "Card entry is out of date",
    message:
      "The card was entered for a different payment gateway than this business uses, so it can't be charged.",
    retryable: true,
    nextSteps: ["Reload the payment form and enter the card again."],
  },
  BOOKS_CARD_PROVIDER_NOT_CONFIGURED: {
    severity: "service",
    title: "No card gateway configured",
    message:
      "This business has no card payment gateway configured, so a card can't be charged.",
    retryable: false,
    nextSteps: [
      "Ask OrderUp support to finish the payment-gateway setup for this business.",
    ],
  },
  BOOKS_CARD_DECLINED: {
    severity: "user_input",
    title: "Card declined",
    message:
      "The card issuer declined the charge. Nothing was captured and the invoice is unchanged.",
    retryable: true,
    nextSteps: [
      "Ask the customer for a different card.",
      "Check the card number, expiry, and billing ZIP before retrying.",
    ],
  },
  BOOKS_CARD_CHARGE_IN_FLIGHT: {
    severity: "service",
    title: "A charge is already running",
    message:
      "A charge for this invoice is already being processed. Nothing was charged twice.",
    retryable: true,
    nextSteps: [
      "Wait a moment, then reopen the invoice to see whether the payment posted.",
    ],
  },
  BOOKS_CARD_CHARGE_FAILED: {
    severity: "service",
    title: "The gateway rejected the charge",
    message:
      "The payment gateway refused the request outright, so no charge was completed.",
    retryable: true,
    nextSteps: [
      "Try the charge again in a moment.",
      "If it keeps failing, contact OrderUp support — the gateway configuration may need attention.",
    ],
  },
  BOOKS_CARD_CHARGE_UNRESOLVED: {
    severity: "service",
    title: "Couldn't confirm this charge",
    message:
      "The payment gateway didn't answer in time, so we can't say whether the card was charged.",
    retryable: false,
    nextSteps: [
      "Do NOT charge the card again.",
      "Reopen the invoice in a few minutes — if the charge went through, the payment will appear on it.",
      "If nothing appears after 20 minutes, contact OrderUp support before retrying.",
    ],
  },
  BOOKS_CARD_CURRENCY_UNSUPPORTED: {
    severity: "user_input",
    title: "Currency not supported for card payments",
    message:
      "This invoice's currency isn't one the payment gateway can charge a card in.",
    retryable: false,
    nextSteps: [
      "Collect this payment outside OrderUp and record it on the invoice manually.",
    ],
  },
  // The card WAS captured and the capture is durably recorded server-side, so
  // this copy must not read as "the charge failed" — and it must not promise
  // that pressing Charge again is what fixes it. The charge intent replays the
  // stored approval (never a second capture) and the hourly sweep posts the
  // receipt without the operator, so the honest instruction is "wait and
  // confirm", not "retry".
  BOOKS_CARD_CHARGE_UNCONFIRMED: {
    severity: "service",
    title: "Charged — posting to the invoice is pending",
    message:
      "The card was charged and the capture is recorded, but it hasn't posted to the invoice yet. It will be applied automatically.",
    retryable: false,
    nextSteps: [
      "Do NOT charge another card — this charge was captured and will be applied.",
      "Reopen the invoice in a few minutes to confirm the payment appears.",
      "If it has not appeared within an hour, contact OrderUp support with this invoice number.",
    ],
  },
  // Raised CLIENT-SIDE when the charge request comes back in a shape the page
  // cannot read: the request went out, the answer did not come back legibly,
  // and therefore the outcome is genuinely unknown. "Failed" is the one thing
  // it must never say — that is what puts a second charge on a card that
  // already paid. Name the uncertainty and the safe order of operations.
  BOOKS_CARD_CHARGE_OUTCOME_UNKNOWN: {
    severity: "service",
    title: "We can't confirm whether this charge went through",
    message:
      "The card may have been charged — the payment page could not read the answer. Do NOT charge it again until you have checked.",
    retryable: false,
    nextSteps: [
      "Close this dialog and reload the invoice. If it shows as paid, the charge went through and there is nothing more to do.",
      "If it is still unpaid after a minute, check the processor's batch for a matching amount before charging again.",
      "Report this — a charge whose result cannot be read is a defect, not normal behaviour.",
    ],
  },
  // A 2xx whose body is NOT our JSON envelope (an HTML interstitial, an empty
  // body, JSON that never parsed). Thrown by the Books API client. It had a
  // code but no copy anywhere, so it rendered as the generic fallback — and on
  // the charge path a generic "try again" is the dangerous answer, because the
  // request may well have moved money before the reply was mangled.
  BOOKS_RESPONSE_MALFORMED: {
    severity: "service",
    title: "We couldn't read the answer from the server",
    message:
      "The request was sent but the reply came back unreadable, so we can't tell you whether it took effect.",
    retryable: false,
    nextSteps: [
      "Reload the page and check whether the change is already there before trying again.",
      "If this involved a payment, do NOT retry until you have confirmed the invoice — the money may already have moved.",
      "Report this — an unreadable reply is a defect, not normal behaviour.",
    ],
  },

  // ── BOOKS — CARD ON FILE ────────────────────────────────────────────────────
  // Two moods, deliberately split: BEFORE the charge (`ATTESTATION_STALE`,
  // `SAVED_CARD_NOT_FOUND`) nothing has moved, so the copy is "fix this and try
  // again". AFTER an approved charge (`SAVE_DECLINED`, `SAVE_NO_TOKEN`,
  // `CONSENT_NOT_RECORDED`, `SAVE_FAILED`) the money DID move, so the copy must
  // never read as a failed payment and must never invite a retry of the charge.
  BOOKS_CARD_ATTESTATION_STALE: {
    severity: "user_input",
    title: "The card-on-file authorization has changed",
    message:
      "The authorization wording the customer needs to hear has been updated, so the card was not saved and nothing was charged.",
    retryable: true,
    nextSteps: [
      "Reload the payment form to get the current wording.",
      "Read it to the customer, then charge and save the card again.",
    ],
  },
  BOOKS_SAVED_CARD_NOT_FOUND: {
    severity: "user_input",
    title: "That saved card is gone",
    message:
      "The card on file you picked is no longer stored for this customer, so nothing was charged.",
    retryable: true,
    nextSteps: [
      "Reload the invoice to see the cards currently on file.",
      "Key the customer's card instead if none is stored.",
    ],
  },
  // NOT necessarily the issuer. This code is raised whenever the vault does not
  // answer `Approved`, and that includes the gateway rejecting a request WE
  // built badly — which is what it always was: the StorageSafe `Description`
  // overflowed TRX's 50-char limit, so every save was refused before any bank
  // saw it. Saying "the card issuer refused" sent operators to ask the customer
  // for a different card to fix our own bug.
  BOOKS_CARD_SAVE_DECLINED: {
    severity: "user_input",
    title: "Charged — but the card was not saved",
    message:
      "The payment went through. Storing the card for future use was refused, so nothing was kept on file.",
    retryable: false,
    nextSteps: [
      "The invoice payment is complete — do NOT charge again.",
      "The reference below carries the processor’s own reason. Quote it to support if saving keeps failing for every card.",
      "If it is specific to this card, ask the customer for a different one next time you need one on file.",
    ],
  },
  BOOKS_CARD_SAVE_NO_TOKEN: {
    severity: "service",
    title: "Charged — but the card was not saved",
    message:
      "The payment went through. The payment vault did not return a usable token, so the card was not kept on file.",
    retryable: false,
    nextSteps: [
      "The invoice payment is complete — do NOT charge again.",
      "Try saving the card again on the next charge.",
    ],
  },
  BOOKS_CARD_CONSENT_NOT_RECORDED: {
    severity: "service",
    title: "Charged — but the card was not saved",
    message:
      "The payment went through. The customer's authorization to keep the card on file could not be recorded, so the card was deliberately not kept.",
    retryable: false,
    nextSteps: [
      "The invoice payment is complete — do NOT charge again.",
      "Try saving the card again on the next charge; contact OrderUp support if it keeps failing.",
    ],
  },
  BOOKS_CARD_SAVE_FAILED: {
    severity: "service",
    title: "Charged — but the card was not saved",
    message:
      "The payment went through. Storing the card for future use did not complete, so it was not kept on file.",
    retryable: false,
    nextSteps: [
      "The invoice payment is complete — do NOT charge again.",
      "Try saving the card again on the next charge.",
    ],
  },
  BOOKS_CARD_FORGET_FAILED: {
    severity: "service",
    title: "Couldn't remove the saved card",
    message:
      "The payment vault refused to delete the stored card, so it is still on file and could still be charged.",
    retryable: true,
    nextSteps: [
      "Try removing it again in a moment.",
      "If it keeps failing, contact OrderUp support — the card must not stay chargeable after the customer withdraws consent.",
    ],
  },

  // ── BOOKS — CHARGE DIALOG PRE-FLIGHT ────────────────────────────────────────
  // Raised by the payment dialog itself, before anything is sent. Catalogued
  // rather than kept component-local for the same reason as the rest of this
  // block: a code whose copy lives in one component is illegible to every other
  // surface that ends up rendering it.
  // ── TRX HOSTED FIELDS — TOKENIZE REFUSED ───────────────────────────────────
  // The gateway's own iframes refused to tokenize what was typed. This is a
  // VALIDATION REFUSAL, so both entries set `preferServerMessage`: the sentence
  // built by `describeTrxCardError` / `describeTrxBankError` names WHICH field
  // TRX rejected, and the catalog can only ever say something generic.
  //
  // Why they are catalogued at all: a tokenize is not a sale, so a refusal
  // reaches NO gateway portal, NO server log and NO client log — the thrown
  // message is the only carrier of the reason. Before these codes existed the
  // errors had no `code`, fell to `GENERIC_FALLBACK`, and had their message
  // discarded: True Wealth invoice 1042 (2026-09-03) showed an operator
  // "Something went wrong. Please try again." for a mistyped card number.
  CARD_FIELDS_REJECTED: {
    severity: "user_input",
    title: "Check the card details",
    message:
      "The card details could not be read. Please check them and try again. Nothing was charged.",
    retryable: true,
    preferServerMessage: true,
    nextSteps: [
      "Re-enter the card details and try again.",
      "If they are definitely correct, ask the cardholder for another card.",
    ],
  },
  BANK_FIELDS_REJECTED: {
    severity: "user_input",
    title: "Check the bank details",
    message:
      "The bank-account details could not be read. Please check them and try again. Nothing was debited.",
    retryable: true,
    preferServerMessage: true,
    nextSteps: [
      "Re-enter the routing and account numbers and try again.",
      "Confirm the numbers against a cheque or a bank statement.",
    ],
  },

  CARD_FIELDS_NOT_READY: {
    severity: "service",
    title: "The payment form is still loading",
    message:
      "The secure payment fields have not finished loading, so the details could not be read. Nothing was charged.",
    retryable: true,
    preferServerMessage: true,
    nextSteps: [
      "Wait a moment for the fields to appear, then try again.",
      "Close and reopen this dialog if they do not appear.",
    ],
  },
  CARD_FIELDS_INCOMPLETE: {
    severity: "user_input",
    title: "Enter the card details",
    message:
      "The card details are incomplete, so nothing could be charged. Please fill them in and try again.",
    retryable: true,
    preferServerMessage: true,
    nextSteps: ["Complete every card field, then try again."],
  },

  BOOKS_CARD_FIELDS_NOT_READY: {
    severity: "service",
    title: "The card form is still loading",
    message:
      "The secure card fields have not finished loading, so the card could not be read. Nothing was charged.",
    retryable: true,
    nextSteps: [
      "Wait a moment for the card fields to appear, then try again.",
      "Close and reopen this dialog if they do not appear.",
    ],
  },
  BOOKS_CARD_MERCHANT_UNRESOLVED: {
    severity: "service",
    title: "We can't confirm who would be paid",
    message:
      "The business that would receive this payment could not be loaded, so a card cannot be charged. Nothing was charged.",
    retryable: true,
    nextSteps: [
      "Reload the page and open this dialog again.",
      "Record the payment instead if it was taken elsewhere.",
    ],
  },
  // Not a user mistake and not retryable by trying harder: the screen is
  // showing one business's merchant account beside another business's document,
  // and charging would settle the money into the wrong books.
  BOOKS_CARD_MERCHANT_SCOPE_MISMATCH: {
    severity: "service",
    title: "This payment would go to the wrong business",
    message:
      "The merchant account on screen does not belong to the business that issued this document, so a card cannot be charged. Nothing was charged.",
    retryable: false,
    nextSteps: [
      "Reload the page.",
      "If it happens again, report it — this should not be possible.",
    ],
  },
  BOOKS_ACH_DEBIT_AMOUNT_INVALID: {
    severity: "user_input",
    title: "Nothing to debit",
    message:
      "The bank debit amount for this invoice isn't a positive amount, so no debit was queued.",
    retryable: false,
    nextSteps: ["Open the invoice to check its remaining balance due."],
  },

  // ── PUBLIC PAY LINK (/pay/:token) ──────────────────────────────────────────
  //
  // THE AUDIENCE HERE IS THE CUSTOMER, NOT AN OPERATOR. This is the page someone
  // lands on from an emailed invoice to pay with their own card.
  //
  // `apps/api/components/routes/public/pay.ts` emits 21 typed codes; 12 had no
  // entry here, and the page (apps/management/src/app/pay/[token]/page.tsx:260)
  // calls `translateError(err)` with no overrides. So a DECLINED CARD rendered to
  // the payer as "Something went wrong. Please try again." with retryable: true —
  // which is how a customer re-submits the same declined card against their
  // issuer's velocity rules, never being told to use a different one. And
  // `PAYMENT_IN_PROGRESS` — a charge already in flight for this very link — said
  // the same thing, inviting a second payment for one invoice.
  //
  // The server already writes good copy for most of these (pay.ts sends "Your
  // card was declined. Please try another card."). It never arrived, because the
  // generic fallback deliberately discards `error.message` to avoid echoing raw
  // machine strings. These entries are where that copy now lives.
  //
  // Names are unprefixed because the route emits them unprefixed; verified no
  // conflicting producer elsewhere (MERCHANT_NOT_CONFIGURED is also raised by
  // public/booking.ts, where this wording still reads correctly).
  CARD_DECLINED: {
    severity: "user_input",
    title: "Card declined",
    message:
      "Your card was declined by the bank that issued it. Nothing was charged.",
    retryable: false,
    nextSteps: [
      "Try a different card.",
      "Check the number, expiry date and billing ZIP if you think this one should work.",
      // This used to read "we are not told the reason", which was false — we
      // hold the issuer's decline code on every one of these responses and
      // simply had nowhere to render it. The route now resolves cardholder
      // copy from the shared decline catalog and sends it as `decline`, which
      // `translateError` prefers over this entry. This wording is the fallback
      // for the genuinely unknown case, and it no longer claims ignorance we
      // do not have.
      "Your bank can tell you more — they see the full reason, and can authorise the card for you.",
    ],
  },
  // Deliberately NOT "try another card": this is the gateway refusing the
  // request, so a second card fails identically and only adds declines to the
  // payer's record.
  CARD_REJECTED: {
    severity: "service",
    title: "This payment could not be processed",
    message:
      "The payment could not be processed. Nothing was charged, and trying another card will not help.",
    retryable: false,
    nextSteps: [
      "Contact the business that sent you this invoice.",
      "They can take the payment another way or send a new link.",
    ],
  },
  CARD_PROVIDER_MISMATCH: {
    severity: "user_input",
    title: "This page is out of date",
    message:
      "Your card details were prepared for a different payment provider than this business now uses, so they could not be submitted. Nothing was charged.",
    retryable: true,
    nextSteps: ["Reload this page and enter your card again."],
  },
  // The customer has ALREADY submitted and a charge is in flight. Telling them
  // to try again is how one invoice gets paid twice.
  PAYMENT_IN_PROGRESS: {
    severity: "service",
    title: "This payment is already going through",
    message:
      "A payment for this link is already being processed. Do not submit it again — you could be charged twice.",
    retryable: false,
    nextSteps: [
      "Wait about a minute, then reload this page to see the receipt.",
      "Contact the business if no receipt appears after a few minutes.",
    ],
  },
  AMOUNT_EXCEEDS_DUE: {
    severity: "user_input",
    title: "That is more than the amount due",
    message:
      "The amount entered is larger than the balance remaining on this invoice, so it was not charged.",
    retryable: true,
    nextSteps: [
      "Enter an amount up to the balance shown on this page.",
      "Reload the page if you think the balance is out of date.",
    ],
  },
  CURRENCY_UNSUPPORTED: {
    severity: "service",
    title: "This currency can't be charged",
    message:
      "This invoice is in a currency the payment provider cannot charge a card in. Nothing was charged.",
    retryable: false,
    nextSteps: [
      "Contact the business that sent this invoice to arrange another way to pay.",
    ],
  },
  // Over-tolerance card tip review (POST /payments/over-tip-tolerance/review).
  // Neither refusal moved money — the tip was captured before anyone reviewed it.
  OVER_TIP_PAYMENT_NOT_FOUND: {
    severity: "user_input",
    title: "Payment not found",
    message:
      "We couldn't find this payment at this location, so it wasn't marked reviewed.",
    retryable: false,
    nextSteps: ["Refresh the report — the payment may belong to another location."],
  },
  OVER_TIP_INDEX_BUILDING: {
    severity: "service",
    title: "This report is still being set up",
    message:
      "The over-tolerance tip report isn't ready at this location yet. It usually is within a few minutes.",
    retryable: true,
    nextSteps: ["Try again in a few minutes."],
  },
  OVER_TIP_NOT_FLAGGED: {
    severity: "user_input",
    title: "Nothing to review",
    message:
      "This payment isn't flagged as an over-tolerance tip, so there is nothing to mark reviewed.",
    retryable: false,
    nextSteps: ["Refresh the report to see the current list."],
  },
  PAYMENT_LINK_NOT_FOUND: {
    severity: "user_input",
    title: "This payment link doesn't exist",
    message:
      "This payment link could not be found. It may have been cancelled, or the address may be incomplete.",
    retryable: false,
    nextSteps: [
      "Check that you opened the full link from your email — long links are sometimes cut in half.",
      "Ask the business to send you a new payment link.",
    ],
  },
  PAYMENT_LINK_NOT_PAYABLE: {
    severity: "user_input",
    title: "This link can no longer be paid",
    message:
      "This payment link is no longer accepting payment — it may already be paid, expired, or cancelled.",
    retryable: false,
    nextSteps: [
      "Reload this page to see the current status of the invoice.",
      "Contact the business if you believe you still owe this amount.",
    ],
  },
  // Books "Pay by check": a link that asks the payer to approve their own
  // check offers that check's account and amount only.
  PAYMENT_LINK_ACCOUNT_LOCKED: {
    severity: "user_input",
    title: "This payment is for your check",
    message:
      "This payment comes from the bank account on your check, for the amount of the check. Neither can be changed here, so nothing was set up.",
    retryable: false,
    nextSteps: [
      "Reload this page and approve the payment as shown.",
      "Contact the business if the account or the amount is wrong.",
    ],
  },
  // order-up-supabase 20261019022749: the ledger refused an approval through
  // a check link that did not name the check (or named one the link does not
  // carry). The route always sends the right one, so this is a fault of ours.
  BILLING_ACH_CHECK_LINK_MISMATCH: {
    severity: "service",
    title: "Approval not recorded",
    message:
      "This approval couldn’t be matched to the check it is for, so nothing was set up and nothing will be debited.",
    retryable: false,
    nextSteps: ["Contact the business so they can send you a new link."],
  },
  MERCHANT_NOT_CONFIGURED: {
    severity: "service",
    title: "This business can't take card payments yet",
    message:
      "This business is not set up to accept online payments, so nothing could be charged.",
    retryable: false,
    nextSteps: [
      "Contact the business directly to arrange payment.",
      "Nothing was charged and you do not need to do anything else here.",
    ],
  },
  DEPOSIT_ACCOUNT_NOT_CONFIGURED: {
    severity: "service",
    title: "This business can't take card payments yet",
    message:
      "This business has not finished setting up its accounting, so a payment cannot be recorded. Nothing was charged.",
    retryable: false,
    nextSteps: ["Contact the business directly to arrange payment."],
  },
  CARD_SESSION_NOT_REQUIRED: {
    severity: "service",
    title: "This page is out of date",
    message:
      "This payment page tried to start a card session this business does not use. Nothing was charged.",
    retryable: true,
    nextSteps: [
      "Reload this page and try again.",
      "Contact the business if it keeps happening.",
    ],
  },
  PUBLIC_PAY_ERROR: {
    severity: "service",
    title: "Something went wrong on our end",
    message: "The payment service could not be reached, so nothing was charged.",
    retryable: true,
    nextSteps: [
      "Wait a moment and try again.",
      "Reload the page first to confirm the invoice is still unpaid before retrying.",
    ],
  },

  // ── BOOKS — BANKING (bank feed, matching, rules) ──────────────────────────
  // Raised by the banking RPCs (match_bank_transaction, unmatch_bank_transaction,
  // match_bank_transaction_to_document, and the rules engine). Every one of these
  // is a user-actionable refusal — the match cannot proceed as-is, and the next
  // step tells the operator what to do instead.
  BANKING_TXN_NOT_FOUND: {
    severity: "user_input",
    title: "Bank line not found",
    message:
      "We couldn't find that bank transaction — it may have been removed or refreshed from the feed.",
    retryable: false,
    nextSteps: ["Refresh the bank feed and try again."],
  },
  BANKING_TXN_DELETED: {
    severity: "user_input",
    title: "Bank line was removed",
    message:
      "This bank transaction was removed from the feed and can no longer be matched or categorized.",
    retryable: false,
    nextSteps: [
      "Refresh the bank feed to see the current list of transactions.",
    ],
  },
  BANKING_TXN_NOT_MATCHABLE: {
    severity: "user_input",
    title: "This line can't be matched",
    message:
      "This bank line has already been categorized, excluded, or reconciled — only an unactioned or previously matched line can be matched.",
    retryable: false,
    nextSteps: ["Undo the existing action on this line first, then match it."],
  },
  BANKING_TXN_NOT_MATCHED: {
    severity: "user_input",
    title: "This line isn't matched",
    message:
      "There's no existing match to undo on this bank line.",
    retryable: false,
  },
  BANKING_MATCH_PAYMENT_NOT_POSTED: {
    severity: "user_input",
    title: "Payment hasn't posted yet",
    message:
      "This payment is still pending verification, so it has no journal entry to link — matching requires a fully posted payment.",
    retryable: false,
    nextSteps: [
      "Wait for the payment to be verified and posted, then try matching again.",
    ],
  },
  BANKING_MATCH_DIRECTION_MISMATCH: {
    severity: "user_input",
    title: "Direction doesn't match",
    message:
      "A bank deposit can only settle money received; a withdrawal can only settle money paid out — this payment goes the other way.",
    retryable: false,
    nextSteps: [
      "Pick a payment that flows in the same direction as this bank line.",
    ],
  },
  BANKING_MATCH_ACCOUNT_MISMATCH: {
    severity: "user_input",
    title: "Different bank account",
    message:
      "The payment you picked was posted to a different bank account than this bank line.",
    retryable: false,
    nextSteps: [
      "Pick a payment posted to the same account as this bank line, or categorize the line instead.",
    ],
  },
  BANKING_MATCH_AMOUNT_MISMATCH: {
    severity: "user_input",
    title: "Amounts don't match",
    message:
      "This bank line and the payment you picked are for different amounts, so linking them would leave your books out of step.",
    retryable: false,
    nextSteps: [
      "Pick a payment for the same amount, or categorize this line instead.",
    ],
  },
  BANKING_MATCH_PAYMENT_ALREADY_MATCHED: {
    severity: "user_input",
    title: "Payment is already matched",
    message:
      "Each payment can only be linked to one bank line, and this one is already matched.",
    retryable: false,
    nextSteps: [
      "Pick a different payment, or find and unmatch the existing link first.",
    ],
  },
  BANKING_RULES_MISSING_TENANT: {
    severity: "service",
    title: "Banking rules aren't set up yet",
    message:
      "The banking rules engine couldn't find settings for this location, so the rules can't run.",
    retryable: false,
    nextSteps: ["Contact support if this keeps happening."],
  },
  // New code from migration 20260801000237_bank_match_open_documents — raised
  // by match_bank_transaction_to_document when the bill or invoice isn't in a
  // state that can accept a settlement payment (draft, void, paid, or a
  // different location's document).
  BANKING_MATCH_DOCUMENT_NOT_OPEN: {
    severity: "user_input",
    title: "That bill or invoice isn't open",
    message:
      "It's a draft, it's already paid, it was voided, or it belongs to another location — so there's no balance for this bank line to settle.",
    retryable: false,
    nextSteps: [
      "Refresh and pick another document, or categorize this line instead.",
    ],
  },
  // New code from migration 20260801000237_bank_match_open_documents — raised
  // when the `kind` field is not 'bill' or 'invoice'. Never a user mistake
  // (the UI renders a fixed list); this indicates a wiring defect.
  BANKING_MATCH_UNKNOWN_KIND: {
    severity: "service",
    title: "Unrecognized document type",
    message:
      "The document type sent with this match request isn't one we recognize. This is on us — please contact support.",
    retryable: false,
  },
  // ── Bank connection / vault / token ──────────────────────────────────────────
  // Raised by vault_create_secret_safe when the Plaid access token it was
  // asked to store is null or empty — the bank returned no credential.
  BANKING_VAULT_EMPTY_TOKEN: {
    severity: "service",
    title: "Bank returned no credential",
    message:
      "The bank's connection returned an empty access token, so the connection couldn't be saved. This is on the bank's side, not yours.",
    retryable: true,
    nextSteps: [
      "Wait a moment and try connecting again from Banking → Connections.",
      "If it keeps failing, contact support with the support reference below.",
    ],
  },
  // Raised by vault_create_secret_safe (on store) and vault_decrypt_secret
  // (on read) when neither Supabase Vault nor the pgcrypto dev-key fallback
  // is configured or reachable — a server infrastructure fault.
  BANKING_VAULT_UNAVAILABLE: {
    severity: "service",
    title: "Credential storage is temporarily unavailable",
    message:
      "We couldn't reach the secure storage needed to save or read your bank connection's credentials. This is a problem on our end — nothing was changed.",
    retryable: true,
    nextSteps: [
      "Try again in a moment.",
      "If it keeps happening, contact support with the support reference below.",
    ],
  },
  // Raised by vault_decrypt_secret when both token storage slots on the
  // bank_connections row are NULL (nothing was ever stored, or the row was
  // cleared) — the connection exists but its credential is unreadable.
  BANKING_TOKEN_NOT_FOUND: {
    severity: "service",
    title: "Bank credential no longer readable",
    message:
      "The stored credential for this bank connection can no longer be read — the link needs to be re-established.",
    retryable: false,
    nextSteps: [
      "Reconnect this bank account from Banking → Connections to restore the credential.",
    ],
  },
  // Raised by decrypt_plaid_token and revert_bank_connection when a bank
  // connection ID cannot be found (deleted, wrong ID, or wrong restaurant).
  BANKING_CONNECTION_NOT_FOUND: {
    severity: "user_input",
    title: "Bank connection not found",
    message:
      "We couldn't find that bank connection — it may have been removed or is no longer linked to this location.",
    retryable: false,
    nextSteps: [
      "Refresh the page to see the current list of connections.",
    ],
  },
  // ── Categorize / exclude / unexclude guards ──────────────────────────────────
  // Raised by categorize_bank_transaction when the line's status is
  // 'matched', 'excluded', or 'reconciled'. Re-categorization of an already-
  // categorized line is allowed; matched/excluded/reconciled are not.
  BANKING_TXN_NOT_CATEGORIZABLE: {
    severity: "user_input",
    title: "This line can't be categorized",
    message:
      "This bank line is already matched, excluded, or reconciled — only an unactioned or previously categorized line can be given a category.",
    retryable: false,
    nextSteps: [
      "Undo the existing match or exclusion on this line first, then categorize it.",
    ],
  },
  // Raised by categorize_bank_transaction when ABS(amount) = 0. A zero-amount
  // line produces no journal entry and cannot be categorized.
  BANKING_TXN_ZERO_AMOUNT: {
    severity: "user_input",
    title: "This line has a zero amount",
    message:
      "A bank line with a zero amount can't be categorized — there's nothing to post to your books.",
    retryable: false,
    nextSteps: ["Exclude this line instead if it doesn't represent a real transaction."],
  },
  // Raised by exclude_bank_transaction when the line's status is 'matched' or
  // 'reconciled'. Categorized lines can be excluded; matched/reconciled can't.
  BANKING_TXN_NOT_EXCLUDABLE: {
    severity: "user_input",
    title: "This line can't be excluded",
    message:
      "This bank line is already matched or reconciled — only an unactioned or categorized line can be excluded.",
    retryable: false,
    nextSteps: [
      "Undo the existing match on this line first, then exclude it.",
    ],
  },
  // Raised by unexclude_bank_transaction when the line's status is not
  // 'excluded' — there is nothing to undo.
  BANKING_TXN_NOT_EXCLUDED: {
    severity: "user_input",
    title: "This line isn't excluded",
    message:
      "There's no exclusion to undo on this bank line.",
    retryable: false,
  },
  // ── Split rules (bank_rule_splits) ──────────────────────────────────────────
  // Raised by validate_bank_rule_splits when a rule that excludes from books is
  // also given split legs. An exclusion posts nothing, so there is nothing to
  // divide.
  BANKING_RULE_SPLIT_ON_EXCLUDE: {
    severity: "user_input",
    title: "A rule can't split and exclude",
    message:
      "This rule excludes matching transactions from your books, so there's nothing for it to split across categories.",
    retryable: false,
    nextSteps: [
      "Remove the split lines, or switch the rule from “Exclude from books” to “Categorize as…”.",
    ],
  },
  // Raised by validate_bank_rule_splits when one rule mixes percentage legs with
  // fixed-amount legs — the remainder would be ambiguous.
  BANKING_RULE_SPLIT_MIXED_BASIS: {
    severity: "user_input",
    title: "Use percentages or amounts, not both",
    message:
      "Every split line on a rule has to be measured the same way — all percentages, or all fixed amounts.",
    retryable: false,
    nextSteps: ["Pick one basis for this rule and re-enter the split lines."],
  },
  // Raised by validate_bank_rule_splits when the percentage legs add to more
  // than 100% of the transaction.
  BANKING_RULE_SPLIT_PERCENT_OVER_100: {
    severity: "user_input",
    title: "The splits add up to more than 100%",
    message:
      "A rule can't allocate more than the whole transaction. Reduce the percentages so they total 100% or less.",
    retryable: false,
    nextSteps: [
      "Anything under 100% is fine — the rest goes to the rule's main category.",
    ],
  },
  // Raised by resolve_bank_rule_splits when a rule's fixed-amount legs total
  // more than the bank line it matched.
  BANKING_RULE_SPLIT_EXCEEDS_AMOUNT: {
    severity: "user_input",
    title: "The split amounts are bigger than the transaction",
    message:
      "This rule's fixed split amounts add up to more than this bank transaction, so it can't be divided as written.",
    retryable: false,
    nextSteps: [
      "Lower the fixed amounts on the rule, or use percentages so it scales to whatever the transaction is.",
    ],
  },
  // ── Categorization legs (categorize_bank_transaction_splits) ────────────────
  // Raised when a categorization arrives with no category account at all.
  BANKING_CATEGORIZE_NO_LEGS: {
    severity: "user_input",
    title: "Pick a category first",
    message:
      "This bank line can't be categorized without at least one category account to post it to.",
    retryable: false,
    nextSteps: ["Choose a category, then try again."],
  },
  // Raised when a split leg carries a zero or negative amount.
  BANKING_CATEGORIZE_LEG_NOT_POSITIVE: {
    severity: "user_input",
    title: "Every split line needs a positive amount",
    message:
      "One of the split lines has a zero or negative amount, which can't be posted to your books.",
    retryable: false,
    nextSteps: ["Give every split line an amount greater than zero, or remove it."],
  },
  // Raised when the split legs do not total the bank line. Fails closed rather
  // than posting an unbalanced or partial entry.
  BANKING_CATEGORIZE_LEGS_DO_NOT_TOTAL: {
    severity: "user_input",
    title: "The splits don't add up to the transaction",
    message:
      "The split lines have to account for the full amount of the bank transaction — no more and no less.",
    retryable: false,
    nextSteps: [
      "Adjust the split lines so they total the transaction amount exactly.",
    ],
  },
  // Raised by revert_bank_rule when the rule ID is not found — deleted in
  // another tab or an incorrect reference.
  BANKING_RULE_NOT_FOUND: {
    severity: "user_input",
    title: "Banking rule not found",
    message:
      "We couldn't find that banking rule — it may have been deleted.",
    retryable: false,
    nextSteps: ["Refresh the rules list to see the current rules."],
  },

  // ── BOOKS — FORM STYLES (book_form_styles RPCs) ─────────────────────────────
  BOOKS_FORM_STYLE_NOT_FOUND: {
    severity: "user_input",
    title: "Form style not found",
    message:
      "That form style no longer exists — it may have been deleted in another tab. Go back to Form styles to see the current list.",
    retryable: false,
  },
  BOOKS_FORM_STYLE_INACTIVE: {
    severity: "user_input",
    title: "This form style is archived",
    message:
      "Archived form styles can’t be edited, duplicated, or made the default. Restore it first.",
    retryable: false,
  },
  BOOKS_FORM_STYLE_CANNOT_DELETE_DEFAULT: {
    severity: "user_input",
    title: "This is the default style",
    message:
      "Every form type needs one default to draw with. Make another style the default first, then archive this one.",
    retryable: false,
  },
  BOOKS_FORM_STYLE_NAME_CONFLICT: {
    severity: "user_input",
    title: "That name is taken",
    message:
      "Another form style of this type already uses that name. Pick a different one.",
    retryable: false,
  },
  BOOKS_FORM_STYLE_CONFIG_TOO_LARGE: {
    severity: "user_input",
    title: "This style is too large to save",
    message:
      "The design, content, or email settings for this style exceed the size limit. Shorten your custom labels or footer text and try again.",
    retryable: false,
  },

  // ── BOOKS — STATEMENT PDF PARSING ───────────────────────────────────────────
  STATEMENT_PARSING_NOT_CONFIGURED: {
    severity: "service",
    title: "Statement import unavailable",
    message:
      "PDF statement import isn’t enabled on the server yet. This is on us — please contact support.",
    retryable: false,
  },
  STATEMENT_PARSING_UNSUPPORTED_MIME: {
    severity: "user_input",
    title: "Unsupported file",
    message:
      "That file type isn’t supported. Upload a PDF or an image of your statement.",
    retryable: false,
  },
  STATEMENT_PARSING_FAILED: {
    severity: "user_input",
    title: "Couldn’t read that statement",
    message:
      "We couldn’t read transactions from this statement. It may be a scanned image, password-protected, or an unusual layout. Try a clearer PDF, or import a CSV instead.",
    retryable: true,
  },
  STATEMENT_PARSING_TIMEOUT: {
    severity: "service",
    title: "Statement took too long",
    message:
      "Parsing this statement took too long. Try a smaller date range or split the PDF into fewer pages.",
    retryable: true,
  },
  BOOKS_STATEMENT_PDF_IMPORT_DISABLED: {
    severity: "service",
    title: "Not enabled yet",
    message: "PDF statement import isn’t turned on for this restaurant yet.",
    retryable: false,
  },
  // Raised by `requireBooksAccess` when a Books request reaches the API without
  // a resolved tenant/restaurant — on the import screen this means no location
  // is loaded (or the page loaded before selection settled).
  BOOKS_MISSING_TENANT_GUID: {
    severity: "user_input",
    title: "Select a location first",
    message:
      "No restaurant location is loaded yet. Pick a location from the top bar (or reload the page), then try again.",
    retryable: true,
  },

  // ── MENU IMPORT — AI PHOTO/PDF EXTRACTION ───────────────────────────────────
  // Raised by the AI menu-import route (apps/api …/routes/menuImport.ts) and
  // surfaced in the management import wizard (Step1FileUpload). Multi-page PDFs
  // are read page-by-page and merged, so this only fires when every page failed.
  MENU_AI_EXTRACTION_FAILED: {
    severity: "service",
    title: "Couldn’t read that menu",
    message:
      "We couldn’t read this menu automatically. It may be very dense, low-contrast, or an unusual layout.",
    retryable: true,
    nextSteps: [
      "Try again — a second pass often succeeds.",
      "If it keeps failing, upload each page separately or as clearer, higher-contrast photos.",
      "Or import a CSV/Excel file instead.",
    ],
  },
  MENU_AI_COLUMN_MAPPING_FAILED: {
    severity: "service",
    title: "Couldn’t auto-map those columns",
    message:
      "We couldn’t automatically match your spreadsheet columns. Map them by hand and continue.",
    retryable: true,
  },
  // Shared with Books AI — the server has no AI provider key configured.
  AI_NOT_CONFIGURED: {
    severity: "service",
    title: "AI import isn’t enabled",
    message:
      "AI import isn’t turned on for this server yet. This is on us — please contact support.",
    retryable: false,
  },
  UNSUPPORTED_MEDIA_TYPE: {
    severity: "user_input",
    title: "Unsupported file",
    message: "That file type isn’t supported. Upload a PNG, JPEG, WEBP, or PDF.",
    retryable: false,
  },

  // ── API-ENVELOPE / AppError class-name keys (migrated from USER_FRIENDLY_MESSAGES) ──
  AuthenticationError: {
    severity: "auth",
    title: "Please sign in",
    message: "Please sign in to continue.",
    retryable: true,
  },
  PermissionDeniedError: {
    severity: "auth",
    title: "No access",
    message: "You don’t have permission to do that.",
    retryable: false,
  },
  PERMISSION_DENIED: {
    severity: "auth",
    title: "No access",
    message: "You don’t have permission to do that.",
    retryable: false,
  },
  // Per-job-title management-feature LOCK (Layer 3): the location HAS the
  // feature, but the requesting user's job role is explicitly locked out of it.
  // Distinct from FEATURE_NOT_ENTITLED below — the fix is an admin unlocking the
  // feature for the job, not a billing/plan change.
  FEATURE_LOCKED: {
    severity: "auth",
    title: "Locked for your role",
    message:
      "Your job role doesn’t have access to this feature. Ask an owner or administrator to unlock it for your role.",
    retryable: false,
  },
  // Layer 1 entitlement: the location itself doesn't have this feature enabled.
  // The fix is a plan/entitlement change, not a per-job unlock.
  // ── Tenant purge dual control (order-up-supabase 20261003035114) ──────────
  // A purge runs only by consuming an authorisation: requested by one person,
  // approved by a DIFFERENT listed approver, and 72 hours after approval. Each
  // refusal below is the guard working, so the copy says what to do next.
  TENANT_PURGE_NOT_AUTHORIZED: {
    severity: "user_input",
    title: "Purge not authorised",
    message:
      "This tenant has no approved purge authorisation. Request one, and have the other approver approve it.",
    retryable: false,
    nextSteps: ["Open Site Admin → Tenant purges and request a purge for this tenant."],
  },
  TENANT_PURGE_NOT_YET: {
    severity: "user_input",
    title: "Waiting period still running",
    message:
      "This purge is approved but cannot run until 72 hours after approval.",
    retryable: false,
    preferServerMessage: true,
  },
  TENANT_PURGE_SAME_APPROVER: {
    severity: "auth",
    title: "Needs a second person",
    message:
      "A purge must be approved by someone other than the person who requested it.",
    retryable: false,
  },
  TENANT_PURGE_APPROVER_NOT_ALLOWED: {
    severity: "auth",
    title: "Not an approver",
    message: "Only a listed purge approver can approve a tenant purge.",
    retryable: false,
  },
  TENANT_PURGE_ALREADY_REQUESTED: {
    severity: "user_input",
    title: "Already requested",
    message:
      "This tenant already has an open purge request for that data. Approve or cancel it first.",
    retryable: false,
  },
  TENANT_PURGE_NOT_PENDING: {
    severity: "user_input",
    title: "No longer pending",
    message: "This purge request has already been approved, cancelled or carried out.",
    retryable: false,
  },
  TENANT_PURGE_REQUEST_NOT_FOUND: {
    severity: "user_input",
    title: "Request not found",
    message: "That purge request does not exist.",
    retryable: false,
  },
  TENANT_PURGE_REASON_REQUIRED: {
    severity: "user_input",
    title: "Reason required",
    message: "Give a reason (at least 10 characters) — it is kept with the audit record.",
    retryable: false,
  },
  TENANT_PURGE_SCOPE_INVALID: {
    severity: "user_input",
    title: "Choose what to purge",
    message: "Choose at least one of Books, Property Management or Non-statutory data.",
    retryable: false,
  },
  TENANT_PURGE_LEGAL_HOLD: {
    severity: "user_input",
    title: "Under legal hold",
    message:
      "This tenant is under a legal hold, so nothing can be purged until the hold is released.",
    retryable: false,
  },
  FEATURE_NOT_ENTITLED: {
    severity: "auth",
    title: "Not available for this location",
    message:
      "This feature isn’t enabled for your location. Contact OrderUp to add it to your plan.",
    retryable: false,
    nextSteps: ["Ask OrderUp support to enable it for this location."],
  },
  // Plan 5: the entitlement read FAILED (Firestore error). The write was
  // refused because the gate fails closed — retrying is the right move.
  FEATURE_ENTITLEMENT_CHECK_UNAVAILABLE: {
    severity: "service",
    title: "Couldn’t confirm this feature",
    message:
      "We couldn’t confirm this feature is enabled for your location, so nothing was changed. Please try again in a moment.",
    retryable: true,
  },
  // ── Merchant onboarding (TRX boarding) ──────────────────────────────────────
  MERCHANT_APPLICATION_INCOMPLETE: {
    severity: "user_input",
    title: "Application isn’t complete yet",
    message:
      "A few required details are still missing before this merchant application can be submitted for boarding.",
    retryable: false,
    nextSteps: [
      "Review each tab for any required field marked incomplete.",
      "Make sure the primary owner’s identity, a settlement bank account, and the required documents are provided.",
    ],
  },
  MERCHANT_APPLICATION_ALREADY_SUBMITTED: {
    severity: "auth",
    title: "Already submitted",
    message:
      "This merchant application has already been submitted and can no longer be edited. Contact OrderUp if you need to make a change.",
    retryable: false,
  },
  MERCHANT_APPLICATION_AGREEMENT_REQUIRED: {
    severity: "user_input",
    title: "Merchant Agreement not signed",
    message: "Sign the Merchant Agreement before submitting.",
    retryable: false,
    nextSteps: [
      "Open the Agreement tab and sign in-app, or email the signing link to the authorized signer.",
    ],
  },
  MERCHANT_APPLICATION_AGREEMENT_STALE: {
    severity: "user_input",
    title: "Agreement is out of date",
    message:
      "The application changed after it was signed — re-sign the agreement.",
    retryable: false,
  },
  // Approve-time fail-closed gates. Both are seen only by OrderUp underwriting
  // staff (the approve surface is staff-only), so the copy speaks to the
  // person typing the credentials, not to the merchant.
  MERCHANT_CREDENTIALS_VERIFICATION_FAILED: {
    severity: "user_input",
    title: "Those credentials didn’t authenticate",
    message:
      "The processor rejected these credentials, so nothing was saved and the restaurant was not taken live.",
    retryable: true,
    nextSteps: [
      "Re-check the client ID, source ID, and password against what the processor issued — a trailing space or a swapped ID is the usual cause.",
      "Confirm you are pointed at the right gateway environment: test credentials never authenticate against production, and the other way round.",
      "If the values are definitely correct, ask the processor to confirm the account is open before approving again.",
    ],
  },
  MERCHANT_NO_RAIL_SELECTED: {
    severity: "user_input",
    title: "Choose what was approved",
    message:
      "Pick at least one payment type the merchant was approved for — approving with none would leave them unable to take any payment.",
    retryable: true,
    nextSteps: [
      "Tick cards, bank transfers (ACH), or both, matching the underwriting decision.",
    ],
  },
  // Raised only by the out-of-band rail-activation path, so the copy speaks to
  // the OrderUp operator recording an activation, not to the merchant.
  MERCHANT_NOT_BOARDED: {
    severity: "user_input",
    title: "This business isn’t connected to a processor yet",
    message:
      "Payment rails record what a processor underwrote, so they can only be activated for a business that already has a gateway and stored credentials. Nothing was changed.",
    retryable: false,
    nextSteps: [
      "Set the payment provider and credentials on the restaurant first, then activate the rails.",
      "If the business was boarded in the processor’s portal, copy its credentials across before recording the activation.",
    ],
  },

  // Training videos kill-switch (`initiative.training_videos`). Raised by
  // TrainingService on every plane while the feature is switched off.
  TRAINING_DISABLED: {
    severity: "service",
    title: "Training videos aren’t available yet",
    message:
      "Training videos are switched off for now. Nothing you did caused this, and nothing was changed.",
    retryable: false,
    nextSteps: [
      "Check back later, or ask your OrderUp contact when training videos will be turned on.",
    ],
  },

  // Tip auth buffer (docs/CARD_TIPPING_ARCHITECTURE.md §5b). A till asked for a
  // buffered card hold the server would not choose; refused before the card was
  // charged, so the fix is to run the card again for the check amount.
  TIP_AUTH_BUFFER_REFUSED: {
    severity: "user_input",
    title: "Run the card again for the check amount",
    message:
      "This card hold included a tip buffer that isn’t allowed here right now. The card was not charged.",
    retryable: true,
    nextSteps: [
      "Run the card again — the till will hold just the check amount.",
      "If this keeps happening, update the POS app: its tip buffer setting is out of step with the restaurant’s.",
    ],
  },

  // A raw card number reached the payments API (SOC 2 R60, PCI-1): the till's
  // own keyed fields are retired; keyed cards go through the processor's
  // secure card entry, which encrypts the number before it leaves the page.
  RAW_CARD_NUMBER_REFUSED: {
    severity: "user_input",
    title: "Use the secure card entry",
    message:
      "Card numbers can no longer be typed into the till’s own fields. Nothing was charged.",
    retryable: true,
    nextSteps: [
      "Take the card on the card reader, or key it into the secure card entry.",
      "If the till keeps showing its own card-number fields, update the POS app and restart it.",
    ],
  },

  // Tip auth buffer, reader rail (docs/CARD_TIPPING_ARCHITECTURE.md §5b,
  // ruling P1-R14). The bank approved LESS than the reader was armed at (code
  // 00, not ARC 10); the server released that partial and declined the
  // payment. Nothing is held, so the till re-reads the same card at the check
  // amount. The till receives this as a decline (`respcode` + `decline`
  // guidance on the auth response); this is the copy for the bare code.
  TIP_BUFFER_PARTIAL_RELEASED: {
    severity: "user_input",
    title: "Card not charged — run it again for the check amount",
    message:
      "The bank approved less than the amount held for a tip, so that partial hold was released. Nothing was charged and nothing is held on the card.",
    retryable: true,
    nextSteps: [
      "Run the same card again — the till will hold just the check amount.",
      "Or take the payment with a different card or another form of payment.",
    ],
  },

  // ── TRX PAYPAGE SESSION MINT (POST /payments/trx/paypage/init) ────────────
  // Every one of these used to reach the browser as a bare message with NO
  // code, so `translateError` fell through to the generic "check your input"
  // — telling an operator their CARD ENTRY was wrong when the real cause was a
  // routing, credential or gateway problem they could not see. None of these
  // are user input errors, and none say so.
  TRX_PAYPAGE_NOT_TRX_ROUTED: {
    severity: "service",
    title: "This location isn’t set up for TRX card entry",
    message:
      "Secure card entry needs the business routed to TRX, and this one is routed somewhere else (or nowhere yet), so the card form can’t start.",
    retryable: false,
    nextSteps: [
      "Set the payment provider to TRX for this location in the admin app, then reopen this screen.",
    ],
  },
  TRX_PAYPAGE_CREDENTIALS_MISSING: {
    severity: "service",
    title: "This location has no gateway credentials stored",
    message:
      "Secure card entry needs the processor credentials for this business, and none are stored, so the card form can’t start.",
    retryable: false,
    nextSteps: [
      "Add the TRX Client ID, Source ID and API password for this location in the admin app.",
    ],
  },
  TRX_PAYPAGE_CREDENTIALS_INCOMPLETE: {
    severity: "service",
    title: "This location’s gateway credentials are incomplete",
    message:
      "Some of the processor credentials for this business are missing, so a secure card-entry session can’t be created.",
    retryable: false,
    nextSteps: [
      "Re-enter the TRX Client ID, Source ID and API password in the admin app — the message above names which field is missing.",
    ],
  },
  TRX_PAYPAGE_SESSION_NOT_RETURNED: {
    severity: "service",
    title: "The processor didn’t return a card-entry session",
    message:
      "TRX accepted the request but didn’t return a usable payment page, so the card fields can’t load. This is between OrderUp and the processor — it isn’t anything you entered.",
    retryable: true,
    nextSteps: [
      "Try again — a session can fail transiently.",
      "If it keeps happening, contact OrderUp support with the reference below. The processor’s own response code is included in the message and tells support whether hosted card entry is enabled on this merchant account.",
    ],
  },
  TRX_PAYPAGE_AMOUNT_REQUIRED: {
    severity: "service",
    title: "Card entry couldn’t start",
    message:
      "This card-entry session was requested in a mode that needs an amount, and none was supplied.",
    retryable: false,
    nextSteps: ["Contact OrderUp support with the reference below."],
  },
  TRX_PAYPAGE_TENANT_NOT_FOUND: {
    severity: "service",
    title: "Card entry couldn’t start",
    message:
      "The business this payment belongs to couldn’t be found, so a secure card-entry session can’t be created.",
    retryable: false,
    nextSteps: ["Reload the page.", "Contact OrderUp support if it persists."],
  },
  TRX_PAYPAGE_RESTAURANT_NOT_FOUND: {
    severity: "service",
    title: "Card entry couldn’t start",
    message:
      "This location couldn’t be found under its business, so a secure card-entry session can’t be created.",
    retryable: false,
    nextSteps: ["Reload the page.", "Contact OrderUp support if it persists."],
  },
  TRX_PAYPAGE_RESTAURANT_SHAPE_INVALID: {
    severity: "service",
    title: "Card entry couldn’t start",
    message:
      "This location’s record is missing data the card form needs, so a secure card-entry session can’t be created.",
    retryable: false,
    nextSteps: ["Contact OrderUp support with the reference below."],
  },

  // ── BOOKS — ORDERSIGN (BOOKS_ORDERSIGN_* raised by the ordersign RPCs;
  //    BOOKS_SIGNATURE_REQUIRED by accept_book_estimate_by_token) ────────────
  BOOKS_SIGNATURE_REQUIRED: {
    severity: "user_input",
    title: "Signature required",
    message:
      "This document requires an electronic signature — use the Review & sign link to accept it.",
    retryable: false,
  },
  BOOKS_ORDERSIGN_CONSENT_REQUIRED: {
    severity: "user_input",
    title: "Consent needed first",
    message:
      "Please agree to use electronic records and signatures before signing.",
    retryable: true,
  },
  BOOKS_ORDERSIGN_ALREADY_SIGNED: {
    severity: "user_input",
    title: "Already signed",
    message:
      "This document has already been signed — no further action is needed.",
    retryable: false,
  },
  BOOKS_ORDERSIGN_EXPIRED: {
    severity: "user_input",
    title: "Signing request expired",
    message: "This signing request has expired.",
    retryable: false,
    nextSteps: ["Ask the sender to re-send the signing request."],
  },
  BOOKS_ORDERSIGN_ENTITY_NOT_SIGNABLE: {
    severity: "user_input",
    title: "No longer signable",
    message:
      "This document changed or was closed after the signing request went out, so it can no longer be signed.",
    retryable: false,
  },
  BOOKS_ORDERSIGN_DISCLOSURE_UPDATED: {
    severity: "user_input",
    title: "Legal text updated",
    message: "The legal text was updated — review and agree again.",
    retryable: true,
  },
  BOOKS_ORDERSIGN_GEO_REQUIRED: {
    severity: "user_input",
    title: "Location required",
    message:
      "Your device's location is recorded as part of this signature, so it can't be completed without it.",
    retryable: true,
    nextSteps: [
      "Allow location access for this site in your browser, then sign again.",
      "On a phone, also allow location for the browser in system settings.",
    ],
  },
  BOOKS_ORDERSIGN_TYPED_NAME_MISMATCH: {
    severity: "user_input",
    title: "Signature doesn't match your name",
    message:
      "The name you typed to sign doesn't match the full name on this document.",
    retryable: true,
    nextSteps: [
      "Type your name exactly as it appears in the Full name field, then sign again.",
      "If the Full name is wrong, correct it first — or draw your signature instead.",
    ],
  },
  BOOKS_ORDERSIGN_STAFFDOC_CONTEXT_REQUIRED: {
    severity: "service",
    title: "Onboarding document could not be sent",
    message: "The onboarding document is missing information needed to send it.",
    retryable: false,
    nextSteps: ["Re-upload the document, then send it again."],
  },
  BOOKS_ORDERSIGN_STAFFDOC_KEYS_REQUIRED: {
    severity: "service",
    title: "Onboarding document could not be sent",
    message: "The onboarding document is missing information needed to send it.",
    retryable: false,
    nextSteps: ["Re-upload the document, then send it again."],
  },
  BOOKS_ORDERSIGN_STAFFDOC_TENANCY_INVALID: {
    severity: "service",
    title: "Onboarding document could not be sent",
    message:
      "This location is missing the identifiers needed to send onboarding documents.",
    retryable: false,
    nextSteps: ["Contact support with this location's name."],
  },
  ONBOARDING_DOC_EMPLOYEE_NOT_LINKED: {
    severity: "user_input",
    title: "Employee hasn't joined yet",
    message:
      "Onboarding documents can only be sent once the employee has accepted their account invitation.",
    retryable: false,
    nextSteps: ["Send or re-send their account invitation first."],
  },
  ONBOARDING_DOC_EMPLOYEE_NO_EMAIL: {
    severity: "user_input",
    title: "No email address on file",
    message: "This employee has no email address to send the document to.",
    retryable: false,
    nextSteps: ["Add an email address to the employee's profile, then send again."],
  },
  ONBOARDING_DOC_QUOTA_EXCEEDED: {
    severity: "user_input",
    title: "Storage limit reached",
    message:
      "Uploading this document would exceed this location's drive storage limit.",
    retryable: false,
    nextSteps: ["Remove unused files from the location drive, or upgrade storage."],
  },
  ONBOARDING_DOC_GUID_MISMATCH: {
    severity: "service",
    title: "Upload could not be verified",
    message: "The upload was rejected because its details did not match.",
    retryable: true,
    nextSteps: ["Reload the page and try the upload again."],
  },
  ONBOARDING_DOC_BAD_REQUEST: {
    severity: "service",
    title: "Upload could not be read",
    message: "The file upload did not arrive in a readable form.",
    retryable: true,
    nextSteps: [
      "Reload the page and choose the PDF again.",
      "If it keeps failing, try a different browser.",
    ],
  },
  // fs-plane refusals surfaced by the Drive writes the onboarding upload and
  // the signed-artifact archive perform. Uncatalogued they render the generic
  // "something went wrong", which tells a manager nothing about a name clash
  // they can actually fix.
  FS_NAME_CONFLICT: {
    severity: "user_input",
    title: "That name is already taken",
    message:
      "A file or folder with this name already exists in that drive folder.",
    retryable: false,
    nextSteps: ["Rename the document and try again."],
  },
  FS_PARENT_INVALID: {
    severity: "service",
    title: "Drive folder unavailable",
    message: "The drive folder this document belongs in could not be used.",
    retryable: true,
    nextSteps: [
      "Try again in a moment.",
      "If it keeps failing, contact support with the reference below.",
    ],
  },
  BOOKS_ORDERSIGN_REQUEST_WITHDRAWN: {
    severity: "user_input",
    title: "Request withdrawn",
    message: "The sender withdrew this signing request.",
    retryable: false,
  },
  BOOKS_ORDERSIGN_TOKEN_UNKNOWN: {
    severity: "user_input",
    title: "Link not recognized",
    message:
      "This signing link isn’t valid anymore — it may have been replaced by a newer email.",
    retryable: false,
  },
  BOOKS_ORDERSIGN_REQUEST_NOT_FOUND: {
    severity: "user_input",
    title: "Signing request not found",
    message: "We couldn’t find that signing request.",
    retryable: false,
  },
  BOOKS_ORDERSIGN_DISABLED: {
    severity: "service",
    title: "Not enabled yet",
    message: "OrderSign signatures aren’t turned on for this business yet.",
    retryable: false,
  },
  BOOKS_ORDERSIGN_MANDATE_DISCLOSURE_MISSING: {
    severity: "service",
    title: "Mandate terms not published yet",
    message:
      "The ACH authorization terms for this mandate haven’t been published yet, so it can’t be sent for signature.",
    retryable: false,
    nextSteps: ["Contact OrderUp support if this persists."],
  },
  BOOKS_ORDERSIGN_MANDATE_INTENT_MISSING: {
    severity: "service",
    title: "Signing legal text not published yet",
    message:
      "The OrderSign intent-to-sign legal text for ACH mandates hasn’t been published yet, so the signer couldn’t complete this mandate — it can’t be sent for signature.",
    retryable: false,
    nextSteps: [
      "Publish an active OrderSign intent disclosure for ACH mandates (counsel), then send the mandate again.",
      "Contact OrderUp support if this persists.",
    ],
  },
  // Mandate-request guards raised by create_ordersign_request when the frozen
  // standing-authorization facts are missing or malformed. The *_REQUIRED ones
  // are fixable in the send form; the rest indicate wiring and go to support.
  BOOKS_ORDERSIGN_MANDATE_AMOUNT_REQUIRED: {
    severity: "user_input",
    title: "Amount required",
    message:
      "Enter either a fixed debit amount or a description of how the amount is calculated before sending the mandate.",
    retryable: true,
  },
  BOOKS_ORDERSIGN_MANDATE_EFFECTIVE_FROM_REQUIRED: {
    severity: "user_input",
    title: "Start date required",
    message: "Enter the date the debit authorization takes effect before sending the mandate.",
    retryable: true,
  },
  BOOKS_ORDERSIGN_MANDATE_ACCOUNT_LAST4_REQUIRED: {
    severity: "user_input",
    title: "Account last four required",
    message:
      "Enter the last four digits of the bank account the mandate authorizes before sending it.",
    retryable: true,
  },
  BOOKS_ORDERSIGN_MANDATE_CUSTOMER_REQUIRED: {
    severity: "user_input",
    title: "Customer required",
    message:
      "A customer mandate needs a customer to sign it. Set the customer on the schedule’s invoice template, then send the mandate again.",
    retryable: false,
  },
  BOOKS_ORDERSIGN_MANDATE_CONTEXT_REQUIRED: {
    severity: "service",
    title: "Mandate couldn’t be created",
    message:
      "The mandate request was missing its authorization details, so it wasn’t created. This is on us — please contact support.",
    retryable: false,
  },
  BOOKS_ORDERSIGN_MANDATE_SCOPE_INVALID: {
    severity: "service",
    title: "Mandate couldn’t be created",
    message:
      "The mandate request named an unknown authorization scope, so it wasn’t created. This is on us — please contact support.",
    retryable: false,
  },
  BOOKS_ORDERSIGN_MANDATE_DISCLOSURE_VERSION_REQUIRED: {
    severity: "service",
    title: "Mandate couldn’t be created",
    message:
      "The mandate request didn’t pin the authorization terms it was rendered from, so it wasn’t created. This is on us — please contact support.",
    retryable: false,
  },
  BOOKS_ORDERSIGN_MANDATE_CUSTOMER_FORBIDDEN: {
    severity: "service",
    title: "Mandate couldn’t be created",
    message:
      "A business-billing mandate must not name a customer, so this request was refused. This is on us — please contact support.",
    retryable: false,
  },
  BOOKS_ORDERSIGN_MANDATE_TENANCY_INVALID: {
    severity: "service",
    title: "Mandate couldn’t be created",
    message:
      "The mandate’s customer couldn’t be matched to this business, so the request was refused. This is on us — please contact support.",
    retryable: false,
  },
  // Signed-mandate ARM step (`…/ach-mandate/ordersign/arm`): vault the bank
  // account the signed mandate names and bind the schedule. No new evidence
  // is captured here, so every refusal is about the recorded authorization.
  BOOKS_ACH_MANDATE_NOT_RECORDED: {
    severity: "service",
    title: "Signed mandate still recording",
    message:
      "The signed mandate hasn’t finished recording its authorization yet. Recording completes automatically — try again in a few minutes.",
    retryable: true,
  },
  BOOKS_ACH_MANDATE_REVOKED: {
    severity: "user_input",
    title: "Mandate revoked",
    message:
      "The signed mandate’s authorization has been revoked, so autopay can’t be armed from it. Send a new mandate for signature.",
    retryable: false,
  },
  BOOKS_ACH_MANDATE_SUBJECT_MISMATCH: {
    severity: "service",
    title: "Mandate doesn’t cover this customer",
    message:
      "The recorded authorization doesn’t cover this schedule’s customer, so autopay wasn’t armed. This is on us — please contact support.",
    retryable: false,
  },
  BOOKS_ACH_MANDATE_SCHEDULE_MISMATCH: {
    severity: "user_input",
    title: "Mandate is for a different schedule",
    message:
      "The signed mandate was sent for a different recurring schedule. Arm autopay from that schedule, or send a new mandate for this one.",
    retryable: false,
  },
  BOOKS_ACH_MANDATE_ACCOUNT_MISMATCH: {
    severity: "user_input",
    title: "Wrong bank account",
    message:
      "The signed mandate authorizes a specific bank account, and the account entered doesn’t match it. Enter that account, or send a new mandate for signature.",
    retryable: true,
  },
  // A pre-vaulted StorageSafe reference supplied with a mandate CAPTURE is
  // verified server-side (it must be this customer's own profile, holding the
  // account being authorized) before anything is bound. Unknown, foreign and
  // wrong-account references all collapse to this one code so a response can
  // never be used to probe which references exist.
  BOOKS_ACH_VAULT_REF_NOT_OWNED: {
    severity: "user_input",
    title: "Bank account couldn’t be verified",
    message:
      "That saved bank account couldn’t be verified for this customer. Re-enter the bank details to authorize autopay.",
    retryable: false,
  },
  // Bank-account VALIDATION — a different fact from authorization. A mandate
  // can be flawlessly consented-to and still name an account that does not
  // exist (one transposed digit), which nothing in the consent record can
  // detect. Raised by the mandate ceremonies when the processor's $0 account
  // check REFUSES the account, and by the saved-account path when no verdict
  // was ever recorded for the account being reused. Retryable — the fix is to
  // re-read the numbers off the check and enter them again.
  BOOKS_ACH_ACCOUNT_NOT_VALIDATED: {
    severity: "user_input",
    title: "Bank account couldn’t be verified",
    message:
      "The payment processor could not verify this bank account, so autopay wasn’t set up and nothing was saved. Check the routing and account numbers with the customer and enter them again.",
    retryable: true,
    nextSteps: [
      "Re-read the routing and account numbers from a check or bank letter, then enter them again.",
    ],
  },
  BOOKS_ACH_INSTRUMENT_REQUIRED: {
    severity: "user_input",
    title: "Bank details required",
    message:
      "Autopay needs the customer’s bank details before it can be authorized. Enter the account and routing numbers, then try again.",
    retryable: true,
  },
  // The bank network only accepts an account-holder name written in
  // `[A-Za-z0-9 ]`, and it rejects the account check outright when the name is
  // empty (TRX_API_REFERENCE §4.2). A customer whose name has no such
  // characters therefore cannot have their account verified at all — refused
  // at the ceremony, where the record can still be corrected, rather than
  // spending a probe that can never answer.
  BOOKS_ACH_ACCOUNT_HOLDER_NAME_REQUIRED: {
    severity: "user_input",
    title: "Account holder name needed",
    message:
      "The bank requires the account holder’s name in Latin letters or numbers, and this customer’s name has none. Add a name the bank will accept to the customer record, then set up autopay again.",
    retryable: true,
    nextSteps: [
      "Edit the customer and enter their name (or business name) using letters A–Z and numbers.",
      "Set up autopay again.",
    ],
  },
  // OrderUp Books lock (the original Phase-0 per-feature enforcement). Same
  // shape as FEATURE_LOCKED but emitted by requireBooksAccess with its own code.
  BOOKS_ACCESS_DENIED: {
    severity: "auth",
    title: "No access to Books",
    message:
      "Your role doesn’t have access to OrderUp Books for this location. Ask an owner or administrator to grant it.",
    retryable: false,
  },
  // Emitted by requireBooksAccess when a masquerade impersonation session
  // attempts a Books WRITE. Reads still work, so the copy has to explain that
  // looking is fine and only changing is blocked — otherwise a support agent
  // reads this as "impersonation is broken" and files a bug. The two named
  // next steps are the two real ways forward, in the order support should try
  // them.
  BOOKS_IMPERSONATED_WRITE_DENIED: {
    severity: "auth",
    title: "Can’t change Books while impersonating",
    message:
      "You’re signed in as someone else, so you can view this business’s Books but not change them — any entry you saved would be recorded under their name instead of yours. Nothing was saved.",
    retryable: false,
    nextSteps: [
      "End the impersonation session and sign back in as yourself to make the change.",
      "If you need to work inside this business as yourself, start a support-scope session instead of impersonating a user.",
    ],
  },
  // Emitted by the POS / check / payment restaurant-scope gates
  // (apps/api/components/utils/locationScopeAccess.ts) when a support session
  // — "Enter as support" or the POS staff console — tries to move money or
  // change a check. Reads still work, so the copy says looking is fine. Money
  // movement by support waits on the Phase 3 escalation confirm
  // (docs/POS_STAFF_CONSOLE.md §6).
  SUPPORT_SESSION_READ_ONLY: {
    severity: "auth",
    title: "Support sessions are view-only here",
    message:
      "You’re in a support session, so you can view this restaurant’s payments and checks but not charge, void, refund, tip-adjust or change a check. Nothing was changed.",
    retryable: false,
    nextSteps: [
      "Ask someone at the restaurant to make the change on their POS or in the management app.",
    ],
  },
  // Emitted by requireTenantPermission when a manager-PIN approval names an
  // employee or restaurant other than the one the action is for.
  OPERATOR_SCOPE_MISMATCH: {
    severity: "auth",
    title: "Approval from another location",
    message:
      "The approving employee has to belong to the restaurant this action is for. Nothing was changed.",
    retryable: false,
    nextSteps: [
      "Have a manager at this restaurant approve it.",
      "If this device was recently moved between locations, sign out and back in so it picks up the right restaurant.",
    ],
  },
  // ── OPERATOR ASSERTIONS (server-verified manager / cashier PIN) ────────────
  // docs/OPERATOR_ASSERTION.md. The POS re-prompts for a PIN on REQUIRED,
  // INVALID and EXPIRED; the copy below is the fallback for any other client.
  OPERATOR_ASSERTION_REQUIRED: {
    severity: "auth",
    title: "Enter a PIN to continue",
    message:
      "This action needs a PIN entered on this device. Nothing was changed.",
    retryable: false,
    nextSteps: ["Enter the PIN of an employee who is allowed to do this, then try again."],
  },
  OPERATOR_ASSERTION_INVALID: {
    severity: "auth",
    title: "PIN approval not accepted",
    message:
      "The PIN approval sent with this action could not be verified. Nothing was changed.",
    retryable: false,
    nextSteps: ["Enter the PIN again."],
  },
  OPERATOR_ASSERTION_EXPIRED: {
    severity: "auth",
    title: "PIN approval expired",
    message: "The PIN approval for this action has expired. Nothing was changed.",
    retryable: false,
    nextSteps: ["Enter the PIN again."],
  },
  OPERATOR_ASSERTION_REPLAYED: {
    severity: "auth",
    title: "Approval already used",
    message:
      "That manager approval was already used for another action. Each approval covers one action. Nothing was changed.",
    retryable: false,
    nextSteps: ["Have the manager enter their PIN again for this action."],
  },
  OPERATOR_ASSERTION_SUBJECT_MISMATCH: {
    severity: "auth",
    title: "Approval is for a different order",
    message:
      "That manager approval was given for a different order, so it cannot be used here. Nothing was changed.",
    retryable: false,
    nextSteps: ["Ask a manager to approve this order."],
  },
  // ── Billing dual control — OrderUp's own money actions (SOC 2 R19, docs/BILLING_DUAL_CONTROL.md) ──
  BILLING_APPROVAL_PENDING: {
    severity: "user_input",
    title: "Sent for approval",
    message:
      "This needs a second person. Nothing has happened yet: it waits in Billing → Money Approvals until someone else approves it.",
    retryable: false,
    nextSteps: ["Ask another billing approver to approve it in Billing → Money Approvals within 72 hours."],
  },
  BILLING_APPROVAL_EXPIRED: {
    severity: "user_input",
    title: "This request expired",
    message: "Nobody approved this request in time, so it can no longer run. Nothing was done.",
    retryable: false,
    nextSteps: ["If it is still needed, ask for it again."],
  },
  BILLING_APPROVAL_AMOUNT_CHANGED: {
    severity: "user_input",
    title: "The amount changed",
    message: "The amount is now higher than what was approved, so nothing was done.",
    retryable: false,
    nextSteps: ["Ask for it again so the new amount can be approved."],
  },
  BILLING_APPROVAL_TARGET_CHANGED: {
    severity: "user_input",
    title: "The subscription changed",
    message: "This location has a different subscription than when the request was raised, so nothing was done.",
    retryable: false,
    nextSteps: ["Ask for it again against the current subscription."],
  },
  BILLING_APPROVAL_CARD_TOKEN_NOT_QUEUEABLE: {
    severity: "user_input",
    title: "Put the card on file first",
    message:
      "A plan change needs a second approver, and a request waiting for approval cannot carry a card. Nothing was changed.",
    retryable: false,
    nextSteps: ["Add the card on file, then assign the plan."],
  },
  BILLING_APPROVAL_REASON_REQUIRED: {
    severity: "user_input",
    title: "Say why",
    message: "Give a reason of at least 10 characters so the approver can judge it.",
    retryable: true,
  },
  BILLING_APPROVAL_NOT_FOUND: {
    severity: "user_input",
    title: "Request not found",
    message: "That approval request doesn't exist.",
    retryable: false,
  },
  BILLING_DUAL_CONTROL_OFF: {
    severity: "user_input",
    title: "Approvals are switched off",
    message: "Billing approvals are switched off, so nothing waiting can be approved. The person who asked can now do it directly.",
    retryable: false,
  },
  BILLING_DUAL_CONTROL_UNAVAILABLE: {
    severity: "service",
    title: "Approval rules can't be checked",
    message: "We couldn't confirm whether this needs a second approver. Nothing was done.",
    retryable: true,
    nextSteps: ["Try again in a moment."],
  },
  BILLING_DUAL_CONTROL_NOT_DEPLOYED: {
    severity: "service",
    title: "Approvals aren't set up here",
    message: "Billing approvals are switched on, but they are not set up in this environment yet. Nothing was done.",
    retryable: false,
    nextSteps: ["Tell engineering: the billing dual-control database change is not deployed here."],
  },
  BILLING_DUAL_CONTROL_NO_EXECUTOR: {
    severity: "service",
    title: "This request can't run",
    message: "This version of OrderUp can't run that request. Nothing was done.",
    retryable: false,
    nextSteps: ["Tell engineering, and ask for it again once fixed."],
  },
  OPS_APPROVAL_SELF_DECISION_FORBIDDEN: {
    severity: "auth",
    title: "Someone else has to approve this",
    message: "You asked for this, so a different person has to approve or deny it.",
    retryable: false,
  },
  OPS_APPROVAL_APPROVER_ROLE_REQUIRED: {
    severity: "auth",
    title: "You aren't an approver",
    message: "Approving this needs the Billing Approver role. Nothing was done.",
    retryable: false,
    nextSteps: ["Ask the owner to name you a Billing Approver."],
  },
  OPS_DUAL_CONTROL_NOT_PENDING: {
    severity: "user_input",
    title: "Already decided",
    message: "This request was already approved, denied, withdrawn or expired. It runs at most once.",
    retryable: false,
    nextSteps: ["Reload to see where it stands."],
  },
  // ── Remote manager approval (docs/superpowers/specs/2026-10-16-remote-manager-approval-design.md) ──
  REMOTE_APPROVAL_GATE_OFF: {
    severity: "service",
    title: "Remote approval is off",
    message: "Remote approval is not available right now. Nothing was changed.",
    retryable: false,
    nextSteps: ["Have a manager enter their PIN at this till."],
  },
  REMOTE_APPROVAL_GATE_UNAVAILABLE: {
    severity: "service",
    title: "Remote approval can't be checked",
    message: "We couldn't confirm remote approval is available. Nothing was changed.",
    retryable: true,
    nextSteps: ["Try again in a moment, or have a manager enter their PIN at this till."],
  },
  REMOTE_APPROVAL_UNAVAILABLE: {
    severity: "service",
    title: "PIN can't be checked right now",
    message: "The PIN could not be checked right now. Nothing was changed.",
    retryable: true,
    nextSteps: ["Try again in a moment."],
  },
  REMOTE_APPROVAL_DISABLED: {
    severity: "user_input",
    title: "Remote approval is off here",
    message: "This location has not turned on remote approval. Nothing was changed.",
    retryable: false,
    nextSteps: ["Have a manager enter their PIN at this till, or turn it on in Settings → Text Alerts."],
  },
  REMOTE_APPROVAL_NOT_ENTITLED: {
    severity: "user_input",
    title: "Remote approval isn't on your plan",
    message: "Remote manager approval is an add-on this location doesn't have. Nothing was changed.",
    retryable: false,
    nextSteps: ["Have a manager enter their PIN at this till. The owner can add Remote manager approval in Billing."],
  },
  REQUESTER_PIN_REQUIRED: {
    severity: "auth",
    title: "Enter your own PIN",
    message: "Enter your own PIN to send this request to a manager. Nothing was sent.",
    retryable: true,
    nextSteps: ["Enter your PIN, then send the request again."],
  },
  NOT_REMOTE_APPROVABLE: {
    severity: "user_input",
    title: "Needs a manager here",
    message: "This action can't be approved remotely. Nothing was changed.",
    retryable: false,
    nextSteps: ["Have a manager enter their PIN at this till."],
  },
  NO_REMOTE_APPROVERS: {
    severity: "user_input",
    title: "No manager can be reached",
    message: "No manager who can approve this can be reached right now. Nothing was sent.",
    retryable: false,
    nextSteps: ["Have a manager enter their PIN at this till."],
  },
  SUBJECT_NOT_FOUND: {
    severity: "user_input",
    title: "Order not ready yet",
    message: "The order or an item on it could not be read yet. Nothing was sent.",
    retryable: true,
    nextSteps: ["Wait a moment, then send the request again."],
  },
  CLAIM_EXCEEDS_CHECK: {
    severity: "user_input",
    title: "Amount is more than the check",
    message: "The amount to approve is more than the check total. Nothing was sent.",
    retryable: false,
    nextSteps: ["Check the discount, then ask again."],
  },
  TOO_MANY_PENDING: {
    severity: "user_input",
    title: "Too many approvals waiting",
    message: "Too many approval requests are waiting at this location. Nothing was sent.",
    retryable: true,
    nextSteps: ["Wait for a request to be answered, or have a manager enter their PIN here."],
  },
  APPROVAL_GUID_IN_USE: {
    severity: "user_input",
    title: "Request already exists",
    message: "This request was already sent from another till. Nothing was sent.",
    retryable: false,
    nextSteps: ["Start the action again to send a new request."],
  },
  APPROVAL_NOT_FOUND: {
    severity: "user_input",
    title: "Request not found",
    message: "This approval request could not be found, or it is no longer active.",
    retryable: false,
  },
  APPROVAL_EXPIRED: {
    severity: "user_input",
    title: "Request expired",
    message: "This approval request has expired. Nothing was changed.",
    retryable: false,
    nextSteps: ["Ask again, or have a manager enter their PIN at the till."],
  },
  ALREADY_DECIDED: {
    severity: "user_input",
    title: "Already answered",
    message: "Another manager already answered this request.",
    retryable: false,
  },
  APPROVAL_NOT_APPROVED: {
    severity: "user_input",
    title: "Not approved",
    message: "This request has not been approved, so nothing can be done with it yet.",
    retryable: false,
  },
  NOT_AN_APPROVER: {
    severity: "auth",
    title: "Not your request to answer",
    message: "You are not one of the managers asked to approve this request.",
    retryable: false,
  },
  SELF_APPROVAL: {
    severity: "auth",
    title: "You asked for this one",
    message: "You can't approve your own request. Another manager has to answer it.",
    retryable: false,
  },
  TEMPORARY_PIN: {
    severity: "auth",
    title: "Set your own PIN first",
    message: "Your PIN is a temporary one. Set your own PIN on a till before approving remotely.",
    retryable: false,
  },
  STATION_ACCOUNT: {
    severity: "auth",
    title: "Station accounts can't approve",
    message: "A shared station account can't approve requests.",
    retryable: false,
  },
  REMOTE_APPROVAL_MISMATCH: {
    severity: "auth",
    title: "Approval is for something else",
    message: "That remote approval was given for a different action, so it cannot be used here. Nothing was changed.",
    retryable: false,
    nextSteps: ["Ask a manager to approve this action."],
  },
  // ── Shift alerts (docs/superpowers/specs/2026-10-21-shift-alerts-and-attendance-design.md §12) ──
  // The server's sentence names the field that was refused (for example a grace
  // period of 10 or more minutes), so it wins over the generic copy.
  SHIFT_ALERTS_CONFIG_INVALID: {
    severity: "user_input",
    title: "Shift alert settings not saved",
    message: "One of the shift alert settings isn't valid, so nothing was saved.",
    retryable: false,
    preferServerMessage: true,
    nextSteps: ["Check the grace period is 0 to 9 minutes, then save again."],
  },
  SHIFT_ALERTS_SUBSCRIPTION_INVALID: {
    severity: "user_input",
    title: "Your alert choice wasn't saved",
    message: "Choose when you get late alerts and how (app, text or both). Nothing was saved.",
    retryable: false,
    preferServerMessage: true,
  },
  // A read or a write failed (an outage), never a refusal: nothing changed and
  // trying again is safe.
  SHIFT_ALERTS_UNAVAILABLE: {
    severity: "service",
    title: "Shift alerts can't be reached right now",
    message: "Shift alert settings couldn't be loaded or saved right now. Nothing was changed.",
    retryable: true,
    nextSteps: ["Try again in a minute."],
  },
  // ── The on-time record (spec 2026-10-21 §8, §9, §12) ── statuses in
  // `packages/core/src/services/attendance/attendanceErrors.ts`. A validation
  // refusal's server sentence says which part was refused, so it wins.
  ATTENDANCE_REASON_INVALID: {
    severity: "user_input",
    title: "Add a reason",
    message: "Write a short reason so the record shows why. Nothing was changed.",
    retryable: false,
    preferServerMessage: true,
  },
  ATTENDANCE_RANGE_INVALID: {
    severity: "user_input",
    title: "Pick a shorter date range",
    message: "Choose a start date before the end date, no more than a year apart.",
    retryable: false,
    preferServerMessage: true,
  },
  // A time zone a staff app sent with its dates that is not an IANA zone: the
  // days cannot be read on any calendar, so nothing is shown (never read as UTC).
  ATTENDANCE_TIMEZONE_INVALID: {
    severity: "user_input",
    title: "Time zone not recognised",
    message: "The time zone these dates were sent in isn't one we recognise, so the record can't be shown for them.",
    retryable: false,
    nextSteps: ["Update the app and try again."],
  },
  ATTENDANCE_OWN_RECORD: {
    severity: "auth",
    title: "This is your own record",
    message: "You can't excuse, re-check or call out your own attendance. Nothing was changed.",
    retryable: false,
    nextSteps: ["Ask an owner or another manager to do it."],
  },
  ATTENDANCE_RECORD_NOT_FOUND: {
    severity: "user_input",
    title: "Attendance record not found",
    message: "That attendance record isn't at this location any more. Nothing was changed.",
    retryable: false,
    nextSteps: ["Refresh the page and try again."],
  },
  ATTENDANCE_SHIFT_NOT_FOUND: {
    severity: "user_input",
    title: "Shift not found",
    message: "That shift isn't on this location's schedule any more. Nothing was changed.",
    retryable: false,
    nextSteps: ["Refresh the schedule and try again."],
  },
  ATTENDANCE_LOCATION_NOT_FOUND: {
    severity: "user_input",
    title: "Location not found",
    message: "This location couldn't be found, so its attendance can't be shown.",
    retryable: false,
  },
  ATTENDANCE_NOT_EXCUSABLE: {
    severity: "user_input",
    title: "Nothing to excuse",
    message: "This shift was on time, so there is nothing to excuse. Nothing was changed.",
    retryable: false,
  },
  ATTENDANCE_ALREADY_EXCUSED: {
    severity: "user_input",
    title: "Already excused",
    message: "Someone has already excused this record. Nothing was changed.",
    retryable: false,
    nextSteps: ["To change the reason, remove the excuse first, then excuse it again."],
  },
  ATTENDANCE_NOT_EXCUSED: {
    severity: "user_input",
    title: "Not excused",
    message: "This record isn't excused, so there is no excuse to remove. Nothing was changed.",
    retryable: false,
  },
  // Ruling R40: a Called-out record keeps its excuse until the shift is final
  // (end + 2 h); removing it earlier would stop the shift's alerts and book a
  // no-show decided before the shift.
  ATTENDANCE_UNEXCUSE_TOO_EARLY: {
    severity: "user_input",
    title: "Too early to undo",
    message: "You can remove this excuse 2 hours after the shift ends, once its attendance is final. Nothing was changed.",
    retryable: false,
  },
  ATTENDANCE_SHIFT_NOT_LIVE: {
    severity: "user_input",
    title: "This shift can't be marked absent",
    message: "Only a published shift assigned to someone tracked here can be marked absent. Nothing was changed.",
    retryable: false,
  },
  ATTENDANCE_SHIFT_CLOSED: {
    severity: "user_input",
    title: "Too late to mark absent",
    message: "This shift ended too long ago to mark them absent. Nothing was changed.",
    retryable: false,
    nextSteps: ["Excuse its attendance record instead."],
  },
  ATTENDANCE_RECORD_EXISTS: {
    severity: "user_input",
    title: "Attendance already recorded",
    message: "This shift already has its attendance record. Nothing was changed.",
    retryable: false,
    nextSteps: ["Excuse the record instead."],
  },
  // Ruling R57: a no-show is a shift that started with nobody in. Before the
  // start, someone who let you know is marked with their reason instead.
  ATTENDANCE_NO_SHOW_TOO_EARLY: {
    severity: "user_input",
    title: "Their shift hasn't started",
    message: "This shift hasn't started, so it can't be a no-show yet. Nothing was changed.",
    retryable: false,
    nextSteps: ["If they let you know they can't come, choose why they are out instead."],
  },
  // Ruling R57: someone clocked in for the shift came (§7), whatever was said.
  // The server's sentence names them, so it wins over this generic copy.
  ATTENDANCE_CLOCKED_IN: {
    severity: "user_input",
    title: "They clocked in",
    message: "They clocked in for this shift, so they aren't a no-show. Nothing was changed.",
    retryable: false,
    preferServerMessage: true,
  },
  ATTENDANCE_OFF: {
    severity: "user_input",
    title: "Shift alerts are off here",
    message: "Shift alerts are off at this location, so no attendance is recorded for this shift. Nothing was changed.",
    retryable: false,
  },
  // A read failed or the launch switch could not be read (an outage), never a
  // refusal: nothing changed and trying again is safe.
  ATTENDANCE_UNAVAILABLE: {
    severity: "service",
    title: "Attendance can't be reached right now",
    message: "Attendance couldn't be loaded or saved right now. Nothing was changed.",
    retryable: true,
    nextSteps: ["Try again in a minute."],
  },
  // ── Time clock: clocking in from the staff app switched off (spec §14b, D20) ──
  // A location's manager turned it off, so the staff app's punch-in and
  // punch-out are refused and every punch goes through the till. The server's
  // sentence names the location, so it wins over this generic copy.
  TIME_CLOCK_STAFF_APP_DISABLED: {
    severity: "auth",
    title: "Clock in and out at the POS",
    message: "Clocking in from the app is turned off at this location. Clock in and out at the POS.",
    retryable: false,
    preferServerMessage: true,
    nextSteps: ["Clock in and out at the POS.", "Ask a manager if you think this is wrong."],
  },
  // ── Time clock: cash tips declared at a clock-out off the till (STF-02) ──
  // `POST /time-clock/punch-out` takes a declaration only where the location
  // takes cash tips at clock-out. Where they are paid through payroll they are
  // handed in at the till's shift review, so the person clocks out there; where
  // they are declared monthly the punch out carries none. Nothing is saved
  // either way, and the server's sentence names the location, so it wins.
  TIME_CLOCK_CASH_TIPS_AT_POS: {
    severity: "user_input",
    title: "Clock out at the POS",
    message: "Cash tips at this location are handed in at the POS. Clock out at the POS. Nothing was saved.",
    retryable: false,
    preferServerMessage: true,
    nextSteps: ["Clock out at the POS, where you hand in your cash tips."],
  },
  TIME_CLOCK_CASH_TIPS_NOT_TAKEN: {
    severity: "user_input",
    title: "Cash tips aren't declared at clock-out here",
    message:
      "This location records cash tips with the monthly declaration, not at clock-out. Clock out without declaring them. Nothing was saved.",
    retryable: false,
    preferServerMessage: true,
  },
  OPERATOR_ASSERTION_MISMATCH: {
    severity: "auth",
    title: "Approval is for someone else",
    message:
      "The PIN that was entered belongs to a different employee than the one this action names. Nothing was changed.",
    retryable: false,
  },
  OPERATOR_ASSERTION_UNAVAILABLE: {
    severity: "service",
    title: "PIN approval unavailable",
    message: "PIN approvals can’t be issued right now. Nothing was changed.",
    retryable: true,
    nextSteps: ["Try again in a few minutes. If it keeps happening, contact OrderUp support."],
  },
  INVALID_PIN: {
    severity: "auth",
    title: "Incorrect PIN",
    message: "That PIN didn’t match an employee at this location.",
    retryable: true,
  },
  ACCOUNT_LOCKED: {
    severity: "auth",
    title: "Account locked",
    message:
      "This employee’s PIN is temporarily locked after repeated incorrect attempts.",
    retryable: false,
    nextSteps: ["Wait for the lock to expire, or ask a manager to unlock the account."],
  },
  TOO_MANY_ATTEMPTS: {
    severity: "auth",
    title: "Too many incorrect PINs",
    message:
      "Too many incorrect PINs were entered on this device. It will accept PINs again shortly.",
    retryable: true,
  },
  // An authorization lookup FAILED (as opposed to refusing). Retryable by
  // design: the POS and gateway retry on it instead of reading it as a decline.
  PERMISSION_CHECK_UNAVAILABLE: {
    severity: "service",
    title: "Couldn’t check access",
    message:
      "We couldn’t confirm access just now, so nothing was done. This is usually brief.",
    retryable: true,
    nextSteps: ["Try again in a few seconds."],
  },
  NotFoundError: {
    severity: "user_input",
    title: "Not found",
    message: "We couldn’t find what you were looking for.",
    retryable: false,
  },
  // Invitation accept (identity.letsorderup.com/invite/accept). The API
  // flattens every accept failure to this one code; without a catalog entry
  // it fell through to the generic "check your input" copy, which told a
  // brand-new invitee nothing (prod 2026-08-01).
  ACCEPT_FAILED: {
    severity: "user_input",
    title: "Couldn’t accept the invitation",
    message:
      "Something went wrong while accepting this invitation. Try again — if it keeps failing, ask the business to re-send the invite.",
    retryable: true,
  },
  // The accept route now answers with WHY (InvitationAcceptErrorCode in
  // @order-up/models), so the page can tell a new hire something they can act
  // on, in their own language (errors/locales). ACCEPT_FAILED stays for the
  // unexpected.
  INVITATION_NOT_FOUND: {
    severity: "user_input",
    title: "Invitation not found",
    message:
      "We couldn’t find this invitation. Check that you opened the newest invitation email, or ask the business to send a new one.",
    retryable: false,
  },
  INVITATION_ALREADY_ACCEPTED: {
    severity: "user_input",
    title: "Invitation already accepted",
    message: "This invitation has already been accepted. Sign in to continue.",
    retryable: false,
  },
  INVITATION_REVOKED: {
    severity: "user_input",
    title: "Invitation cancelled",
    message: "The business cancelled this invitation. Ask them to send a new one if you still need access.",
    retryable: false,
  },
  INVITATION_EXPIRED: {
    severity: "user_input",
    title: "Invitation expired",
    message: "This invitation has expired. Ask the business to send you a new one.",
    retryable: false,
  },
  INVITATION_DECLINED: {
    severity: "user_input",
    title: "Invitation declined",
    message: "This invitation was declined. Ask the business to send a new one if you changed your mind.",
    retryable: false,
  },
  INVITATION_ACCOUNT_NOT_FOUND: {
    severity: "service",
    title: "Your account isn’t ready yet",
    message: "Your account is still being set up. Wait a moment and try again.",
    retryable: true,
  },
  INVITATION_EMAIL_MISMATCH: {
    severity: "user_input",
    title: "Different email address",
    message:
      "This invitation was sent to a different email address. Sign in with the account for that address to accept it.",
    retryable: false,
  },
  INVITATION_EMPLOYEE_NOT_FOUND: {
    severity: "user_input",
    title: "Employee record not found",
    message: "The business no longer has an employee record for this invitation. Ask them to send a new one.",
    retryable: false,
  },
  INVITATION_EMPLOYEE_ALREADY_LINKED: {
    severity: "user_input",
    title: "Already linked to another account",
    message:
      "This employee record is already linked to a different account. Sign in with that account, or ask the business for help.",
    retryable: false,
  },
  ACCEPTING_UID_MISMATCH: {
    severity: "auth",
    title: "Signed in as someone else",
    message: "You can only accept an invitation for the account you’re signed in with. Sign out and try again.",
    retryable: false,
  },
  ValidationError: {
    severity: "user_input",
    title: "Check your input",
    message: "Please check your input and try again.",
    retryable: true,
  },
  VALIDATION_ERROR: {
    severity: "user_input",
    title: "Check your input",
    message: "Please check your input and try again.",
    retryable: true,
  },
  JOB_RATE_INVALID: {
    severity: "user_input",
    title: "Check the job rates",
    message:
      "Each job rate needs a job and an hourly rate above zero. Remove a job's rate by deleting its row, then try again.",
    retryable: true,
  },
  LOCATION_FIELD_MISMATCH: {
    severity: "user_input",
    title: "Two different locations",
    message: "This request named two different locations. Send one location and try again.",
    retryable: false,
  },
  // ── A REP'S OWN MAILBOX (CRM rep workspace 2 §3.1) ─────────────────────────
  // The API verifies the login before storing it, and the rep's next step
  // depends on WHICH refusal it was — a wrong app password, a wrong server
  // name, a blocked port — so the server's sentence (which names the host and
  // port, never the password) wins over any generic copy.
  LEAD_MAILBOX_LOGIN_REFUSED: {
    severity: "user_input",
    title: "The mail server refused the login",
    message: "The mail server refused the username or password.",
    retryable: true,
    preferServerMessage: true,
    nextSteps: ["Check the username, and use an app password rather than your normal password."],
  },
  LEAD_MAILBOX_UNREACHABLE: {
    severity: "user_input",
    title: "Could not reach the mail server",
    message: "Could not reach the mail server. Check the server name and port.",
    retryable: true,
    preferServerMessage: true,
  },
  LEAD_MAILBOX_HOST_NOT_PUBLIC: {
    severity: "user_input",
    title: "That is not a public mail server",
    message: "That server is not a public mail server.",
    retryable: true,
    preferServerMessage: true,
  },
  LEAD_MAILBOX_PORT_NOT_ALLOWED: {
    severity: "user_input",
    title: "That port is not allowed",
    message: "Use IMAP port 993 or 143 and SMTP port 465 or 587.",
    retryable: true,
    preferServerMessage: true,
  },
  LEAD_MAILBOX_SETTINGS_REQUIRED: {
    severity: "user_input",
    title: "Some server settings are missing",
    message: "Fill in the IMAP and SMTP server settings.",
    retryable: true,
    preferServerMessage: true,
  },
  LEAD_MAILBOX_SETTINGS_INVALID: {
    severity: "user_input",
    title: "Those settings are not allowed",
    message: "Those mail server settings are not allowed. Check the ports and security.",
    retryable: true,
    preferServerMessage: true,
  },
  LEAD_MAILBOX_VAULT_UNAVAILABLE: {
    severity: "service",
    title: "Could not save your mailbox",
    message: "Secure storage is unavailable right now, so your mailbox has not been saved. Try again in a few minutes.",
    retryable: true,
  },
  // ── LEAD AUTO-OUTREACH (spec 2026-10-12) ───────────────────────────────────
  // The Plays data is not in this database yet: the migration has not reached
  // it. Not a failure of the page and nothing the rep can fix; it clears once
  // the data release arrives.
  LEAD_OUTREACH_SCHEMA_NOT_DEPLOYED: {
    severity: "service",
    title: "Plays are not available yet",
    message: "Plays are not set up in this environment yet. They will appear once the latest data update is released.",
    retryable: true,
  },
  // ── CADENCES (CRM rep workspace 2 §5) ──────────────────────────────────────
  // Each refusal says what to do next — which step is out of order, why this
  // lead cannot start a cadence — so the server's sentence wins. None is
  // retryable: the same request meets the same rule.
  LEAD_CADENCE_ALREADY_ENROLLED: {
    severity: "user_input",
    title: "Already in a cadence",
    message: "This lead is already in a cadence.",
    retryable: false,
    preferServerMessage: true,
  },
  LEAD_CADENCE_STAGE_CLOSED: {
    severity: "user_input",
    title: "This lead cannot start a cadence",
    message: "A lead that is won, lost or in nurture cannot start a cadence.",
    retryable: false,
    preferServerMessage: true,
  },
  LEAD_CADENCE_DO_NOT_CONTACT: {
    severity: "user_input",
    title: "Do not contact",
    message: "This lead is marked do not contact.",
    retryable: false,
    preferServerMessage: true,
  },
  LEAD_CADENCE_SUPPRESSED: {
    severity: "user_input",
    title: "Email suppressed",
    message: "That contact's email address is suppressed.",
    retryable: false,
    preferServerMessage: true,
  },
  LEAD_CADENCE_CONTACT_EMAIL_REQUIRED: {
    severity: "user_input",
    title: "Pick a contact with an email",
    message: "This cadence has an email step, so the contact needs an email address.",
    retryable: false,
    preferServerMessage: true,
  },
  LEAD_CADENCE_NAME_TAKEN: {
    severity: "user_input",
    title: "Name already used",
    message: "A cadence with that name already exists.",
    retryable: false,
    preferServerMessage: true,
  },
  LEAD_CADENCE_ARCHIVED: {
    severity: "user_input",
    title: "Cadence archived",
    message: "That cadence has been archived.",
    retryable: false,
    preferServerMessage: true,
  },
  LEAD_CADENCE_OFFSET_ORDER: {
    severity: "user_input",
    title: "Steps out of order",
    message: "A step is scheduled before the step above it.",
    retryable: false,
    preferServerMessage: true,
  },
  LEAD_CADENCE_STEP_INVALID: {
    severity: "user_input",
    title: "Check the steps",
    message: "A step needs a day from 0 to 365 and a kind.",
    retryable: false,
    preferServerMessage: true,
  },
  LEAD_CADENCE_TEMPLATE_KIND: {
    severity: "user_input",
    title: "Template on the wrong step",
    message: "Only an email step can use a template.",
    retryable: false,
    preferServerMessage: true,
  },
  LEAD_CADENCE_TEMPLATE_NOT_FOUND: {
    severity: "user_input",
    title: "Template not found",
    message: "A step uses a template that does not exist or is archived.",
    retryable: false,
    preferServerMessage: true,
  },
  LEAD_EMAIL_TASK_INVALID: {
    severity: "user_input",
    title: "That email step is closed",
    message: "That email step is not open on this lead any more.",
    retryable: false,
    preferServerMessage: true,
  },
  // ── COMPOSE FROM THE LEAD (CRM rep workspace 2 §4) ─────────────────────────
  // Every refusal names the rep's way out — which merge field is empty, why
  // clearOutreach said no, where the postal address is set — so the server's
  // sentence wins. A clearance refusal is NOT retryable: pressing Send again
  // meets the same suppression, quiet hours or frequency cap.
  OUTREACH_REFUSED: {
    severity: "user_input",
    title: "This contact cannot be emailed right now",
    message: "The outreach rules refused this email.",
    retryable: false,
    preferServerMessage: true,
  },
  LEAD_EMAIL_MAILBOX_REQUIRED: {
    severity: "user_input",
    title: "Connect your mailbox first",
    message: "Connect your mailbox first, then send.",
    retryable: false,
    preferServerMessage: true,
    nextSteps: ["Open Mailbox settings and connect your own mailbox."],
  },
  LEAD_EMAIL_MAILBOX_PAUSED: {
    severity: "user_input",
    title: "Your mailbox is paused",
    message: "Your mailbox is paused after repeated login failures. Reconnect it, then send.",
    retryable: false,
    preferServerMessage: true,
  },
  LEAD_EMAIL_POSTAL_ADDRESS_REQUIRED: {
    severity: "user_input",
    title: "Postal address missing",
    message: "Add the company postal address in Pipeline settings before sending.",
    retryable: false,
    preferServerMessage: true,
  },
  LEAD_EMAIL_MERGE_FIELD_EMPTY: {
    severity: "user_input",
    title: "A merge field is empty",
    message: "A merge field has no value on this lead.",
    retryable: false,
    preferServerMessage: true,
  },
  LEAD_EMAIL_MERGE_FIELD_UNKNOWN: {
    severity: "user_input",
    title: "Unknown merge field",
    message: "The email uses a merge field that does not exist.",
    retryable: false,
    preferServerMessage: true,
  },
  LEAD_EMAIL_ALREADY_SENDING: {
    severity: "user_input",
    title: "Already sending",
    message: "This email is already sending.",
    retryable: false,
    preferServerMessage: true,
  },
  LEAD_EMAIL_SEND_FAILED_NEW_KEY_REQUIRED: {
    severity: "user_input",
    title: "That send failed",
    message: "That send failed. Reopen the compose window to try again.",
    retryable: false,
    preferServerMessage: true,
  },
  LEAD_EMAIL_SMTP_AUTH: {
    severity: "user_input",
    title: "Your mail server refused the password",
    message: "Your mail server refused the password. Reconnect your mailbox.",
    retryable: false,
    preferServerMessage: true,
  },
  LEAD_EMAIL_SMTP_REJECTED: {
    severity: "service",
    title: "The email was not accepted",
    message: "Your mail server did not accept the email.",
    retryable: false,
    preferServerMessage: true,
  },
  LEAD_EMAIL_SMTP_UNREACHABLE: {
    severity: "service",
    title: "The email was not sent",
    message: "Could not reach your mail server, so the email was not sent.",
    retryable: true,
    preferServerMessage: true,
  },
  // ── SALES AI (CRM rep workspace 2 §6) ───────────────────────────────────────
  // Every model failed, or the 20 s budget ran out. The server's sentence is
  // NOT preferred: its details carry model ids and gateway statuses, which
  // are noise to a rep. Retryable — the next press may well answer.
  SALES_AI_UNAVAILABLE: {
    severity: "service",
    title: "The sales assistant is not answering",
    message: "The sales assistant is not answering right now. Try again in a minute, or write it yourself.",
    retryable: true,
  },
  // ── DIALER (CRM rep workspace 2 §7) ────────────────────────────────────────
  // Each tells the rep what to do next. The server's sentence is preferred
  // where it names the fix; "not configured" is fixed copy — a rep cannot set
  // TWILIO_* and should just call from their phone.
  LEAD_CALLING_NOT_CONFIGURED: {
    severity: "service",
    title: "Click-to-call is not set up",
    message: "Click-to-call is not set up yet. Call from your phone instead.",
    retryable: false,
  },
  LEAD_CALLING_NO_CALLBACK_PHONE: {
    severity: "user_input",
    title: "Save your phone number",
    message: "Click-to-call rings your phone first. Save your phone number under Mailbox & calling.",
    retryable: false,
    preferServerMessage: true,
  },
  LEAD_CALL_IN_PROGRESS: {
    severity: "user_input",
    title: "A call is already ringing",
    message: "You already have a call ringing. Answer it, or wait a minute and try again.",
    retryable: false,
    preferServerMessage: true,
  },
  LEAD_CALL_NUMBER_INVALID: {
    severity: "user_input",
    title: "Not a US number",
    message: "Only US numbers can be called from the CRM.",
    retryable: false,
  },
  LEAD_CALL_START_FAILED: {
    severity: "service",
    title: "The call did not go through",
    message: "Twilio could not place the call. Try again, or call from your phone.",
    retryable: true,
    preferServerMessage: true,
  },
  LEAD_TEMPLATE_DUPLICATE: {
    severity: "user_input",
    title: "Name already used",
    message: "A template with that name already exists.",
    retryable: false,
    preferServerMessage: true,
  },
  ConflictError: {
    severity: "user_input",
    title: "Conflict",
    message: "This conflicts with existing data.",
    retryable: false,
  },
  RateLimitError: {
    severity: "service",
    title: "Slow down",
    message: "Too many requests. Please try again later.",
    retryable: false,
  },
  RATE_LIMIT_EXCEEDED: {
    severity: "service",
    title: "Slow down",
    message: "Too many requests. Please try again later.",
    retryable: false,
  },
  NetworkError: {
    severity: "service",
    title: "Connection problem",
    message:
      "Network connection failed. Please check your internet connection.",
    retryable: true,
  },
  TimeoutError: {
    severity: "service",
    title: "Timed out",
    message: "That took too long. Please try again.",
    retryable: true,
  },
  // The API host answered with a deployment-protection / proxy auth wall (an
  // HTML page) instead of the app's JSON envelope — the request never reached
  // our server. Emitted by the client `createApiClient` factory when it detects
  // a non-JSON 401/403 (e.g. Vercel Deployment Protection on a preview/staging
  // alias whose `x-vercel-protection-bypass` secret is missing or expired).
  // This is an environment-config issue, NOT a sign-in/session failure — keeping
  // it a distinct code stops it masquerading as AUTH_SESSION_INVALID /
  // STALE_AUTH_REAUTH_REQUIRED and sending users into a pointless sign-out loop.
  DEPLOYMENT_PROTECTED: {
    severity: "service",
    title: "This environment is protected",
    message:
      "The app couldn’t reach the API because this environment is behind deployment protection. This is a setup issue with the environment, not your account.",
    retryable: true,
    nextSteps: [
      "Reload the page — that re-establishes the environment’s access and is often enough.",
      "If it keeps happening, the deployment-protection bypass for this environment needs to be configured by the platform team. Signing out will NOT fix it.",
    ],
  },

  // ── ACH (bank-account) PAYMENTS — POA initiative ────────────────────────────
  // BILLING_ACH_* codes are raised by the public pay routes, the POA
  // authorization service, and the debit engine; BOOKS_ACH_* by the
  // recurring-autopay (S2) surfaces. Copy never mentions SEC codes,
  // disclosures, or other Nacha internals — it says what the payer or
  // operator can actually do next.
  BILLING_ACH_NOT_ENABLED: {
    severity: "user_input",
    title: "Bank payments not accepted",
    message:
      "This business doesn’t accept bank-account (ACH) payments. Please pay by card instead.",
    retryable: false,
  },
  BILLING_ACH_AMOUNT_MISMATCH: {
    severity: "user_input",
    title: "Amount must match the balance due",
    message:
      "A bank payment has to match the balance due exactly — partial amounts and tips aren’t supported for bank transfers.",
    retryable: true,
    nextSteps: ["Refresh the page to load the current balance, then try again."],
  },
  BILLING_ACH_DECLINED: {
    severity: "user_input",
    title: "Bank payment declined",
    message:
      "The bank did not accept this payment. Check the routing and account numbers, try a different account, or pay by card.",
    retryable: true,
  },
  BILLING_ACH_SEC_POLICY_UNSET: {
    severity: "service",
    title: "Bank payments not available yet",
    message:
      "Bank-account payments aren’t fully set up for this business yet. Please pay by card, or check back later.",
    retryable: false,
  },
  // Raised when the deployment cannot tell OrderUp HQ's own Books apart from a
  // restaurant's (the bootstrap-written ORDERUP_HQ_* pair is missing), so the
  // authorization classification for a bank debit cannot be determined.
  //
  // It is deliberately raised by STATUS READS as well as by writes. Those
  // reads report whether a mandate is armed, and "not armed" is a claim that
  // no money is moving — a claim an unresolvable HQ identity cannot support,
  // because the autopay job keeps debiting regardless. Refusing loudly is the
  // only answer that is not a guess.
  BILLING_ACH_HQ_SCOPE_UNRESOLVED: {
    severity: "service",
    title: "Bank payments aren’t configured on this deployment",
    message:
      "Bank-account (ACH) payments can’t be set up or reported on here yet, because this deployment hasn’t finished its billing setup. Nothing you can do on this page will change it.",
    retryable: false,
    nextSteps: [
      "Contact support — this needs a platform change, not a retry.",
      "Do not assume automatic bank payments are switched off: this page cannot tell, so check with support before relying on it.",
    ],
  },
  BILLING_ACH_ACKNOWLEDGMENT_UNRESOLVED: {
    severity: "user_input",
    title: "Authorization text incomplete",
    message:
      "The authorization text still has blanks in it, so it can’t be accepted. Reload the page and try again; if it keeps happening, contact support.",
    retryable: false,
  },
  BILLING_ACH_DISCLOSURE_NOT_FOUND: {
    severity: "service",
    title: "Bank payments not available yet",
    message:
      "The bank-payment authorization terms aren’t published yet, so bank payments can’t be accepted. Please pay by card, or check back later.",
    retryable: false,
  },
  BILLING_ACH_ACKNOWLEDGMENT_MISMATCH: {
    severity: "user_input",
    title: "Authorization terms changed",
    message:
      "The debit authorization terms changed since this page loaded, so the payment wasn’t started. Refresh the page, review the authorization again, and re-accept.",
    retryable: true,
    nextSteps: ["Refresh the page and accept the updated authorization."],
  },
  // TRX — or OrderUp's own pre-wire check — refused OrderUp's REQUEST (an
  // X-code, the merchant setup, a duplicate): nothing reached the bank. Never
  // the payer's decline, and never the vendor's text (Oct 17 2026: an X0 on
  // our own body was emailed to a customer as "Validation Error").
  BILLING_ACH_INTEGRATION_FAULT: {
    severity: "service",
    title: "Bank payment not sent",
    message:
      "We could not send this bank payment because of a problem on our side. You were not charged. Please try again later, or pay by card.",
    retryable: true,
  },
  // A stored bank account is missing what the processor requires on every
  // bank debit (checking/savings, or the holder's name) — saved before the
  // bank forms asked. Nothing was sent.
  BILLING_ACH_STORED_ACCOUNT_INCOMPLETE: {
    severity: "user_input",
    title: "Bank account needs to be added again",
    message:
      "This saved bank account is missing details the payment processor needs (whether it is checking or savings, or the account holder's name), so it can't be debited. Add the bank account again.",
    retryable: false,
  },
  BILLING_ACH_DEBIT_UNRESOLVED: {
    severity: "service",
    title: "Bank payment outcome unconfirmed",
    message:
      "We could not confirm whether a bank payment on this link went through. To avoid a duplicate charge, no new attempt was started — contact the business to confirm before paying again.",
    retryable: false,
  },
  PAYMENT_NEEDS_RECONCILIATION: {
    severity: "service",
    title: "Previous payment awaiting confirmation",
    message:
      "A previous payment on this link is awaiting confirmation. To avoid a duplicate charge, please do not pay again — contact the business to confirm.",
    retryable: false,
  },
  BILLING_ACH_AUTHORIZATION_REVOKED: {
    severity: "user_input",
    title: "Authorization was revoked",
    message:
      "The bank-debit authorization for this payment was revoked, so it can’t be charged. A new authorization is needed before another bank payment.",
    retryable: false,
  },
  // The debit engine's ACCOUNT gate, which fails CLOSED. Distinct from every
  // authorization refusal above: those say "nobody agreed to this", these say
  // "nothing ever proved this bank account exists". Never checking is not
  // permission, and the first debit is the wrong place to find out.
  // "Verify again" while the same account's check is still running (a double
  // click, or two tabs) — the first answer is on its way.
  BILLING_ACH_ACCOUNT_CHECK_IN_PROGRESS: {
    severity: "user_input",
    title: "Already verifying this account",
    message:
      "This bank account is already being verified with the payment processor. Wait a moment, then refresh.",
    retryable: true,
    nextSteps: ["Wait a moment, then refresh the page."],
  },
  BILLING_ACH_ACCOUNT_NOT_VALIDATED: {
    severity: "user_input",
    title: "Bank account not verified",
    message:
      "This bank account has never been verified with the payment processor, so no bank payment can be taken from it. Use “Verify again” on the saved account to clear it.",
    retryable: true,
    nextSteps: [
      // The account is already on file, so the check re-runs against the
      // saved account — nobody has to re-read numbers off a cheque for a
      // check that never got an answer.
      "Use “Verify again” on the saved bank account.",
    ],
  },
  // Bank details are typed only into TRX's hosted PayPage frames; the API
  // takes TRX's ciphertext and refuses a body that carries account numbers.
  BILLING_ACH_RAW_BANK_NUMBERS_REFUSED: {
    severity: "user_input",
    title: "Use the secure bank form",
    message:
      "Bank details must be entered in the secure bank form. Nothing was saved. Reload the page and enter the account again.",
    retryable: false,
    nextSteps: ["Reload the page and enter the bank account in the secure form."],
  },
  // ── The shared bank-account store (billing_state.bank_accounts) ─────────
  // The debit engine's HARD BINDING: a stored instrument must be the account
  // the payer authorized, on the merchant that vaulted it.
  BILLING_ACH_ACCOUNT_MISMATCH: {
    severity: "user_input",
    title: "Different bank account",
    message:
      "This payment was set up for a different bank account than the one being charged, so it was not taken. Authorize the account you want to pay from.",
    retryable: false,
  },
  BILLING_ACH_ACCOUNT_UNBOUND: {
    severity: "user_input",
    title: "No bank account linked",
    message:
      "This bank-payment authorization isn’t linked to a bank account yet, so nothing can be taken under it. Add the bank account to finish setting it up.",
    retryable: false,
    nextSteps: ["Add or choose the bank account for this authorization."],
  },
  BILLING_ACH_BANK_ACCOUNT_NOT_ACTIVE: {
    severity: "user_input",
    title: "Bank account can’t be used",
    message:
      "The bank account behind this authorization was removed or returned by the bank, so no payment can be taken from it. Add a different bank account.",
    retryable: false,
    nextSteps: ["Add a different bank account."],
  },
  BILLING_ACH_VAULT_MERCHANT_MISMATCH: {
    severity: "service",
    title: "Payment routed to the wrong account",
    message:
      "This bank payment was sent to a merchant that doesn’t hold the bank account, so it was stopped before any money moved. Our team has been notified.",
    retryable: false,
  },
  BILLING_ACH_BANK_ACCOUNT_MISMATCH: {
    severity: "user_input",
    title: "Bank account doesn’t match",
    message:
      "The bank account chosen doesn’t match the one this authorization names. Choose the matching account, or authorize the new one.",
    retryable: false,
  },
  BILLING_ACH_BANK_ACCOUNT_HOLDER_MISMATCH: {
    severity: "user_input",
    title: "Bank account belongs to someone else",
    message:
      "That bank account isn’t on file for the business or customer being charged. Choose one of their own accounts.",
    retryable: false,
  },
  BILLING_ACH_BANK_ACCOUNT_RECEIVER_MISMATCH: {
    severity: "user_input",
    title: "Business or personal account mismatch",
    message:
      "The signed authorization was for a different kind of account (business or personal) than this one. Use a matching account, or sign a new authorization.",
    retryable: false,
  },
  BILLING_ACH_BANK_ACCOUNT_ALREADY_BOUND: {
    severity: "user_input",
    title: "Authorization already has an account",
    message:
      "This authorization is already linked to a bank account. To use a different one, add it and authorize it.",
    retryable: false,
  },
  BILLING_BANK_ACCOUNT_NOT_FOUND: {
    severity: "user_input",
    title: "Bank account not found",
    message: "That bank account isn’t on file. Refresh and try again.",
    retryable: false,
  },
  BILLING_BANK_ACCOUNT_DUPLICATE: {
    severity: "user_input",
    title: "Bank account already on file",
    message:
      "That bank account is already on file for someone else, or was removed. Add it again with fresh details if you meant to reuse it.",
    retryable: false,
  },
  BILLING_BANK_ACCOUNT_NOT_ACTIVE: {
    severity: "user_input",
    title: "Bank account can’t be used",
    message:
      "That bank account was removed or returned by the bank, so it can’t be used. Add a different bank account.",
    retryable: false,
  },
  BILLING_BANK_ACCOUNT_DEBIT_IN_FLIGHT: {
    severity: "user_input",
    title: "Payment in progress",
    message:
      "A payment from this bank account is being processed right now. Try removing it again once the payment finishes.",
    retryable: true,
    preferServerMessage: true,
  },
  BILLING_ACH_BANK_ACCOUNT_REQUIRED: {
    severity: "user_input",
    title: "Choose a stored bank account",
    message:
      "This authorization has to name a stored bank account, because its business or personal use decides how each debit is filed. Choose one, or add the bank account first.",
    retryable: true,
    preferServerMessage: true,
  },
  BILLING_BANK_ACCOUNT_USE_REQUIRED: {
    severity: "user_input",
    title: "Business or personal?",
    message: "Tell us whether this is a business or a personal bank account.",
    retryable: true,
  },
  // The bank needs checking/savings to check a newly entered account (TRX
  // `DdaType`, required on the vault Verify — UAT 2026-10-16). Nothing was
  // sent, so answering and submitting again is the whole fix.
  BILLING_BANK_ACCOUNT_TYPE_REQUIRED: {
    severity: "user_input",
    title: "Checking or savings?",
    message: "Tell us whether this is a checking or a savings account. Nothing was saved or charged.",
    retryable: true,
  },
  BILLING_BANK_ACCOUNT_HOLDER_INVALID: {
    severity: "user_input",
    title: "Bank account owner missing",
    message: "We couldn’t tell who owns this bank account. Refresh and try again.",
    retryable: false,
  },
  BILLING_BANK_ACCOUNT_HOLDER_NOT_FOUND: {
    severity: "user_input",
    title: "Customer not found",
    message: "The customer this bank account belongs to isn’t on file any more.",
    retryable: false,
  },
  BILLING_BANK_ACCOUNT_HQ_CUSTOMER_UNLINKED: {
    severity: "user_input",
    title: "Customer isn’t linked to a location",
    message:
      "OrderUp debits a location from that location’s own bank account, and this customer isn’t linked to a location yet. Link the customer to its location first.",
    retryable: false,
  },
  BILLING_BANK_ACCOUNT_HQ_CUSTOMER_SEVERAL_LOCATIONS: {
    severity: "user_input",
    title: "Each location has its own bank account",
    message:
      "This business has more than one location, and OrderUp debits each location from its own bank account. Set up each location’s account from Restaurants → the location → ACH authorization.",
    retryable: false,
  },
  BILLING_BANK_ACCOUNT_REPLACE_NEEDS_HQ_ACK: {
    severity: "user_input",
    title: "Second authorization needed",
    message:
      "The account you’re replacing also pays OrderUp invoices. Accept that authorization for the new account too, then try again.",
    retryable: true,
  },
  BILLING_BANK_ACCOUNT_REPLACE_HQ_ACK_STALE: {
    severity: "user_input",
    title: "Authorizations changed",
    message:
      "The OrderUp invoice authorizations on the account you’re replacing changed while you were here. Reload the page and accept them again.",
    retryable: true,
  },
  BILLING_BANK_ACCOUNT_SAVE_UNAVAILABLE: {
    severity: "user_input",
    title: "Saved accounts aren’t available here",
    message: "Saved bank accounts aren’t available for this payment. Enter your bank account to pay.",
    retryable: false,
  },
  BILLING_BANK_ACCOUNT_STORAGE_CONSENT_MISMATCH: {
    severity: "user_input",
    title: "Wording changed",
    message: "The “save this account” wording has changed. Reload the page and try again.",
    retryable: true,
  },
  BILLING_BANK_ACCOUNT_STORAGE_CONSENT_NOT_RECORDED: {
    severity: "service",
    title: "Account not saved",
    message:
      "Your bank account couldn’t be saved, so nothing was charged. Try again, or pay without saving it.",
    retryable: true,
  },
  BILLING_BANK_ACCOUNT_VAULT_MISMATCH: {
    severity: "service",
    title: "Bank account couldn’t be saved",
    message: "This bank account couldn’t be saved for this business. Our team has been notified.",
    retryable: false,
  },
  BILLING_BANK_ACCOUNT_IMMUTABLE: {
    severity: "user_input",
    title: "Bank account can’t be changed",
    message:
      "Bank account details can’t be edited once saved. Add the account again with the new details.",
    retryable: false,
  },
  BILLING_BANK_ACCOUNT_STORE_FAILED: {
    severity: "service",
    title: "Bank accounts unavailable",
    message: "We couldn’t reach saved bank accounts just now. Nothing was changed — please try again.",
    retryable: true,
  },
  BILLING_ACH_ACCOUNT_VALIDATION_FAILED: {
    severity: "user_input",
    title: "Bank account was rejected",
    message:
      "The payment processor could not verify this bank account, so no bank payment can be taken from it. Enter a different account, or re-check the routing and account numbers.",
    retryable: false,
    nextSteps: [
      "Confirm the routing and account numbers with the account holder, then add the account again.",
    ],
  },
  // Same §4.2 rule as BOOKS_ACH_ACCOUNT_HOLDER_NAME_REQUIRED, on the SaaS
  // plane: the account holder is the subscriber, and its billing name is what
  // the account check puts on the wire.
  BILLING_ACH_ACCOUNT_HOLDER_NAME_REQUIRED: {
    severity: "user_input",
    title: "Billing name needed",
    message:
      "The bank requires the account holder’s name in Latin letters or numbers, and the billing name on this account has none. Update the billing name, then add the bank account again.",
    retryable: true,
    nextSteps: [
      "Update the billing name using letters A–Z and numbers.",
      "Add the bank account again.",
    ],
  },
  BILLING_ACH_ACCOUNT_HOLDER_LOOKUP_FAILED: {
    severity: "service",
    title: "Couldn’t read the billing name",
    message:
      "We couldn’t read the billing name for this subscription, so the bank-account check didn’t run. Nothing was saved — please try again.",
    retryable: true,
  },
  // ── Bank-account validation: OUR failures, not the account's ──────────────
  //
  // The two above are verdicts about a bank account. Everything here is an
  // infrastructure fault on OUR side of the check, and the distinction is the
  // whole point: a user told "your account was rejected" when Supabase was
  // briefly unreachable will go re-type numbers that were always correct, and
  // the machine reading these codes must retry rather than abandon a mandate.
  // Service severity, retryable, and deliberately outside the
  // ACH_DEBIT_GATE_REFUSAL_CODES allowlist.
  BILLING_ACH_VALIDATION_STORE_FAILED: {
    severity: "service",
    title: "Couldn’t save the account check",
    message:
      "The bank verified this account but we couldn’t record the result, so it wasn’t saved. Nothing was charged — please try again.",
    retryable: true,
  },
  BILLING_ACH_VALIDATION_READ_FAILED: {
    severity: "service",
    title: "Couldn’t look up the account check",
    message:
      "We couldn’t look up the bank-account check for this account, so no bank payment was taken. This is a problem on our side, not with the account — please try again.",
    retryable: true,
  },
  BILLING_ACH_GATE_UNAVAILABLE: {
    severity: "service",
    title: "Bank payment couldn’t start",
    message:
      "We couldn’t confirm this bank payment was authorized because something on our side was unavailable, so nothing was charged. Please try again.",
    retryable: true,
  },
  // The account-validation ledger's own write guards. These can only be
  // reached by a caller sending a shape the ledger refuses — a defect, not a
  // user mistake — so they never invite the user to retype anything.
  BILLING_ACH_VALIDATION_REF_REQUIRED: {
    severity: "service",
    title: "Account check couldn’t be recorded",
    message:
      "The result of the bank-account check couldn’t be recorded because the saved account it belongs to wasn’t identified. Nothing was charged — please contact support if this keeps happening.",
    retryable: false,
  },
  BILLING_ACH_VALIDATION_VERDICT_INVALID: {
    severity: "service",
    title: "Account check couldn’t be recorded",
    message:
      "The result of the bank-account check couldn’t be recorded because it wasn’t a final answer. Nothing was charged — please try the check again.",
    retryable: true,
  },
  BILLING_ACH_VALIDATION_METHOD_INVALID: {
    severity: "service",
    title: "Account check couldn’t be recorded",
    message:
      "The result of the bank-account check couldn’t be recorded because it didn’t say how the account was checked. Nothing was charged — please contact support if this keeps happening.",
    retryable: false,
  },
  BILLING_ACH_VALIDATION_GRANDFATHER_INVALID: {
    severity: "service",
    title: "Account check couldn’t be recorded",
    message:
      "We couldn’t record how this bank account was cleared for use. Nothing was charged — please contact support if this keeps happening.",
    retryable: false,
  },
  BILLING_ACH_ALREADY_REVOKED: {
    severity: "user_input",
    title: "Already revoked",
    message:
      "This bank-debit authorization has already been revoked — no further action is needed.",
    retryable: false,
  },
  BOOKS_ACH_MANDATE_REQUIRED: {
    severity: "user_input",
    title: "Bank authorization needed",
    message:
      "This schedule needs a bank authorization before autopay can start.",
    retryable: false,
    nextSteps: [
      "Ask the customer to authorize bank debits, then link the authorization to this schedule.",
    ],
  },
  // "Charge saved bank account" refusals (POST /books/invoices/:id/charge-ach):
  // one live bank debit per invoice.
  BOOKS_ACH_DEBIT_IN_FLIGHT: {
    severity: "user_input",
    title: "Bank payment already pending",
    message:
      "A bank debit for this invoice is already queued or being sent. It goes out on its own — there’s nothing to charge again.",
    retryable: false,
  },
  BOOKS_ACH_DEBIT_ALREADY_SENT: {
    severity: "user_input",
    title: "Bank payment already sent",
    message:
      "A bank debit for this invoice was already sent to the bank. It posts once it clears; charge again only if the bank returns it.",
    retryable: false,
  },
  BOOKS_ACH_DEBIT_UNDER_REVIEW: {
    severity: "user_input",
    title: "Earlier bank payment under review",
    message:
      "The processor’s answer for an earlier bank debit on this invoice couldn’t be confirmed, and OrderUp is reconciling it. Charging again could debit the customer twice.",
    retryable: false,
    nextSteps: [
      "Record the payment another way, or contact OrderUp support to resolve the earlier debit.",
    ],
  },
  BOOKS_ACH_DEBIT_RETURN_NOT_RETRYABLE: {
    severity: "user_input",
    title: "The bank won’t take this debit again",
    message:
      "The bank returned this invoice’s debit for a reason that doesn’t allow debiting the account for it again.",
    retryable: false,
    nextSteps: [
      "Collect the invoice another way, or set up bank autopay with new bank details.",
    ],
  },
  // "Pay by bank now" (POST /books/invoices/:id/pay-by-bank and the cancel of
  // a queued one-time debit): a one-time bank payment the customer authorizes
  // in person.
  BOOKS_ACH_PAY_BY_BANK_AMOUNT_INVALID: {
    severity: "user_input",
    title: "Check the amount",
    message:
      "A bank payment must be more than $0.00 and no more than the invoice’s open balance.",
    retryable: true,
  },
  BOOKS_ACH_PAY_BY_BANK_ACCOUNT_UNVERIFIED: {
    severity: "user_input",
    title: "Bank account not verified yet",
    message:
      "The payment processor couldn’t confirm this bank account yet, so no payment was set up. The account was saved for the customer.",
    retryable: true,
    nextSteps: [
      "Use “Verify again” on the customer’s bank account, then take the payment from it.",
    ],
  },
  BOOKS_ACH_PAY_BY_BANK_UNAVAILABLE: {
    severity: "user_input",
    title: "Pay by bank isn’t available here",
    message:
      "A one-time bank payment can’t be taken on this invoice right now. Nothing was charged.",
    retryable: false,
    nextSteps: ["Record the payment another way, or reload the page and try again."],
  },
  // "Pay by check" (POST /books/invoices/:id/pay-by-check and its photo,
  // paper-check and cancel routes): a photographed check becomes a one-time
  // bank payment — on the check alone for a business check, with the
  // customer's approval for a personal one.
  BOOKS_ACH_PAY_BY_CHECK_DISABLED: {
    severity: "user_input",
    title: "Pay by check isn’t on yet",
    message: "Converting checks to bank payments isn’t turned on for this business yet.",
    retryable: false,
    nextSteps: ["Deposit the check, or record the payment another way."],
  },
  BOOKS_ACH_PAY_BY_CHECK_NOT_DEPLOYED: {
    severity: "service",
    title: "Pay by check isn’t available yet",
    message: "Converting checks isn’t switched on for this server yet. Nothing was charged.",
    retryable: false,
    nextSteps: ["Deposit the check, or record the payment another way."],
  },
  BOOKS_ACH_PAY_BY_CHECK_UNAVAILABLE: {
    severity: "service",
    title: "Couldn’t check whether a check can be converted",
    message: "The checks Pay by check needs couldn’t be read right now.",
    retryable: true,
    nextSteps: ["Reload the page and try again."],
  },
  BOOKS_ACH_PAY_BY_CHECK_ACCOUNT_UNVERIFIED: {
    severity: "user_input",
    title: "Bank account not verified",
    message:
      "The payment processor couldn’t confirm the bank account from this check, so the check was not converted.",
    retryable: true,
    nextSteps: ["Check the routing and account numbers against the check, or deposit the check instead."],
  },
  BOOKS_CHECK_AMOUNT_INVALID: {
    severity: "user_input",
    title: "Check the amount",
    message: "Enter the amount written on the check, more than $0.00.",
    retryable: true,
  },
  BOOKS_CHECK_AMOUNT_OVER_BALANCE: {
    severity: "user_input",
    title: "The check is for more than the balance",
    message: "Only a check for the invoice’s open balance or less can be converted here.",
    retryable: false,
    nextSteps: ["Deposit the check, or return it and ask for one for the balance."],
  },
  BOOKS_CHECK_AMOUNT_OVER_LIMIT: {
    severity: "user_input",
    title: "Check over $25,000",
    message: "A check over $25,000.00 can’t be converted to a bank payment.",
    retryable: false,
    nextSteps: ["Deposit the check instead."],
  },
  BOOKS_CHECK_NUMBER_INVALID: {
    severity: "user_input",
    title: "Enter the check number",
    message: "The check number is digits only, up to 9 of them, exactly as printed on the check.",
    retryable: true,
  },
  BOOKS_CHECK_NOT_ELIGIBLE: {
    severity: "user_input",
    title: "This check can’t be converted",
    message:
      "Every item of the checklist must be confirmed. Third-party checks, money orders, traveler’s, cashier’s and other bank checks, credit-card checks and government checks can’t be converted.",
    retryable: false,
    nextSteps: ["Deposit the check instead."],
  },
  BOOKS_CHECK_ELIGIBILITY_STALE: {
    severity: "user_input",
    title: "The checklist changed",
    message: "The check checklist was updated while the dialog was open.",
    retryable: true,
    nextSteps: ["Reload and confirm the checklist again."],
  },
  BOOKS_CHECK_NOTICE_STALE: {
    severity: "user_input",
    title: "The check notice changed",
    message: "The business’s check-conversion notice changed while the dialog was open.",
    retryable: true,
    nextSteps: ["Reload and try again."],
  },
  BOOKS_CHECK_NOTICE_INVALID: {
    severity: "user_input",
    title: "Check the notice",
    message: "The notice must say that a check may be converted into a one-time electronic payment.",
    retryable: true,
  },
  BOOKS_CHECK_ALREADY_CONVERTED: {
    severity: "user_input",
    title: "This check was already converted",
    message:
      "A check with this account, check number and amount was already converted to a bank payment. A check can’t be converted twice.",
    retryable: false,
    nextSteps: ["Look at the invoice’s converted checks, or return the paper check to the customer."],
  },
  BOOKS_CHECK_IMAGE_NOT_IMAGE: {
    severity: "user_input",
    title: "That file isn’t a photo",
    message: "A check photo must be a JPEG, PNG or HEIC image — the file’s contents decide, not its name.",
    retryable: true,
    nextSteps: ["Take the photo with the camera, or choose a photo of the check."],
  },
  BOOKS_CHECK_IMAGE_TOO_LARGE: {
    severity: "user_input",
    title: "Photo too large",
    message: "A check photo can be at most 15 MB.",
    retryable: true,
  },
  BOOKS_CHECK_IMAGE_EMPTY: {
    severity: "user_input",
    title: "Photo is empty",
    message: "That check photo is empty or cut off.",
    retryable: true,
    nextSteps: ["Take the photo again."],
  },
  BOOKS_CHECK_IMAGE_MISSING: {
    severity: "user_input",
    title: "Photo didn’t finish uploading",
    message: "A check photo didn’t finish uploading.",
    retryable: true,
    nextSteps: ["Take or choose the photo again."],
  },
  BOOKS_CHECK_IMAGE_INVALID: {
    severity: "user_input",
    title: "Upload the photos again",
    message: "The check photos couldn’t be matched to this invoice.",
    retryable: true,
  },
  BOOKS_CHECK_IMAGE_REQUIRED: {
    severity: "user_input",
    title: "Photo of the front required",
    message: "Take a photo of the front of the check.",
    retryable: true,
  },
  BOOKS_CHECK_IMAGE_UPLOAD_UNAVAILABLE: {
    severity: "service",
    title: "Couldn’t start the upload",
    message: "The photo upload couldn’t be started.",
    retryable: true,
    nextSteps: ["Try again in a moment."],
  },
  BOOKS_CHECK_IMAGE_AUDIT_FAILED: {
    severity: "service",
    title: "Photo not opened",
    message: "Every view of a check photo is recorded, and this one couldn’t be, so the photo wasn’t opened.",
    retryable: true,
  },
  BOOKS_CHECK_CONVERSION_NOT_CANCELLABLE: {
    severity: "user_input",
    title: "Cancel the bank payment instead",
    message: "This check’s bank payment is set up, so it’s cancelled by cancelling the bank payment.",
    retryable: false,
  },
  BOOKS_CHECK_CONVERSION_NOT_APPROVABLE: {
    severity: "user_input",
    title: "Already approved or cancelled",
    message: "This check was already approved or cancelled.",
    retryable: false,
  },
  BOOKS_CHECK_CONVERSION_MISMATCH: {
    severity: "user_input",
    title: "Approve the check itself",
    message: "The customer’s approval must be for the check: its bank account and exactly its amount.",
    retryable: false,
  },
  BOOKS_CHECK_PAPER_NOT_CONVERTED: {
    severity: "user_input",
    title: "This check wasn’t converted",
    message: "Only a converted check is destroyed. Return or deposit this paper check instead.",
    retryable: false,
  },
  BILLING_ACH_CHECK_IMAGE_PERSONAL_ACCOUNT: {
    severity: "user_input",
    title: "A personal check needs the customer’s approval",
    message:
      "A personal check isn’t debited on the check alone. The customer approves the payment in person, by phone or by a link, and the check photos are attached to that approval.",
    retryable: false,
  },
  BOOKS_ACH_PAY_BY_BANK_NOT_DEPLOYED: {
    severity: "service",
    title: "Pay by bank isn’t available yet",
    message:
      "One-time bank payments aren’t switched on for this server yet. Nothing was charged.",
    retryable: false,
    nextSteps: ["Record the payment another way, or try again later."],
  },
  // "Pay by bank now" → "Customer is on the phone" (POST
  // /books/invoices/:id/pay-by-bank/phone and its recording upload): a
  // one-time bank payment authorized on a recorded call (Nacha TEL / CCD).
  BOOKS_ACH_PHONE_DISABLED: {
    severity: "user_input",
    title: "Phone payments aren’t on yet",
    message:
      "Taking a bank payment by phone isn’t turned on for this business yet. Nothing was charged.",
    retryable: false,
    nextSteps: ["Send the customer a pay link instead."],
  },
  BOOKS_ACH_PHONE_NOT_DEPLOYED: {
    severity: "service",
    title: "Phone payments aren’t available yet",
    message:
      "Phone authorizations aren’t available on this server yet. Nothing was charged.",
    retryable: false,
    nextSteps: ["Send the customer a pay link instead, or try again later."],
  },
  BOOKS_ACH_PHONE_ATTESTATION_REQUIRED: {
    severity: "user_input",
    title: "Confirm the call",
    message: "Confirm that you read the script exactly and the customer said yes.",
    retryable: true,
  },
  BOOKS_ACH_PHONE_RECORDING_REQUIRED: {
    severity: "user_input",
    title: "Upload the call recording",
    message: "A phone authorization needs the recording of the call. Nothing was charged.",
    retryable: true,
    nextSteps: ["Upload the recording of the call, then submit again."],
  },
  BOOKS_ACH_PHONE_RECORDING_MISSING: {
    severity: "user_input",
    title: "The recording didn’t finish uploading",
    message: "The call recording isn’t there yet, so nothing was charged.",
    retryable: true,
    nextSteps: ["Upload the recording again, then submit."],
  },
  BOOKS_ACH_PHONE_RECORDING_NOT_AUDIO: {
    severity: "user_input",
    title: "That isn’t an audio recording",
    message:
      "Upload the call recording as an M4A, AAC, MP3, WAV, WebM or Ogg audio file.",
    retryable: true,
  },
  BOOKS_ACH_PHONE_RECORDING_TOO_LARGE: {
    severity: "user_input",
    title: "The recording is too large",
    message: "Call recordings can be up to 50 MB.",
    retryable: true,
    nextSteps: ["Export the recording at a lower quality, then upload it again."],
  },
  BOOKS_ACH_PHONE_RECORDING_UPLOAD_UNAVAILABLE: {
    severity: "service",
    title: "Couldn’t start the upload",
    message: "The recording upload couldn’t be started. Nothing was charged.",
    retryable: true,
  },
  BOOKS_ACH_PHONE_RECORDING_UPLOAD_FAILED: {
    severity: "service",
    title: "The upload didn’t finish",
    message: "The call recording didn’t finish uploading.",
    retryable: true,
    nextSteps: ["Check the connection, then upload it again."],
  },
  BOOKS_ACH_PHONE_RECORDING_NOT_FOUND: {
    severity: "user_input",
    title: "Recording not found",
    message: "That call recording isn’t on this invoice.",
    retryable: false,
  },
  BOOKS_ACH_PHONE_RECORDING_AUDIT_FAILED: {
    severity: "service",
    title: "Couldn’t open the recording",
    message:
      "Every play and download of a call recording is logged, and the log couldn’t be written just now, so the recording wasn’t opened.",
    retryable: true,
  },
  BILLING_ACH_PHONE_ACCOUNT_REQUIRED: {
    severity: "user_input",
    title: "Choose an account on file",
    message:
      "A phone authorization can only use a verified bank account already on file. Nothing was charged.",
    retryable: false,
    nextSteps: ["Send the customer a pay link so they can enter a new account themselves."],
  },
  BILLING_ACH_PHONE_RECORDING_INVALID: {
    severity: "user_input",
    title: "The recording couldn’t be filed",
    message:
      "The call recording couldn’t be attached to this authorization. Nothing was charged.",
    retryable: true,
    nextSteps: ["Upload the recording again, then submit."],
  },
  BOOKS_ACH_DEBIT_NOT_FOUND: {
    severity: "user_input",
    title: "Bank payment not found",
    message: "That bank payment isn’t on this invoice any more. Reload the page.",
    retryable: false,
  },
  BOOKS_ACH_DEBIT_NOT_CANCELLABLE: {
    severity: "user_input",
    title: "This bank payment can’t be cancelled here",
    message:
      "Only a one-time bank payment that is still queued can be cancelled from the invoice. An autopay debit is stopped by stopping the customer’s autopay.",
    retryable: false,
  },
  // Books autopay on send (order-up-supabase 20261021183803): the invoice
  // email that states the debit queues it; staff may skip it.
  BOOKS_ACH_AUTOPAY_NOT_SKIPPABLE: {
    severity: "user_input",
    title: "Not an autopay debit",
    message:
      "This bank debit was not scheduled by autopay, so there is no autopay to skip. A one-time bank payment has its own Cancel.",
    retryable: false,
  },
  BOOKS_ACH_AUTOPAY_NOTHING_TO_SKIP: {
    severity: "user_input",
    title: "Nothing to skip",
    message: "This bank payment is no longer queued, so there is nothing to skip.",
    retryable: false,
  },
  BOOKS_ACH_AUTOPAY_NOT_DEPLOYED: {
    severity: "service",
    title: "Not available yet",
    message: "Skipping autopay is not available on this database yet. Try again shortly.",
    retryable: true,
  },
  BOOKS_ACH_AUTOPAY_SKIP_REASON_REQUIRED: {
    severity: "user_input",
    title: "Reason required",
    message: "Say why autopay is being skipped for this invoice.",
    retryable: false,
  },
  BOOKS_ACH_AUTOPAY_NO_NOTICE: {
    severity: "service",
    title: "Autopay not scheduled",
    message:
      "Autopay is only scheduled by an invoice email that stated the debit. Nothing was queued; charge the saved bank account instead.",
    retryable: false,
  },
  BOOKS_ACH_AUTOPAY_NOTICE_PERIOD: {
    severity: "service",
    title: "Too soon for a personal account",
    message:
      "A personal bank account cannot be debited sooner than 10 days after the invoice email. Nothing was queued.",
    retryable: false,
  },
  BOOKS_ACH_AUTOPAY_WRONG_AUTHORIZATION: {
    severity: "service",
    title: "Autopay not scheduled",
    message:
      "The customer's autopay authorization did not match this invoice, so nothing was queued. Charge the saved bank account instead.",
    retryable: false,
  },
  BOOKS_ACH_AUTOPAY_NOT_BEFORE_REQUIRED: {
    severity: "service",
    title: "Autopay not scheduled",
    message: "Autopay could not be scheduled for this invoice. Nothing was queued.",
    retryable: false,
  },
  BOOKS_ACH_AUTOPAY_KEY_TAKEN: {
    severity: "service",
    title: "Autopay changed meanwhile",
    message: "Another bank debit for this invoice was queued at the same moment. Refresh the invoice.",
    retryable: true,
  },
  BOOKS_ACH_DEBIT_SENDING: {
    severity: "user_input",
    title: "Already being sent",
    message:
      "This bank payment is being sent to the bank right now, so it can no longer be cancelled. Its result shows on the invoice shortly.",
    retryable: false,
  },
  BOOKS_INVOICE_HAS_LIVE_ACH_DEBIT: {
    severity: "user_input",
    title: "Bank payment still live",
    message:
      "A bank payment for this invoice is still live at the processor. Cancel (void) or refund it first — voiding or deleting the invoice would leave the customer charged with no invoice behind it.",
    retryable: false,
  },
  BOOKS_INVOICE_HAS_SETTLED_ACH_DEBIT: {
    severity: "user_input",
    title: "Bank payment already at the bank",
    message:
      "This invoice has a bank payment that has already gone to the bank or settled. A plain void would keep the customer’s money — use “Refund and void” to send it back and void the invoice in one step.",
    retryable: false,
  },
  BOOKS_ACH_VOID_TOO_LATE: {
    severity: "user_input",
    title: "Already gone to the bank",
    message:
      "This bank payment has already gone to the bank, so it can no longer be cancelled. Nothing was changed. Refund it instead.",
    retryable: false,
  },
  BOOKS_ACH_VOID_FAILED: {
    severity: "service",
    title: "Bank payment not cancelled",
    message:
      "The payment processor did not cancel this bank payment. Nothing was changed — the payment and the invoice are as they were.",
    retryable: true,
  },
  BOOKS_ACH_VOID_UNCONFIRMED: {
    severity: "service",
    title: "Cancellation not confirmed",
    message:
      "OrderUp could not confirm with the payment processor whether this bank payment was cancelled. Nothing in Books was changed. Try again in a few minutes.",
    retryable: true,
  },
  BOOKS_ACH_REFUND_FAILED: {
    severity: "service",
    title: "Refund not sent",
    message:
      "The payment processor did not send this refund. No money moved, and Books was not changed.",
    retryable: true,
  },
  BOOKS_ACH_REFUND_UNCONFIRMED: {
    severity: "service",
    title: "Refund not confirmed",
    message:
      "OrderUp could not confirm with the payment processor whether this refund went through. Do not refund again — OrderUp is reconciling it with the processor.",
    retryable: false,
  },
  BOOKS_ACH_REVERSAL_NOT_DEPLOYED: {
    severity: "service",
    title: "Not available yet",
    message:
      "Cancelling and refunding sent bank payments is still being switched on for this account. Nothing was changed — try again shortly.",
    retryable: true,
  },
  BOOKS_ACH_REVERSAL_NOT_RECORDED: {
    severity: "service",
    title: "Done at the bank, not in Books",
    message:
      "The payment processor cancelled or refunded the bank payment, but Books could not record it. Do NOT try again — contact OrderUp support with this invoice number.",
    retryable: false,
  },
  BOOKS_ACH_REVERSAL_FAILED: {
    severity: "service",
    title: "Bank payment not changed",
    message:
      "The bank payment could not be cancelled or refunded. Nothing was changed — try again, or contact OrderUp support if it keeps happening.",
    retryable: true,
  },
  BOOKS_ACH_REVERSAL_IN_PROGRESS: {
    severity: "user_input",
    title: "Already in progress",
    message:
      "A cancellation or refund of this bank payment is already being processed. Wait for its result before trying again.",
    retryable: false,
  },
  BOOKS_ACH_REVERSAL_UNRESOLVED: {
    severity: "user_input",
    title: "Earlier result unconfirmed",
    message:
      "An earlier cancellation or refund of this bank payment could not be confirmed with the processor. OrderUp is reconciling it; nothing more can be refunded until it is resolved.",
    retryable: false,
  },
  BOOKS_ACH_REVERSAL_KIND_INVALID: {
    severity: "user_input",
    title: "Not a valid action",
    message:
      "That is not a way to take back a bank payment.",
    retryable: false,
  },
  BOOKS_ACH_REVERSAL_REASON_REQUIRED: {
    severity: "user_input",
    title: "Reason required",
    message:
      "Enter a reason for cancelling or refunding this bank payment.",
    retryable: false,
  },
  BOOKS_ACH_REVERSAL_KEY_REQUIRED: {
    severity: "user_input",
    title: "Request incomplete",
    message:
      "The request was missing its idempotency key. Reload the page and try again.",
    retryable: false,
  },
  BOOKS_ACH_REVERSAL_KEY_REUSED: {
    severity: "user_input",
    title: "Request already used",
    message:
      "This request was already used for a different action. Reload the page and try again.",
    retryable: false,
  },
  BOOKS_ACH_REVERSAL_NOT_FOUND: {
    severity: "user_input",
    title: "Not found",
    message:
      "That cancellation or refund no longer exists.",
    retryable: false,
  },
  BOOKS_ACH_REVERSAL_OUTCOME_INVALID: {
    severity: "service",
    title: "Result not recorded",
    message:
      "The processor’s answer could not be recorded. Contact OrderUp support with this invoice number.",
    retryable: false,
  },
  BOOKS_ACH_REVERSAL_ALREADY_COMPLETED: {
    severity: "user_input",
    title: "Already answered",
    message:
      "This cancellation or refund already has a result. Reload the page to see it.",
    retryable: false,
  },
  BOOKS_ACH_DEBIT_ATTEMPT_NOT_FOUND: {
    severity: "user_input",
    title: "Bank payment not found",
    message:
      "That bank payment no longer exists on this invoice.",
    retryable: false,
  },
  BOOKS_ACH_DEBIT_NOT_SENT: {
    severity: "user_input",
    title: "Not sent yet",
    message:
      "This bank payment has not been sent yet, so there is nothing to refund. Cancel it instead.",
    retryable: false,
  },
  BOOKS_ACH_DEBIT_RETURNED: {
    severity: "user_input",
    title: "Returned by the bank",
    message:
      "The customer’s bank already returned this payment, so the money is back with the customer. There is nothing to refund.",
    retryable: false,
  },
  BOOKS_ACH_DEBIT_NOT_COLLECTED: {
    severity: "user_input",
    title: "No money taken",
    message:
      "This bank payment never took any money, so there is nothing to cancel or refund.",
    retryable: false,
  },
  BOOKS_ACH_DEBIT_ALREADY_VOIDED: {
    severity: "user_input",
    title: "Already cancelled",
    message:
      "This bank payment was already cancelled at the processor.",
    retryable: false,
  },
  BOOKS_ACH_DEBIT_ALREADY_REFUNDED: {
    severity: "user_input",
    title: "Already refunded",
    message:
      "This bank payment was already refunded in full.",
    retryable: false,
  },
  BOOKS_ACH_DEBIT_NO_PROCESSOR_REFERENCE: {
    severity: "service",
    title: "Processor reference missing",
    message:
      "OrderUp has no processor reference for this bank payment, so it cannot be cancelled or refunded from here. Contact OrderUp support with this invoice number.",
    retryable: false,
  },
  BOOKS_ACH_VOID_AFTER_REFUND: {
    severity: "user_input",
    title: "Already partly refunded",
    message:
      "Part of this bank payment was already refunded, so it can no longer be cancelled as a whole. Refund the rest instead.",
    retryable: false,
  },
  BOOKS_ACH_DEBIT_SETTLED: {
    severity: "user_input",
    title: "Already settled",
    message:
      "This bank payment has settled, so it can no longer be cancelled. Refund it instead.",
    retryable: false,
  },
  BOOKS_ACH_VOID_WINDOW_CLOSED: {
    severity: "user_input",
    title: "Already gone to the bank",
    message:
      "This bank payment has already gone to the bank, so it can no longer be cancelled. Refund it instead.",
    retryable: false,
  },
  BOOKS_ACH_DEBIT_NOT_SETTLED: {
    severity: "user_input",
    title: "Not settled yet",
    message:
      "This bank payment has not settled yet, so only the whole payment can be cancelled. Use “Cancel payment (void)” instead.",
    retryable: false,
  },
  BOOKS_ACH_REFUND_AMOUNT_INVALID: {
    severity: "user_input",
    title: "Enter an amount",
    message:
      "Enter a refund amount greater than zero.",
    retryable: false,
  },
  BOOKS_ACH_REFUND_EXCEEDS_REMAINING: {
    severity: "user_input",
    title: "More than was paid",
    message:
      "A refund can’t be more than the bank payment minus what was already refunded.",
    retryable: false,
  },
  BOOKS_ACH_DEBIT_ENQUEUE_FAILED: {
    severity: "service",
    title: "Bank payment not queued",
    message: "The bank payment couldn’t be queued. Nothing was charged — please try again.",
    retryable: true,
  },
  BOOKS_ACH_DEBIT_EXECUTOR_FAILED: {
    severity: "service",
    title: "Bank debit run failed",
    message:
      "The run that sends queued bank debits stopped before it finished. Debits it didn’t reach stay queued for the next run.",
    retryable: true,
  },
  BOOKS_ACH_DEBIT_FAILED: {
    severity: "service",
    title: "Bank debit failed",
    message:
      "The scheduled bank debit could not be completed. The invoice was still posted — collect it manually or retry the debit.",
    retryable: true,
  },
  BOOKS_ACH_SCHEDULE_NOT_INVOICE: {
    severity: "user_input",
    title: "Autopay is for invoice schedules only",
    message:
      "Bank autopay can only be set up on a recurring invoice schedule — this schedule doesn’t create invoices.",
    retryable: false,
  },
  BOOKS_ACH_CUSTOMER_REQUIRED: {
    severity: "user_input",
    title: "Add a customer first",
    message:
      "This schedule’s invoice template has no customer, so there’s no one to authorize bank debits.",
    retryable: false,
    nextSteps: [
      "Set the customer on the schedule’s invoice template, then set up autopay again.",
    ],
  },
  BOOKS_ACH_CUSTOMER_LOOKUP_FAILED: {
    severity: "service",
    title: "Couldn’t verify the customer",
    message:
      "We couldn’t verify the schedule’s customer just now. Nothing was saved — please try again.",
    retryable: true,
  },
  // TRX tokenization + PayPage session mint (billing payment-method + the
  // S2 mandate ceremony both ride these).
  BILLING_TOKENIZE_DECLINED: {
    severity: "user_input",
    title: "Bank account not accepted",
    message:
      "The payment provider did not accept this bank account. Check the routing and account numbers and try again, or use a different account.",
    retryable: true,
  },
  BILLING_ACH_VAULT_FAILED: {
    severity: "service",
    title: "Bank account couldn’t be saved",
    message:
      "We couldn’t save this bank account because of a problem on our side. Nothing was charged; please try again shortly or contact OrderUp support.",
    retryable: true,
  },
  BILLING_ACCOUNT_ACH_OFF: {
    severity: "user_input",
    title: "Bank payment isn't on yet",
    message:
      "OrderUp can't take its monthly bill from a bank account yet. Nothing was saved. Ask OrderUp support to turn bank payments on, then authorize again.",
    retryable: false,
  },
  ACH_AUTHORIZATION_REQUIRED: {
    severity: "user_input",
    title: "Bank account couldn't be used for the bill",
    message:
      "This bank account couldn't be set up to pay your OrderUp bill, so it wasn't saved. Contact OrderUp support.",
    retryable: false,
  },
  BILLING_ACH_LAST4_MISMATCH: {
    severity: "user_input",
    title: "Account number didn’t match",
    message:
      "The bank account the processor saved ends in different digits than the one on the authorization. Nothing was saved — enter the account again and accept the authorization for it.",
    retryable: true,
  },
  BILLING_TOKENIZE_NO_GUID: {
    severity: "service",
    title: "Bank account couldn’t be saved",
    message:
      "The payment provider accepted the account but didn’t return a storage reference, so nothing was saved. Please try again — contact support if it keeps happening.",
    retryable: true,
  },
  BILLING_PAYPAGE_INIT_DECLINED: {
    severity: "service",
    title: "Couldn’t start a secure session",
    message:
      "The payment provider declined to start a secure entry session. Please try again in a moment.",
    retryable: true,
  },
  BILLING_HQ_NOT_CONFIGURED: {
    severity: "service",
    title: "Billing isn’t fully configured",
    message:
      "OrderUp’s billing merchant isn’t fully configured yet, so this payment action can’t run. This is on us — please contact support.",
    retryable: false,
  },
  BILLING_VALIDATION_ERROR: {
    severity: "user_input",
    title: "Check your input",
    message: "Some of the submitted details are invalid. Please check the form and try again.",
    retryable: true,
  },
  // POA evidence checks when saving an ACH payment method / linking a
  // pre-recorded authorization (billing payment-method + admin S3 flows).
  BILLING_ACH_AUTHORIZATION_REQUIRED: {
    severity: "user_input",
    title: "Authorization needed",
    message:
      "A bank account can’t be saved without a debit authorization. Accept the authorization terms, or link a signed authorization that’s already on file.",
    retryable: false,
  },
  BILLING_ACH_AUTHORIZATION_NOT_FOUND: {
    severity: "user_input",
    title: "Authorization not found",
    message:
      "The referenced bank-debit authorization doesn’t exist. It may have been entered with the wrong id — re-check it or capture a new authorization.",
    retryable: false,
  },
  BILLING_ACH_AUTHORIZATION_CONFLICT: {
    severity: "user_input",
    title: "Two authorizations supplied",
    message:
      "Both a new acknowledgment and a pre-recorded authorization were supplied — use one or the other, not both.",
    retryable: false,
  },
  BILLING_ACH_AUTHORIZATION_IMMUTABLE: {
    severity: "service",
    title: "Authorization records can’t be changed",
    message:
      "Recorded debit-authorization evidence is frozen and can’t be edited or deleted. To change terms, capture a new authorization; to stop debits, revoke this one.",
    retryable: false,
  },
  BILLING_ACH_EVIDENCE_REVOKED: {
    severity: "user_input",
    title: "Authorization was revoked",
    message:
      "That bank-debit authorization has been revoked, so it can’t be linked. A new authorization must be captured first.",
    retryable: false,
  },
  BILLING_ACH_EVIDENCE_ACCOUNT_MISMATCH: {
    severity: "user_input",
    title: "Wrong bank account",
    message:
      "The signed authorization covers a specific bank account, and the account entered doesn’t match it. Enter that account, or record a new authorization.",
    retryable: true,
  },
  BILLING_ACH_EVIDENCE_SCOPE_MISMATCH: {
    severity: "user_input",
    title: "Wrong kind of authorization",
    message:
      "That authorization doesn’t cover subscription billing, so it can’t be linked here. Capture a subscription-billing authorization instead.",
    retryable: false,
  },
  BILLING_ACH_EVIDENCE_RESTAURANT_MISMATCH: {
    severity: "user_input",
    title: "Authorization belongs to another location",
    message:
      "That authorization was captured for a different location, so it can’t be linked to this subscription.",
    retryable: false,
  },
  BILLING_ACH_BACKLINK_RESTAURANT_MISMATCH: {
    severity: "service",
    title: "Authorization couldn’t be linked",
    message:
      "The signed authorization and the subscription it points at belong to different locations, so the link was refused. Nothing was saved — contact support.",
    retryable: false,
  },
  BILLING_ACH_DISCLOSURE_STALE: {
    severity: "user_input",
    title: "Authorization terms changed",
    message:
      "The debit authorization terms were updated since this page loaded, so the authorization wasn’t recorded. Refresh, review the current terms, and re-accept.",
    retryable: true,
    nextSteps: ["Refresh the page and accept the updated authorization."],
  },
  // Raised when an HQ operator tries to publish authorization prose that
  // still carries an unreviewed marker — the platform DRAFT header, the
  // migration-seeded PLACEHOLDER header, or an unresolved `[[…]]` reviewer
  // decision. Fail-closed by construction: it can only ever refuse a publish,
  // never approve prose.
  BILLING_ACH_DISCLOSURE_DRAFT_MARKER: {
    severity: "user_input",
    title: "This text is still a draft",
    message:
      "The authorization text still contains draft or placeholder markers, so it wasn’t published. Resolve every [[…]] decision, remove the draft header, and publish only wording counsel has approved.",
    retryable: false,
    nextSteps: [
      "Resolve every [[…]] decision left in the body.",
      "Delete the DRAFT / PLACEHOLDER header lines.",
      "Publish only wording counsel has approved.",
    ],
  },
  // Raised when publishing authorization prose whose `{{merge_field}}`
  // placeholders are not all resolvable for the scope it is being published
  // to. Two failures, one refusal: a field nothing resolves reaches the payer
  // as literal `{{…}}`, and a field that resolves on ANOTHER scope names a
  // DIFFERENT party — an S3 body using {{business_legal_name}} would render
  // the restaurant paying the bill as the entity taking the money.
  BILLING_ACH_DISCLOSURE_MERGE_FIELD_UNRESOLVABLE: {
    severity: "user_input",
    title: "This text uses a field this scope can’t fill in",
    message:
      "The authorization text contains placeholders that nothing filling in this scope’s terms can resolve, so it wasn’t published. Left as-is they would reach the payer as literal {{…}} text — or name the wrong business as the one taking the money.",
    retryable: false,
    nextSteps: [
      "Remove or replace each placeholder the scope cannot resolve.",
      "Write the value out in the prose when no merge field fits.",
      "Re-check the wording with counsel before publishing.",
    ],
  },
  BILLING_ACH_SEC_POLICY_MISMATCH: {
    severity: "service",
    title: "Authorization type not allowed",
    message:
      "This kind of debit authorization isn’t the one configured for this billing surface, so it wasn’t recorded. This is a configuration issue — contact support.",
    retryable: false,
  },
  // The SEC code an authorization files under is DERIVED from WHO is being
  // debited and the ceremony that captured it — a consumer receiver files PPD
  // when signed and WEB when clicked, a business receiver files CCD either way
  // — and then checked against the codes counsel PERMITS for the scope. This
  // is the "classified, but not for THIS payer and ceremony" refusal, distinct
  // from BILLING_ACH_SEC_POLICY_UNSET ("not classified at all") because the
  // remedy differs: permit the derived code. The other ceremony is a remedy
  // only for a consumer payer — a business customer files CCD however they
  // authorize, so a scope without CCD permitted refuses every one of them.
  // None of it is anything the payer can act on, so both read as service
  // issues.
  BILLING_ACH_SEC_POLICY_NOT_PERMITTED: {
    severity: "service",
    title: "Bank payments not available this way yet",
    message:
      "Bank-account authorizations captured this way aren’t enabled for this business yet, so nothing was recorded. Please pay by card, or check back later.",
    retryable: false,
  },
  // The captured code disagreed with the ceremony that produced it — only
  // reachable from a caller that hand-picks a code, or a legacy row. Refused
  // rather than coerced: an entry filed under a class that does not describe
  // how the payer authorized would return priced and timed for the wrong one.
  BILLING_ACH_SEC_CODE_CEREMONY_MISMATCH: {
    severity: "service",
    title: "Authorization type doesn’t match how it was signed",
    message:
      "The authorization type recorded for this bank authorization doesn’t match how it was actually agreed to, so it wasn’t saved. This is a configuration issue — contact support.",
    retryable: false,
  },
  BILLING_ACH_PPD_DOCUMENT_REQUIRED: {
    severity: "user_input",
    title: "Signed form required",
    message:
      "A pre-recorded authorization needs the signed authorization form attached. Upload the signed document and try again.",
    retryable: false,
  },
  BILLING_ACH_PPD_SIGNER_REQUIRED: {
    severity: "user_input",
    title: "Signer name required",
    message:
      "Enter the name of the person who signed the authorization form.",
    retryable: true,
  },
  BILLING_ACH_PPD_SIGNED_AT_REQUIRED: {
    severity: "user_input",
    title: "Signature date required",
    message: "Enter the date the authorization form was signed.",
    retryable: true,
  },
  // Audited IP reveal (admin authorization detail).
  BILLING_ACH_IP_DECRYPT_FAILED: {
    severity: "service",
    title: "Couldn’t reveal the IP address",
    message:
      "The stored IP address could not be decrypted. This is on us — contact support if you need this record.",
    retryable: false,
  },
  BILLING_ACH_REVEAL_AUDIT_FAILED: {
    severity: "service",
    title: "Reveal refused",
    message:
      "The reveal couldn’t be written to the audit log, so it was refused — reveals are never un-audited. Try again in a moment.",
    retryable: true,
  },

  // ── VAULT + STAFF MFA ────────────────────────────────────────────────────────
  // A second factor is required — by the vault (`VAULT_MFA_POLICY`) or by an
  // OrderUp-staff route (`STAFF_MFA_POLICY`). MFA_REAUTH_REQUIRED → the session
  // is valid but has not presented its factor; the client runs the challenge
  // and retries. MFA_ENROLLMENT_REQUIRED → the account has no second factor;
  // the staff consoles open the enrolment flow. Distinct from
  // STALE_AUTH_REAUTH_REQUIRED (same factor, freshness) so the UI prompts for a
  // code instead of a password.
  MFA_REAUTH_REQUIRED: {
    severity: "auth",
    title: "Confirm it's you",
    message:
      "This area is protected by a second sign-in step. Use your passkey, authenticator app or texted code to continue.",
    retryable: false,
    nextSteps: [
      "Use the second step you set up — a passkey, the 6-digit code in your authenticator app, or the code we text you.",
    ],
  },
  MFA_ENROLLMENT_REQUIRED: {
    severity: "auth",
    title: "Set up a second sign-in step",
    message:
      "Access here requires a second sign-in step. Add a passkey, an authenticator app or a phone number to your account to continue.",
    retryable: false,
    nextSteps: [
      "Open Security on your OrderUp account, add a second step, then try again.",
    ],
  },

  // ── CUSTOMER LOYALTY (Phase 14) — launch gate ──────────────────────────────
  // Emitted by requireCustomerLoyaltyEnabled() when the `initiative.customer_loyalty`
  // feature flag is off (GATE_OFF) or the flag service itself threw (GATE_UNAVAILABLE).
  // The customer-facing copy intentionally avoids mentioning "feature flags" or
  // internal infrastructure — it just says the feature isn't on yet.
  CUSTOMER_LOYALTY_LAUNCH_GATE_OFF: {
    severity: "service",
    title: "Loyalty rewards unavailable",
    message:
      "Loyalty rewards are not currently enabled at this restaurant. Check back soon.",
    retryable: false,
  },
  CUSTOMER_LOYALTY_LAUNCH_GATE_UNAVAILABLE: {
    severity: "service",
    title: "Loyalty rewards temporarily unavailable",
    message:
      "We couldn't reach the loyalty service right now. Please try again in a moment.",
    retryable: true,
  },
  // ── APPOINTMENTS + CROSS-BUSINESS LOYALTY LAUNCH GATES ──────────────────────
  // Fail-closed initiative flags (initiative.appointments,
  // initiative.cross_business_loyalty). Customer-facing copy avoids mentioning
  // flags/infrastructure — it just says the feature isn't on yet.
  APPOINTMENTS_LAUNCH_GATE_OFF: {
    severity: "service",
    title: "Booking unavailable",
    message:
      "Online booking is not currently enabled for this business. Check back soon.",
    retryable: false,
  },
  APPOINTMENTS_LAUNCH_GATE_UNAVAILABLE: {
    severity: "service",
    title: "Booking temporarily unavailable",
    message:
      "We couldn't reach the booking service right now. Please try again in a moment.",
    retryable: true,
  },
  CROSS_BUSINESS_LOYALTY_LAUNCH_GATE_OFF: {
    severity: "service",
    title: "Rewards wallet unavailable",
    message:
      "Your cross-business rewards wallet isn't available yet. Check back soon.",
    retryable: false,
  },
  CROSS_BUSINESS_LOYALTY_LAUNCH_GATE_UNAVAILABLE: {
    severity: "service",
    title: "Rewards wallet temporarily unavailable",
    message:
      "We couldn't reach the rewards service right now. Please try again in a moment.",
    retryable: true,
  },

  // ── CUSTOMER CAPTURE & ENROLLMENT (Guestbook POS capture) ───────────────────
  // Register-time customer identification/capture + consent-first loyalty
  // enrollment + the receipt-invite join flow. POS surfaces `message` verbatim;
  // web consumers (management / loyalty) resolve by code.
  CUSTOMER_NOT_FOUND: {
    severity: "user_input",
    title: "Customer not found",
    message: "We couldn't find a customer with that information.",
    retryable: false,
  },
  CUSTOMER_IDENTIFY_FAILED: {
    severity: "service",
    title: "Couldn't look up that customer",
    message:
      "We couldn't look up that customer right now. Please try again.",
    retryable: true,
  },
  MARKETING_CONSENT_INVALID: {
    severity: "user_input",
    title: "Choose a channel",
    message: "Choose at least one channel (text or email) to update.",
    retryable: false,
  },
  MARKETING_CONSENT_FAILED: {
    severity: "service",
    title: "Couldn't save the marketing preference",
    message:
      "We couldn't record the marketing-consent receipt, so the change wasn't saved. Please try again.",
    retryable: true,
  },
  CUSTOMER_PHONE_INVALID: {
    severity: "user_input",
    title: "Check the phone number",
    message:
      "That phone number doesn't look right. Enter a 10-digit US or Canadian number.",
    retryable: false,
    nextSteps: ["Re-enter the phone number as 10 digits."],
  },
  CUSTOMER_IDENTITY_COMINGLING_BLOCKED: {
    severity: "user_input",
    title: "This number belongs to a registered account",
    message:
      "That phone number is tied to a customer's own rewards account. Ask them to sign in on their device to see their points.",
    retryable: false,
  },
  ENROLLMENT_CONSENT_UNAVAILABLE: {
    severity: "service",
    title: "Couldn't complete enrollment",
    message:
      "We couldn't record the loyalty consent receipt, so enrollment wasn't completed. Please try again.",
    retryable: true,
  },
  ENROLLMENT_CONSENT_FAILED: {
    severity: "service",
    title: "Couldn't complete enrollment",
    message:
      "We couldn't record the loyalty consent receipt, so enrollment wasn't completed. Please try again.",
    retryable: true,
  },
  ENROLLMENT_FAILED: {
    severity: "service",
    title: "Couldn't complete enrollment",
    message:
      "The loyalty consent was recorded but enrollment couldn't be completed. Please try again.",
    retryable: true,
  },
  LOYALTY_JOIN_TOKEN_INVALID: {
    severity: "user_input",
    title: "This rewards link isn't valid",
    message: "This rewards invitation link isn't valid.",
    retryable: false,
    nextSteps: ["Ask the restaurant for a new invite, or use the link on a recent receipt."],
  },
  LOYALTY_JOIN_TOKEN_EXPIRED: {
    severity: "user_input",
    title: "This rewards link has expired",
    message: "This rewards invitation link has expired.",
    retryable: false,
    nextSteps: ["Ask the restaurant for a new invite, or use the link on a recent receipt."],
  },

  // ── ONBOARDING ──────────────────────────────────────────────────────────────
  "onboarding/no-tenant": {
    severity: "onboarding",
    title: "Finish setting up your account",
    message:
      "Your account isn’t fully set up yet. Let’s finish onboarding to continue.",
    retryable: false,
    nextSteps: ["Complete onboarding to continue."],
  },
  "onboarding/access-not-active": {
    severity: "onboarding",
    title: "Access not active yet",
    message:
      "Your access isn’t active yet. Contact your administrator to be granted access.",
    retryable: false,
  },
  "onboarding/no-assignment": {
    severity: "onboarding",
    title: "Not assigned yet",
    message:
      "You’re not assigned to a restaurant yet. Ask your administrator to add you.",
    retryable: false,
  },

  // ── AUTH / SESSION RECOVERY ─────────────────────────────────────────────────
  // Surfaced by the shared TokenHandler when the Firebase client SDK cannot be
  // reconciled with a valid server session (bounded retry exhausted, or a
  // wall-clock hold timed out). `retryable` so ErrorNotice offers "Try again";
  // TokenHandler additionally renders a "Sign in again" action for these.
  AUTH_FIREBASE_RECONCILE_FAILED: {
    severity: "auth",
    title: "Session lost",
    message:
      "We couldn’t restore your secure session. Sign in again — your data is safe.",
    retryable: true,
    nextSteps: ["Use “Sign in again” to re-establish your session."],
  },
  // Emitted by the API (GET /auth/session) on an invalid/expired session and by
  // TokenHandler's ghost-session teardown; previously fell through to generic
  // status-code copy.
  AUTH_SESSION_INVALID: {
    severity: "auth",
    title: "Session expired",
    message: "Your session is no longer valid. Please sign in again.",
    retryable: true,
  },
  // No credential reached the API at all — the session cookie aged out and the
  // browser discarded it before the request. Distinct from AUTH_SESSION_INVALID
  // on purpose: that one means "this session is dead, sign in again", while this
  // is repaired in place by re-minting the cookie from the Firebase ID token the
  // client is still holding (`isSessionExpiredError` → `refreshSessionCookie`).
  // Copy stays soft because the user should almost never see it: reaching the UI
  // means the silent re-mint ALSO failed.
  AUTH_SESSION_MISSING: {
    severity: "auth",
    title: "Session needs refreshing",
    message:
      "Your session timed out. We tried to renew it automatically — retry, and sign in again if it keeps happening.",
    retryable: true,
    nextSteps: [
      "Retry — most of the time the session renews itself.",
      "Still stuck? Sign out and sign back in.",
    ],
  },
  // The auth app is signed in but `users/{uid}` never resolved — absent,
  // unreadable, or a rejected query. Every branch that used to cover this
  // rendered "Loading profile..." forever, with no retry, no sign-out and no
  // ceiling, so a freshly-invited staff member bouncing back from the staff
  // portal simply dead-ended (prod, 2026-08-07).
  AUTH_PROFILE_UNAVAILABLE: {
    severity: "auth",
    title: "We couldn’t load your profile",
    message:
      "You’re signed in, but we couldn’t load your account profile. Try again — if it keeps failing, sign out and back in.",
    retryable: true,
    nextSteps: [
      "Use “Try again” to reload your profile.",
      "Still stuck? Sign out and sign back in.",
    ],
  },
  // The server DECLINED to synthesise a `users/{uid}` document for a signed-in
  // account that has none (`POST /users/me/provision`). Three reasons reach
  // here, and all three are terminal for the user: a guest/anonymous session
  // (which must never get a tenant user record), an account with no email
  // address, and an account holding more restaurant grants than its token can
  // list — where rebuilding the profile would silently drop the ones it cannot
  // see, and the next claims resync would turn that into a real revocation.
  // NOT retryable: pressing "Try again" refuses identically every time, and
  // offering it is how the previous version of this screen dead-ended people.
  PROFILE_PROVISION_BLOCKED: {
    severity: "auth",
    title: "We couldn’t set up your account profile",
    message:
      "You’re signed in, but your account profile can’t be rebuilt automatically. Our support team can restore it.",
    retryable: false,
    nextSteps: [
      "Contact support and quote the reference below.",
      "In the meantime you can sign out and back in — your data is safe.",
    ],
  },
  // The provisioning call itself failed (network, a Firestore read that errored
  // rather than answering). Genuinely retryable, unlike the refusal above.
  PROFILE_PROVISION_FAILED: {
    severity: "auth",
    title: "We couldn’t set up your account profile",
    message:
      "Something went wrong while restoring your account profile. Please try again.",
    retryable: true,
    nextSteps: [
      "Use “Try again”.",
      "Still stuck? Sign out and sign back in.",
    ],
  },
  STALE_AUTH_REAUTH_REQUIRED: {
    severity: "auth",
    title: "Re-authentication required",
    message: "Please confirm it’s you to continue — you won’t be signed out.",
    retryable: true,
  },
  // A PIN is the REGISTER credential, and `posAccess` (derived from the system
  // role — see `roleRequiresPosPin`) is what makes it usable. Setting one
  // without it mints a credential the POS refuses with NO_POS_ACCESS the moment
  // it is typed: a silent dead end for the staffer, and 4–6 more digits in a
  // shared keyspace for no capability. Only the Accountant role reaches this, so
  // the copy names the fix (a manager changes the role) instead of blaming the
  // person for pressing a button we showed them.
  POS_PIN_NOT_APPLICABLE: {
    severity: "auth",
    title: "Your role doesn’t use the register",
    message:
      "A POS PIN wouldn’t do anything on your role — it’s the code for signing in at the register and the time clock. If you need either, ask your manager to change your role.",
    retryable: false,
    nextSteps: ["Ask your manager to update your role, then try again."],
  },
  // snake alias for the AuthenticationError class-name entry so envelope codes
  // resolve to actionable copy instead of the generic fallback.
  AUTHENTICATION_ERROR: {
    severity: "auth",
    title: "Please sign in",
    message: "Please sign in to continue.",
    retryable: true,
  },
  // Firebase Auth OAuth domain misconfiguration (blocks popup/redirect sign-in);
  // it is on our side, so a service-severity, non-retryable notice.
  "auth/unauthorized-domain": {
    severity: "service",
    title: "Sign-in unavailable on this domain",
    message:
      "Sign-in isn’t available on this address due to a configuration issue on our side. Please contact support.",
    retryable: false,
  },
  // The management access gate waited past its bounded ceiling for the user's
  // reachable locations (/users/me/locations) without a resolved answer — surface
  // a retryable service notice instead of an indefinite skeleton.
  MANAGEMENT_WORKSPACE_LOAD_TIMEOUT: {
    severity: "service",
    title: "Still loading your workspace",
    message: "We couldn’t finish loading your workspace. Please try again.",
    retryable: true,
  },

  // ── WEBSITE / HOSTING ───────────────────────────────────────────────────────
  // App-wide fallbacks for the `/api/v1/hosting` routes and the management
  // website builder. The hosting pages carry local copy overrides
  // (HostingErrorState); these entries cover every other consumer.
  HOSTING_LAUNCH_GATE_OFF: {
    severity: "service",
    title: "Hosting isn’t available yet",
    message:
      "The Hosting Control Panel isn’t enabled for your account yet. Check back soon.",
    retryable: false,
  },
  HOSTING_LAUNCH_GATE_UNAVAILABLE: {
    severity: "service",
    title: "Hosting is temporarily unavailable",
    message:
      "We couldn’t check hosting availability right now. Please try again in a moment.",
    retryable: true,
  },
  HOSTING_PERMISSION_DENIED: {
    severity: "auth",
    title: "Owner access required",
    message:
      "Only the account owner can manage hosting. Ask the owner to make this change.",
    retryable: false,
  },
  HOSTING_RESTAURANT_MISMATCH: {
    severity: "user_input",
    title: "Wrong location selected",
    message:
      "That hosting resource belongs to a different location. Switch to the location that owns it and try again.",
    retryable: false,
  },
  HOSTING_INTERNAL_ERROR: {
    severity: "service",
    title: "Hosting ran into a problem",
    message:
      "Something went wrong on our end while managing your hosting. Please try again — and contact support if it keeps happening.",
    retryable: true,
  },
  RESTAURANT_NOT_LOADED: {
    severity: "user_input",
    title: "No restaurant loaded",
    message:
      "No restaurant is loaded yet. Pick a location or reload the page, then try again.",
    retryable: true,
  },

  // ── DEVICE IDENTITY (fleet enrollment) ──────────────────────────────────────
  // Raised by DeviceService.registerDevice when a device presents a known
  // hardwareId but a signing key that is NOT the one on file. A reimaged device
  // and a clone look identical on the wire, so the registration is REJECTED and
  // a human decides. Retrying changes nothing — only an operator re-adoption
  // (which rotates the registered key) clears it, so `retryable: false`.
  DEVICE_IDENTITY_CONFLICT: {
    severity: "auth",
    title: "This device isn’t recognized",
    message:
      "This terminal’s hardware ID is already registered to a different device key, so we can’t set it up automatically. If it was reimaged or replaced, an OrderUp operator needs to re-approve it.",
    retryable: false,
    nextSteps: [
      "Contact OrderUp support and mention that the device needs to be re-approved.",
      "Do not factory-reset or re-enroll the terminal — that will not clear this.",
    ],
  },
  DeviceIdentityConflictError: {
    severity: "auth",
    title: "This device isn’t recognized",
    message:
      "This terminal’s hardware ID is already registered to a different device key, so we can’t set it up automatically. If it was reimaged or replaced, an OrderUp operator needs to re-approve it.",
    retryable: false,
    nextSteps: [
      "Contact OrderUp support and mention that the device needs to be re-approved.",
      "Do not factory-reset or re-enroll the terminal — that will not clear this.",
    ],
  },

  // ── GOOGLE BUSINESS PROFILE ────────────────────────────────────────────────
  // The service raises genuinely actionable errors (an ineligible category, an
  // expired token, an unsupported attribute) and the UI used to flatten every
  // one of them into "Failed to sync data. Please try again." — advice that is
  // wrong for all of them. Each of these tells the operator the ONE thing that
  // will actually fix it.
  GOOGLE_FOOD_MENUS_INELIGIBLE: {
    severity: "user_input",
    title: "Google won’t accept a menu for this location yet",
    message:
      "Google only allows food menus on listings it recognizes as a food-serving business. Your listing’s primary category or verification status is currently blocking it — this isn’t something we can override from our side.",
    retryable: false,
    nextSteps: [
      "Open your Google Business Profile and set the PRIMARY category to a food-serving one (e.g. Restaurant, Cafe, Bakery).",
      "Make sure the listing is verified — unverified listings can’t take menus.",
      "Once both are true, come back and press Sync again.",
    ],
  },
  FOOD_MENUS_INVALID: {
    severity: "user_input",
    title: "Your menu can’t be sent to Google as-is",
    message:
      "We checked the menu before sending it and found something Google would reject. Nothing was sent, so your Google listing is unchanged.",
    retryable: false,
    nextSteps: [
      "The details below name the item and field involved — fix that item and press Sync again.",
      "Most often this is an item with no price, or a name/description over Google’s length limit.",
    ],
  },
  FOOD_MENUS_UPDATE_FAILED: {
    severity: "service",
    title: "Google rejected the menu",
    message:
      "We reached Google, but it refused the update. Your existing Google menu is unchanged.",
    retryable: true,
    nextSteps: [
      "Google limits profile edits to 10 per minute — if you’ve just made several changes, wait a minute and try again.",
    ],
  },
  INVALID_OAUTH_STATE: {
    severity: "auth",
    title: "That connection link isn’t valid",
    message:
      "The link that brought you back from Google couldn’t be verified. This usually means it was opened from an old tab or a copied URL.",
    retryable: false,
    nextSteps: [
      "Close this window and start the Google connection again from the Integrations page.",
    ],
  },
  OAUTH_STATE_EXPIRED: {
    severity: "auth",
    title: "The connection attempt timed out",
    message:
      "Too much time passed between starting the Google connection and finishing it, so we expired the request for safety.",
    retryable: true,
    nextSteps: ["Press Connect again and complete the Google sign-in promptly."],
  },
  ATTRIBUTES_UPDATE_FAILED: {
    severity: "service",
    title: "Google didn’t accept those attributes",
    message:
      "Google decides which attributes a listing can have based on its business category and country, and it rejected this update.",
    retryable: true,
    nextSteps: [
      "Only the options Google offers for your category can be set — if one you expected is missing, your primary category is likely the reason.",
    ],
  },
  MORE_HOURS_UPDATE_FAILED: {
    severity: "service",
    title: "Google didn’t accept those service hours",
    message:
      "Delivery, takeout and kitchen hours must sit inside your regular opening hours, and Google rejected this set.",
    retryable: true,
    nextSteps: [
      "Set your regular hours on Google first — service hours can’t exist without them.",
      "Make sure each service window falls within your regular opening hours.",
    ],
  },
  GOOGLE_AUTH_ERROR: {
    severity: "auth",
    title: "Reconnect your Google account",
    message:
      "Google has revoked or expired our access to your Business Profile. Nothing is syncing until it’s reconnected — this is not something that will fix itself.",
    retryable: false,
    nextSteps: [
      "Go to Integrations → Google Business Profile and press Reconnect.",
      "Sign in with the Google account that manages the listing.",
    ],
  },

  // ── GOOGLE LISTING SYNC (apps/api/components/routes/google-listing-sync.ts) ─
  // The owner's accept / reject on a held or contested listing field.
  LISTING_FIELD_CHANGED: {
    severity: "user_input",
    title: "This field changed",
    message:
      "The listing changed since this page loaded, so your choice was not applied. Nothing was sent to Google.",
    retryable: true,
    nextSteps: ["Refresh the page to see the latest values, then choose again."],
  },
  LISTING_ACCEPT_NOT_SUPPORTED: {
    severity: "user_input",
    title: "This field can’t be copied from Google",
    message:
      "Hours and ordering links always come from OrderUp, so Google’s version can’t be copied in.",
    retryable: false,
    nextSteps: ["Change them in OrderUp if Google’s version is right, or reject Google’s change."],
  },
  LISTING_NO_VALUE: {
    severity: "user_input",
    title: "Nothing to send",
    message: "OrderUp has no value for this field, so there is nothing to put back on Google.",
    retryable: false,
    nextSteps: ["Fill in this field in OrderUp first, or accept Google’s value."],
  },
  LISTING_SYNC_DISABLED: {
    severity: "user_input",
    title: "Listing sync is off",
    message:
      "Google listing sync isn’t on for this location, so nothing can be sent to Google from here.",
    retryable: false,
    nextSteps: ["Make sure the location’s Google Business Profile is connected."],
  },
  VOICE_OF_MERCHANT_LOST: {
    severity: "auth",
    title: "Google won’t take changes from this connection",
    message:
      "Google says the connected account can no longer edit this listing, so nothing was sent. Edits wait until the connection can edit the listing again.",
    retryable: false,
    nextSteps: [
      "Open your Google Business Profile and confirm the listing is verified and you are still an owner or manager.",
      "Then reconnect it in Integrations → Google Business Profile.",
    ],
  },
  GOOGLE_LISTING_UPDATE_FAILED: {
    severity: "service",
    title: "Google didn’t accept the change",
    message: "Google refused this update to the listing. Your choice was not applied.",
    retryable: true,
    nextSteps: ["Try again in a moment."],
  },

  // ── CASH DRAWERS (docs/CASH_MANAGEMENT_AND_PERIPHERALS.md) ─────────────────
  // A drawer that does not open during a cash sale is the loudest possible
  // failure at the counter and, until PR-2, produced no message at all. Every
  // entry here is written for someone standing at a till with a queue behind
  // them: it says what happened and what to do next, never "an error occurred".
  DRAWER_KICK_UNDELIVERABLE: {
    severity: "service",
    title: "The cash drawer didn’t open",
    message:
      "We couldn’t reach the printer to open the cash drawer, so it stayed shut. The sale itself is fine — only the drawer pulse failed.",
    retryable: true,
    nextSteps: [
      "Press Open Drawer to try again.",
      "Check that the printer is powered on and connected to the network.",
      "Use the manager key or another till if this station stays unreachable.",
    ],
  },
  // A ticket the server always composes a PrintDocument for, whose composer
  // threw. Nothing was sent to the printer (there is no legacy fallback: every
  // device in service refuses a job without the document). Written for the
  // person at the till who pressed Print and got no paper.
  PRINT_DOCUMENT_COMPOSE_FAILED: {
    severity: "service",
    title: "This ticket didn’t print",
    message:
      "We couldn’t prepare this ticket for the printer, so nothing was sent. The order and any payment are fine — only the paper is missing.",
    retryable: false,
    nextSteps: [
      "Read the order or receipt details from the screen for now.",
      "Contact OrderUp support with the reference below — trying again will fail the same way until we fix it.",
    ],
  },
  DRAWER_NOT_ON_PRINTER: {
    severity: "user_input",
    title: "Wrong drawer for this printer",
    message:
      "That cash drawer isn’t wired to the printer this job was sent to, so we stopped rather than open a different drawer — possibly one at another station.",
    retryable: false,
    nextSteps: [
      "Send the job to the printer the drawer is actually connected to.",
      "If this happened after a printer failover, check the backup printer’s drawer setup in Kitchen → Printers.",
    ],
  },
  PRINTER_HAS_NO_CASH_DRAWER: {
    severity: "user_input",
    title: "No drawer on that printer",
    message:
      "That printer has no cash drawer configured, so there is nothing for us to open.",
    retryable: false,
    nextSteps: [
      "Add the drawer under Kitchen → Printers if one is physically connected.",
      "Otherwise pick the printer the drawer is plugged into.",
    ],
  },

  // ── Gateway-side drawer-kick refusals ──────────────────────────────────────
  // The API refuses a mis-targeted kick before queueing it, but the gateway is
  // the LAST line and refuses independently — it has to, because the LAN and
  // direct tiers accept jobs the cloud never saw. Its refusal arrives as a
  // `PrintRecord.failureReason` string rather than a thrown error, so these
  // entries are keyed on that literal value: a surface rendering a failed
  // record resolves copy with `translateError({ code: record.failureReason })`.
  // Without them a refusal that we deliberately made LOUD renders as
  // "Something went wrong", which is the silent failure wearing a different
  // hat.
  "drawer_kick_refused:printer_has_no_cash_drawer": {
    severity: "user_input",
    title: "No drawer on that printer",
    message:
      "The printer accepted the job but has no cash drawer configured, so nothing was pulsed. Pulsing an empty port would have reported success and opened nothing.",
    retryable: false,
    nextSteps: [
      "Add the drawer under Kitchen → Printers if one is physically connected.",
      "Otherwise send the kick to the printer the drawer is plugged into.",
    ],
  },
  "drawer_kick_refused:drawer_not_on_printer": {
    severity: "user_input",
    title: "Wrong drawer for this printer",
    message:
      "The drawer named in the job isn’t wired to this printer, so nothing was opened. Opening the printer’s own drawer instead could have popped a till at a different station.",
    retryable: false,
    nextSteps: [
      "Send the kick to the printer the drawer is actually connected to.",
      "Check the drawer setup in Kitchen → Printers — a drawer moved between printers keeps its identity.",
    ],
  },
  "drawer_kick_refused:drawer_not_specified": {
    severity: "user_input",
    title: "Which drawer?",
    message:
      "This printer has two drawers and the job didn’t say which one to open. There is no safe default: guessing opens the wrong till half the time.",
    retryable: false,
    nextSteps: [
      "Re-run the action from a screen that selects the drawer.",
      "If this came from an older POS build, update it — the drawer is now chosen at the till, not by the printer.",
    ],
  },
  "drawer_kick_refused:printer_not_configured": {
    severity: "service",
    title: "This printer isn’t set up here",
    message:
      "The gateway has no configuration for the printer this drawer kick was sent to, so it refused rather than guess at the hardware.",
    retryable: true,
    nextSteps: [
      "Confirm the printer exists under Kitchen → Printers.",
      "If it was added recently, give the gateway a moment to sync and try again.",
    ],
  },
  "drawer_kick_refused:payload_unparseable": {
    severity: "service",
    title: "The cash drawer didn’t open",
    message:
      "The gateway couldn’t read the drawer-kick instruction, so it refused it. This is a fault on our side, not something you did wrong.",
    retryable: true,
    nextSteps: [
      "Press Open Drawer to try again.",
      "Use the manager key if the till has to be opened now, and report this — a repeat means a version mismatch between the POS and the gateway.",
    ],
  },

  CASH_DRAWER_SESSION_ALREADY_OPEN: {
    severity: "user_input",
    title: "This till is already open",
    message:
      "Another terminal already has a shift open on this cash drawer. Two ledgers on one physical drawer can never be reconciled, so only one may be open at a time.",
    retryable: false,
    nextSteps: [
      "Join the open session instead of starting a new one.",
      "If the previous shift was abandoned, close and count it first.",
    ],
  },
  CASH_OPENING_COUNT_REQUIRED: {
    severity: "user_input",
    title: "Count the drawer to start the shift",
    message:
      "This location requires every shift to start on a counted drawer, so a shift can’t be opened on the expected amount alone.",
    retryable: true,
    nextSteps: [
      "Open Cash Management, press Start Shift, and count the drawer by denomination.",
      "If this till never shows the count, update it — older versions can’t start a counted shift.",
    ],
  },
  CASH_OPENING_COUNT_MISMATCH: {
    severity: "user_input",
    title: "The starting amount doesn’t match the count",
    message:
      "The opening amount sent with this shift is different from the total of the drawer count, so we can’t tell which one is right.",
    retryable: true,
    nextSteps: ["Start the shift again and count the drawer once — the count’s total is the starting amount."],
  },
  CASH_DRAWER_VARIANCE_APPROVAL_REQUIRED: {
    severity: "user_input",
    title: "A manager needs to approve this close",
    message:
      "This drawer is over or short by more than the allowed tolerance, so it can’t be closed without a manager signing for the difference.",
    retryable: true,
    nextSteps: [
      "Recount the drawer — most variances are a miscount.",
      "Ask a manager with Cash Drawers (Full) access to approve the close.",
    ],
  },
  CASH_DRAWER_CLOSE_DIFFERS_FROM_POSTED: {
    severity: "user_input",
    title: "This drawer’s close was already started with a different count",
    message:
      "An earlier attempt to close this drawer recorded a count with a different total, and part of that close may already be posted. We won’t record a second, different close for the same shift.",
    retryable: false,
    nextSteps: [
      "Enter the same count total that was entered the first time.",
      "If that count can’t be entered again, leave the drawer: it closes at the end of the business day (batch-out) using the count entered the first time.",
    ],
  },
  CASH_DRAWER_CLOSE_LEDGER_CONFLICT: {
    severity: "service",
    title: "This drawer can’t be closed right now",
    message:
      "A ledger entry reserved for this drawer’s close already exists and doesn’t belong to this shift, so we won’t post the close over it.",
    retryable: false,
    nextSteps: [
      "Contact support with the drawer and the shift’s date — the ledger needs to be looked at.",
      "The shift still closes at the end of the business day, but without this count.",
    ],
  },
  CASH_DRAWER_OPEN_LEDGER_CONFLICT: {
    severity: "service",
    title: "This shift’s opening entry couldn’t be recorded",
    message:
      "The shift was started, but a ledger entry reserved for its opening — the drawer’s first float, or a difference between the opening count and the expected float — already exists and doesn’t belong to it, so that entry wasn’t posted.",
    retryable: false,
    nextSteps: [
      "Contact support with the drawer and the shift’s date — the ledger needs to be looked at before this shift’s cash will reconcile.",
      "You can keep trading on this shift in the meantime.",
    ],
  },
  CASH_DRAWER_CLOSE_LEDGER_CHANGED: {
    severity: "user_input",
    title: "The drawer changed while it was being closed",
    message:
      "Cash was paid in or out of this drawer while the close was being worked out, so the close was not recorded — its numbers would have left that movement out.",
    retryable: true,
    nextSteps: [
      "Make sure nobody else is using this drawer, count it again, and close it.",
    ],
  },
  CASH_DRAWER_SESSION_NOT_ACTIONABLE: {
    severity: "user_input",
    title: "This drawer is being closed",
    message:
      "A close has already been started for this shift, so no more drawer movements — pay-outs, drops, float changes — can be recorded against it. Sales and refunds still go through.",
    retryable: false,
    nextSteps: [
      "Finish the close by entering the same count total that was entered the first time.",
      "Record the movement on the next shift once this one has closed.",
    ],
  },
  CASH_DRAWER_LOCKDOWN_EXCEEDED: {
    severity: "user_input",
    title: "This drawer is at its cash limit",
    message:
      "The till is holding more cash than its configured limit, so we won’t add more to it.",
    retryable: true,
    nextSteps: [
      "Do a cash drop to the safe, then continue.",
      "A manager with Cash Drawer Lockdown (Override) can authorize this movement.",
    ],
  },
  // A server recording their OWN tip-out from the drawer at clock-out needs no
  // Cash Drawer Access (ruling R48), up to a per-shift ceiling. Above it the
  // ordinary path applies: someone who holds Cash Drawer Access records it.
  CASH_DRAWER_SELF_TIP_OUT_OVER_CAP: {
    severity: "user_input",
    title: "This tip-out is more than you can take out yourself",
    message:
      "You can record your own tip-out from the drawer up to a set limit for the shift. This one would go over it, so nothing was taken out.",
    retryable: false,
    nextSteps: [
      "Ask a manager with Cash Drawer Access to record the tip-out from the drawer.",
    ],
  },
  // A cash tip hand-in (R33) is a pay-in recorded only where cash tips are
  // paid through payroll: there, payroll approval posts that cash. Anywhere
  // else nothing would post it, so the drawer refuses to expect it. Not
  // retryable — the location's payout setting decides, not timing.
  CASH_DRAWER_HAND_IN_NOT_PAYROLL: {
    severity: "user_input",
    title: "Cash tips are not handed in at this location",
    message:
      "This location does not pay cash tips through payroll, so the cash tips stay with the employee and nothing was added to the drawer.",
    retryable: false,
    nextSteps: [
      "Keep the cash tips — they are not put into the drawer here.",
      "If cash tips should be paid through payroll, a manager can change the cash tip payout in the location's tip settings.",
    ],
  },
  CASH_DRAWER_SESSION_CLOSED: {
    severity: "user_input",
    title: "That shift is already counted out",
    message:
      "This till has been closed and counted, so nothing more can be recorded against it. Anything added now would change a drawer someone has already signed for.",
    retryable: false,
    nextSteps: [
      "Open a new shift on this drawer and record the movement there.",
      "If the close itself was a mistake, have a manager reopen or re-count it.",
    ],
  },
  // A cash amount outside what a till takes (F4, the POS #985 follow-up):
  // a $0.00 or negative pay-in, pay-out or drop, or anything past the cap.
  // The server's sentence names WHICH amount and its range ("A pay-out must
  // be between $0.01 and $999,999.99."), so it wins over this one.
  CASH_AMOUNT_OUT_OF_RANGE: {
    severity: "user_input",
    title: "That amount can’t be recorded",
    message: `Cash paid in, paid out or dropped must be at least $0.01, and no cash amount can be more than ${formatCents(CASH_AMOUNT_MAX_CENTS)}. Nothing was recorded.`,
    retryable: false,
    preferServerMessage: true,
    nextSteps: [
      "Check the amount and enter it again.",
      "A starting float or a drawer count of $0.00 is fine. Only money moving in or out needs an amount.",
    ],
  },

  // ── Cash drawer ENTITIES (CD-0 / CD-4) ─────────────────────────────────────
  // A drawer is its own document now, and the printer↔drawer reference is
  // one-sided (the drawer holds `printerId`). No single document sees both
  // drawers on a printer, so CD-4's pair invariant is enforced by the service —
  // and these are the two refusals that reach an operator.
  CASH_DRAWER_PRINTER_WIRING_CONFLICT: {
    severity: "user_input",
    title: "That drawer can’t sit on this printer",
    message:
      "A printer’s cash-drawer port daisy-chains at most two drawers, and the two must be on different RJ11 pins and different positions. Two drawers on one pin means one box never opens and the other opens twice.",
    retryable: false,
    nextSteps: [
      "Check which drawers are already wired to this printer — a printer can hold two.",
      "Give this drawer the free position (1 or 2); the pin follows from it unless you set it yourself.",
      "If the drawer really belongs here, move or retire the one already in that slot.",
    ],
  },
  PRINTER_HAS_CASH_DRAWERS: {
    severity: "user_input",
    title: "This printer still has a cash drawer on it",
    message:
      "A cash drawer outlives the printer it happens to be attached to — its id is what every shift and every journal entry refers to. Removing the printer with a drawer still wired to it would leave a till nothing can open.",
    retryable: false,
    nextSteps: [
      "Re-wire the drawer to its replacement printer first — it keeps all of its history.",
      "If the drawer is going away too and has never been used, delete it first.",
    ],
  },
  CASH_DRAWER_HAS_HISTORY: {
    severity: "user_input",
    title: "This drawer has shift history and can’t be deleted",
    message:
      "Shifts, cash movements and journal entries all refer to this drawer by its id. Deleting it would leave that history pointing at nothing.",
    retryable: false,
    nextSteps: [
      "Re-wire it to the replacement printer instead — the history stays intact.",
      "If the till is being retired, leave the drawer in place; its records have to stay reachable.",
    ],
  },

  // ── Cash refunds (CD-21) ───────────────────────────────────────────────────
  // A cash refund is a PAYMENT-plane event: a negative child PaymentRecord and
  // a drawer that pops. It posts NO `cash_drawer_events` document, because
  // expected cash already nets the refund out of the payment records — a ledger
  // event too would subtract it twice and post it to the GL twice.
  CASH_REFUND_TENDER_MISMATCH: {
    severity: "user_input",
    title: "That sale wasn’t paid in cash",
    message:
      "This payment was taken on a card, so it has to be refunded back to that card. Handing cash back would leave the cardholder uncredited and the till short.",
    retryable: false,
    nextSteps: [
      "Use Refund on the card transaction instead — it returns the money to the card it came from.",
      "If the guest paid part cash and part card, refund each part on its own tender.",
    ],
  },
  CASH_REFUND_EXCEEDS_REFUNDABLE: {
    severity: "user_input",
    title: "More than is left to refund",
    message:
      "This payment has already been refunded down to a smaller remaining balance, so the amount entered is more than can be given back.",
    retryable: true,
    nextSteps: [
      "Refund the remaining amount shown on the payment instead.",
      "Check the payment’s history — an earlier partial refund may already cover this.",
    ],
  },
  CASH_REFUND_IDEMPOTENCY_KEY_REUSED: {
    severity: "service",
    title: "That refund reference is already in use",
    message:
      "A different cash refund was already recorded under this reference. We stopped rather than overwrite it or record a second one.",
    retryable: false,
    nextSteps: [
      "Start the refund again from the payment — a fresh attempt gets its own reference.",
      "Check the payment’s refund history first to confirm whether the earlier refund went through.",
    ],
  },
  CASH_REFUND_IN_PROGRESS: {
    severity: "service",
    title: "This refund is already going through",
    message:
      "Another terminal is recording this same refund right now. We won’t run it twice.",
    retryable: true,
    nextSteps: [
      "Give it a moment, then re-open the payment to confirm the refund landed.",
      "Don’t re-enter it — a second attempt would hand back the money twice.",
    ],
  },

  CASH_REFUND_PARENT_VOIDED: {
    severity: "user_input",
    title: "That payment was voided",
    message:
      "This cash payment has already been voided, so its money is no longer counted in the till and there is nothing to hand back.",
    retryable: false,
    nextSteps: [
      "Check the ticket — voiding the payment put its amount back on the check.",
      "If the guest still needs money back, look for the payment that is still standing on this ticket.",
    ],
  },

  // ── Cash voids (docs/PAYMENT_REVERSAL_ARCHITECTURE.md §Cash void) ─────────
  // A void ERASES a cash/check tender: its amount is owed again on the check and
  // it leaves the expected cash of the shift the SALE was taken in. Each refusal
  // names the rail that does work.
  CASH_VOID_TENDER_MISMATCH: {
    severity: "user_input",
    title: "That payment wasn’t taken in cash",
    message:
      "This payment was taken on a card, so it has to be voided on the card — not from the drawer.",
    retryable: false,
    nextSteps: ["Use Void on the card transaction instead."],
  },
  CASH_VOID_NOT_VOIDABLE: {
    severity: "user_input",
    title: "This payment can’t be voided",
    message:
      "Only a cash payment that is still standing can be voided. This one has already been voided or reversed.",
    retryable: false,
    nextSteps: ["Re-open the payment to see what already happened to it."],
  },
  CASH_VOID_HAS_REFUNDS: {
    severity: "user_input",
    title: "Part of this payment was already refunded",
    message:
      "A void reverses the whole payment, and some of this one has already been handed back — voiding it would hand that part back twice.",
    retryable: false,
    nextSteps: ["Refund the remaining balance instead."],
  },
  CASH_VOID_NO_DRAWER_SESSION: {
    severity: "user_input",
    title: "This payment isn’t on a drawer shift",
    message:
      "This cash payment was not recorded against a drawer shift, so there is no till we can safely take it back out of.",
    retryable: false,
    nextSteps: ["Refund it instead — the refund comes out of the till that is open now."],
  },
  CASH_VOID_SESSION_CLOSED: {
    severity: "user_input",
    title: "That drawer has already been counted",
    message:
      "The shift this payment was taken in has been closed or is being closed. Voiding it now would change a count someone already signed for.",
    retryable: false,
    nextSteps: ["Refund it instead — the refund comes out of the till that is open now."],
  },
  CASH_VOID_IDEMPOTENCY_KEY_REUSED: {
    severity: "service",
    title: "That void reference is already in use",
    message:
      "This void reference was already used for a different payment. We stopped rather than report a void that did not happen.",
    retryable: false,
    nextSteps: [
      "Start the void again from the payment — a fresh attempt gets its own reference.",
    ],
  },

  // ── Card voids and check voids (docs/PAYMENT_REVERSAL_ARCHITECTURE.md) ────
  PARTIAL_VOID_UNSUPPORTED: {
    severity: "user_input",
    title: "A void always reverses the whole payment",
    message:
      "Card processors don’t void part of a payment, so nothing was sent. To give back less than the whole amount, refund it instead.",
    retryable: false,
    nextSteps: ["Void the full payment, or use Refund for a smaller amount."],
  },
  CHECK_HOLDS_MONEY: {
    severity: "user_input",
    title: "This check still has a payment on it",
    message:
      "Voiding the check would leave money collected against a bill that no longer exists.",
    retryable: false,
    nextSteps: ["Void or refund the payment first, then void the check."],
  },
  ORDER_ID_AMBIGUOUS: {
    severity: "service",
    title: "More than one order has this ID",
    message:
      "Two or more orders share this order ID, so nothing was changed rather than risk changing the wrong one.",
    retryable: false,
    nextSteps: ["Open the order from its own screen and try again, or contact support with the order ID."],
  },

  // ── Marketplace refusals (delivery schedule + menu publish) ─────────────
  //
  // Every one of these used to reach the operator as a 500. They are the
  // marketplace (or our own pre-flight) refusing THE REQUEST, and the server's
  // sentence is the only thing that names what to fix — so the refusals prefer
  // it, and the catalog sentence is the honest fallback when none arrived.
  MARKETPLACE_MENU_REQUIRED: {
    severity: "user_input",
    title: "Publish the menu first",
    message:
      "This marketplace will not take a schedule until it has a menu. Publish the menu, then publish the schedule again.",
    retryable: false,
    preferServerMessage: true,
    nextSteps: [
      "Publish the menu from the menu section of this page.",
      "Once it shows as published, publish the schedule again.",
    ],
  },
  SCHEDULE_REFUSED: {
    severity: "user_input",
    title: "This schedule can’t be published as it is",
    message: "The schedule was refused before it was sent. Correct it and publish again.",
    retryable: false,
    preferServerMessage: true,
  },
  MARKETPLACE_CAPABILITY_UNSUPPORTED: {
    severity: "user_input",
    title: "This marketplace doesn’t support that",
    message:
      "This marketplace does not let us manage that setting — change it in the marketplace’s own dashboard.",
    retryable: false,
    preferServerMessage: true,
  },
  MARKETPLACE_REFUSED: {
    severity: "user_input",
    title: "The marketplace refused this change",
    message: "The marketplace received the change and refused it.",
    retryable: false,
    preferServerMessage: true,
  },
  MARKETPLACE_ACCESS_REFUSED: {
    severity: "user_input",
    title: "The marketplace won’t let us change this",
    message:
      "The marketplace refused access for this account — often because another system manages it.",
    retryable: false,
    preferServerMessage: true,
  },
  MARKETPLACE_NOT_FOUND: {
    severity: "user_input",
    title: "The marketplace no longer has this",
    message:
      "The marketplace could not find this connection. It may have been removed on their side.",
    retryable: false,
    preferServerMessage: true,
  },
  MARKETPLACE_RATE_LIMITED: {
    severity: "service",
    title: "The marketplace asked us to slow down",
    message: "Too many changes reached the marketplace at once. Wait a few seconds and try again.",
    retryable: true,
  },
  MARKETPLACE_UPSTREAM_FAILED: {
    severity: "service",
    title: "The marketplace is having a problem",
    message:
      "The marketplace itself failed to respond. Nothing is wrong with your setup — try again in a few minutes.",
    retryable: true,
  },
  MARKETPLACE_UNAVAILABLE: {
    severity: "service",
    title: "The marketplace is unavailable",
    message: "We could not reach the marketplace just now. Try again in a few minutes.",
    retryable: true,
  },
  MARKETPLACE_UPSTREAM_AUTH_FAILED: {
    severity: "service",
    title: "Our connection to the marketplace was refused",
    message:
      "The marketplace refused OrderUp’s own credentials. This is on our side, not yours — contact support.",
    retryable: false,
  },
  MARKETPLACE_NOT_CONFIGURED: {
    severity: "service",
    title: "This marketplace isn’t set up yet",
    message: "OrderUp’s connection to this marketplace is not configured. Contact support.",
    retryable: false,
  },
  MENU_CONTENT_REJECTED: {
    severity: "user_input",
    title: "The menu wasn’t published",
    message:
      "The menu was refused because of something in it. Your connection is fine — fix the menu and publish again.",
    retryable: false,
    preferServerMessage: true,
  },
  MENU_SYNC_RETRY_SCHEDULED: {
    severity: "service",
    title: "The menu will be sent shortly",
    message:
      "The marketplace is busy right now, so this menu was not published yet. It is retried automatically — nothing is lost.",
    retryable: true,
    preferServerMessage: true,
  },
  MENU_SYNC_DISABLED: {
    severity: "user_input",
    title: "Menu publishing is turned off",
    message: "Menu publishing is switched off for this connection. Turn it on to publish.",
    retryable: false,
  },
  // Scheduled menu pricing on KitchenHub (Bar v2 Part D3): a store's new
  // prices wait for a manager to confirm the price changes.
  MENU_PRICE_DIFF_STALE: {
    severity: "user_input",
    title: "Prices changed since you opened this",
    message: "Prices changed since you opened this — review the new list, then confirm again.",
    retryable: true,
    preferServerMessage: true,
  },
  MENU_PRICE_DIFF_NOT_PENDING: {
    severity: "user_input",
    title: "Nothing to confirm",
    message: "There are no marketplace price changes waiting for confirmation. Refresh the page.",
    retryable: false,
    preferServerMessage: true,
  },
  MENU_PRICE_DIFF_INVALID: {
    severity: "user_input",
    title: "Refresh and try again",
    message: "The confirmation did not say which price changes it was for. Refresh the page and confirm again.",
    retryable: true,
  },
  MARKETPLACE_TABLET_CONNECTION_ACTIVE: {
    severity: "user_input",
    title: "Disconnect the tablet connection first",
    message:
      "This marketplace is still connected through its tablet protocol. Disconnect it, then connect again — OrderUp connects it through its POS integration.",
    retryable: false,
    preferServerMessage: true,
    nextSteps: [
      "Open the KitchenHub page and disconnect this marketplace.",
      "Connect it again from Integrations.",
      "Once it is live, switch the marketplace’s tablet off at the store — otherwise the marketplace may cancel orders you accept.",
    ],
  },
  INTEGRATION_RECONNECT_REQUIRED: {
    severity: "user_input",
    title: "Reconnect this marketplace",
    message:
      "The marketplace no longer accepts this connection. Reconnect it, then try again.",
    retryable: false,
    preferServerMessage: true,
  },

  // ── Platform feature entitlements (billing_state.apply_feature_entitlement_action) ──
  // Raised by the SECURITY DEFINER RPC as P0001 with message = code; see
  // FeatureEntitlementService.
  INVALID_ENTITLEMENT_TRANSITION: {
    severity: "user_input",
    title: "That change isn't possible right now",
    message:
      "That change isn't possible from the feature's current state. Refresh and try again.",
    retryable: true,
    nextSteps: ["Refresh the page to see the feature's current state, then try again."],
  },
  ENTITLEMENT_VERSION_CONFLICT: {
    severity: "user_input",
    title: "This feature changed since you loaded it",
    message:
      "Someone else changed this feature a moment ago. Refresh to see the latest, then try again.",
    retryable: true,
    nextSteps: ["Refresh the page to see the latest change, then try again."],
  },
  COMP_REASON_REQUIRED: {
    severity: "user_input",
    title: "A reason is required",
    message:
      "Enter a reason of at least 10 characters. It's kept in the feature's history.",
    retryable: true,
    nextSteps: ["Enter a reason of at least 10 characters, then try again."],
  },
  UNKNOWN_FEATURE_KEY: {
    severity: "user_input",
    title: "Unknown feature",
    message:
      "This feature isn't in the platform catalog. Contact engineering.",
    retryable: false,
  },
  // Plan 3 paid lifecycle (start_trial / activate / convert_comp_to_paid /
  // convert_trial / cancel / undo_cancel / switch_off_nonpayment).
  FEATURE_NOT_PRICED: {
    severity: "user_input",
    title: "This feature isn't priced yet",
    message:
      "This feature has no monthly price set, so it can't be started as a paid feature. Contact engineering to price it first.",
    retryable: false,
  },
  // Platform tiers.
  UNKNOWN_FEATURE_TIER: {
    severity: "user_input",
    title: "That plan isn't available",
    message: "We couldn't find that plan for this feature. Refresh the page and choose one from the list.",
    retryable: false,
    nextSteps: ["Refresh the page, then pick a plan from the list."],
  },
  FEATURE_TIER_NOT_SELLABLE: {
    severity: "user_input",
    title: "That plan can't be bought",
    message: "That plan isn't available to purchase. Choose a different plan.",
    retryable: false,
  },
  TIER_UNCHANGED: {
    severity: "user_input",
    title: "Already on that plan",
    message: "This location is already on that plan, so nothing was changed.",
    retryable: false,
  },
  PLATFORM_PACKAGES_DISABLED: {
    severity: "user_input",
    title: "Plans aren't available yet",
    message: "Tiers aren't available for this business yet.",
    retryable: false,
  },
  TIER_NOT_APPLICABLE: {
    severity: "user_input",
    title: "This feature has no plans",
    message: "This feature isn't sold in plans, so a plan can't be chosen for it. Refresh the page and try again.",
    retryable: false,
  },
  // The owner route refuses to sell a plan where the location already has the
  // feature with no limit (comped with no tier, or Scheduling at a location
  // from before the auto-comp cutover).
  TIER_ALREADY_INCLUDED: {
    severity: "user_input",
    title: "Already included",
    message: "This location already has this feature with no limits, so there's no plan to buy.",
    retryable: false,
  },
  PAYMENT_METHOD_REQUIRED: {
    severity: "user_input",
    title: "Add a payment method first",
    message:
      "This business has no bank account or card on file for its OrderUp bill. Add one, then try again.",
    retryable: true,
    nextSteps: [
      "The owner adds a bank account (ACH) in Billing → Payment method → Bank accounts, or during merchant onboarding.",
      "Or add a card in HQ Books → Platform subscriptions. Then try again.",
    ],
  },
  TRIAL_ALREADY_USED: {
    severity: "user_input",
    title: "This feature already had its trial",
    message:
      "This business already used its trial for this feature. Activate it directly instead, or override with a reason.",
    retryable: false,
  },
  REASON_REQUIRED: {
    severity: "user_input",
    title: "A reason is required",
    message:
      "Enter a reason of at least 10 characters. It's kept in the feature's history.",
    retryable: true,
    nextSteps: ["Enter a reason of at least 10 characters, then try again."],
  },
  INVALID_TRIAL_END: {
    severity: "user_input",
    title: "Pick a different trial end",
    message:
      "A trial must end after today and within a year of the day it started. Choose one length or one date.",
    retryable: true,
    nextSteps: ["Choose 15, 30, 60 or 90 days, or a date after today, then save again."],
  },
  TENANT_MISMATCH: {
    severity: "user_input",
    title: "That business changed since you loaded it",
    message:
      "This feature belongs to a different business than expected. Refresh the page, then try again.",
    retryable: true,
    nextSteps: ["Refresh the page, then try again."],
  },

  // ── Platform billing accounts (billing_state.create_billing_account /
  //    add_billing_payment_method / remove_billing_payment_method /
  //    set_default_billing_payment_method) ──────────────────────────────
  // Raised by the SECURITY DEFINER RPCs as P0001 with message = code; see
  // BillingAccountService.
  BILLING_ACCOUNT_NOT_FOUND: {
    severity: "user_input",
    title: "Billing account not found",
    message:
      "This business doesn't have a billing account yet. Refresh and try again.",
    retryable: true,
  },
  BILLING_ACCOUNT_TENANT_REQUIRED: {
    severity: "service",
    title: "Something went wrong",
    message:
      "That request was missing which business it was for. Contact engineering.",
    retryable: false,
  },
  // Charge-worker outcomes on a billing account (SaasBillingChargeWorker,
  // Task 12): abandoned without debiting; the daily collection sweep
  // re-enqueues them once the account is ACH-ready.
  BILLING_ACH_NOT_READY: {
    severity: "user_input",
    title: "Bank (ACH) debit not ready",
    message:
      "This business's bank (ACH) authorization or subscription wasn't valid when the charge ran, so nothing was debited. It's retried automatically once the bank authorization is in order.",
    retryable: true,
    nextSteps: ["Check the business's bank (ACH) authorization and subscription in HQ Books → Platform subscriptions."],
  },
  ONLINE_ORDERS_ACH_REQUIRED: {
    severity: "user_input",
    title: "Daily invoices are paid by bank (ACH) only",
    message:
      "This daily online-order invoice can only be paid by bank (ACH) debit, and the business's default payment method isn't a usable bank account. Nothing was charged. It's collected automatically once bank (ACH) payment is set up.",
    retryable: true,
    nextSteps: ["Set up a bank (ACH) payment method with a signed authorization for this business."],
  },
  ONLINE_ORDERS_INVOICE_LOOKUP_FAILED: {
    severity: "service",
    title: "Invoice type couldn't be confirmed",
    message:
      "We couldn't confirm whether this is a daily online-order invoice (bank-only), so nothing was charged. It's retried automatically.",
    retryable: true,
  },
  ACH_NOT_SUPPORTED_YET: {
    severity: "user_input",
    title: "Bank debit isn't available yet",
    message:
      "OrderUp doesn't accept bank-account payment methods yet — add a card instead.",
    retryable: false,
  },
  PAYMENT_METHOD_TOKEN_REQUIRED: {
    severity: "service",
    title: "Something went wrong",
    message:
      "That card couldn't be saved because no token came back from the card reader. Try adding it again.",
    retryable: true,
  },
  PAYMENT_METHOD_NOT_FOUND: {
    severity: "user_input",
    title: "Card not found",
    message:
      "That payment method no longer exists. Refresh and try again.",
    retryable: true,
  },
  BILLING_NOT_LIVE: {
    severity: "user_input",
    title: "Payments aren't live yet",
    message:
      "OrderUp isn't taking payments yet, so cards can't be saved, changed or charged here. Contact engineering.",
    retryable: false,
  },
  TENANT_NOT_FOUND: {
    severity: "user_input",
    title: "Business not found",
    message:
      "We couldn't find that business. Refresh and try again.",
    retryable: true,
  },
  PAYMENT_METHOD_NOT_ACTIVE: {
    severity: "user_input",
    title: "That card was removed",
    message:
      "That card was already removed and can't be made the default. Add a new card instead.",
    retryable: false,
  },
  CARD_CONSENT_REQUIRED: {
    severity: "user_input",
    title: "Confirm consent first",
    message:
      "Confirm the customer authorized storing and charging this card.",
    retryable: true,
    nextSteps: ["Check the consent box, then save the card again."],
  },

  // ── Staff "Retry charge" on a platform invoice run
  //    (POST /api/v1/admin/billing-accounts/:tenantGuid/invoice-runs/:runId/retry-charge,
  //    MonthlyInvoiceRunService.retryChargeByStaff + the SQL enqueue's refusals).
  RETRY_REASON_REQUIRED: {
    severity: "user_input",
    title: "A reason is required to retry",
    message:
      "The last charge was a hard decline or fraud, or its result is unknown. Enter a reason of at least 10 characters to retry it.",
    retryable: true,
    nextSteps: ["Check TRX if the last result is unknown, then enter a reason of at least 10 characters."],
  },
  RETRY_TRX_ATTESTATION_REQUIRED: {
    severity: "user_input",
    title: "Check TRX before retrying",
    message:
      "The last charge's result is unknown — the card may already have been charged. Find it in TRX by its PurchaseId, then enter that PurchaseId to retry.",
    retryable: true,
    nextSteps: [
      "Search TRX for the PurchaseId shown on the charge.",
      "If it settled, don't retry — record the payment instead.",
      "If it did not settle, enter the PurchaseId and confirm it was not charged.",
    ],
  },
  RETRY_TRX_ATTESTATION_MISMATCH: {
    severity: "user_input",
    title: "That PurchaseId doesn't match",
    message:
      "That isn't the PurchaseId the last charge was sent to TRX with, so it can't confirm the charge was checked. Copy the PurchaseId shown for that charge and try again.",
    retryable: true,
  },
  BILLING_RETRY_PRIOR_ATTEMPT_STALE: {
    severity: "user_input",
    title: "The charge changed while you were looking",
    message: "Another charge attempt was recorded for this invoice. Refresh to see the latest attempt, then try again.",
    retryable: true,
  },
  INVOICE_RUN_NOT_FOUND: {
    severity: "user_input",
    title: "Invoice not found",
    message: "That monthly invoice doesn't exist for this business. Refresh and try again.",
    retryable: true,
  },
  INVOICE_RUN_NOT_RETRYABLE: {
    severity: "user_input",
    title: "Nothing to retry",
    message:
      "This invoice isn't waiting on a charge — it's paid, already being charged, or was never invoiced. Refresh to see its status.",
    retryable: false,
  },
  INVOICE_RUN_NOT_MONTHLY: {
    severity: "user_input",
    title: "Collected by bank debit",
    message:
      "This is a daily online-order invoice. It's collected by bank debit, so it can't be retried as a card charge here.",
    retryable: false,
  },
  BILLING_CHARGE_ALREADY_ACTIVE: {
    severity: "user_input",
    title: "A charge is already in progress",
    message: "A charge for this invoice is already in progress. Wait for it to finish, then refresh.",
    retryable: true,
  },
  // order-up-supabase 20261021192613: one live debit per invoice across the
  // SaaS charge worker's queue AND the Books ACH queue (both directions).
  BILLING_INVOICE_DEBIT_CONFLICT: {
    severity: "user_input",
    title: "This invoice is already being collected",
    message:
      "Another payment for this invoice is queued, being sent, or still clearing, so a second one wasn't set up. Wait for it to finish or fail, then refresh.",
    retryable: true,
  },
  BILLING_INVOICE_NOT_CHARGEABLE: {
    severity: "user_input",
    title: "This invoice can't be charged",
    message:
      "This invoice has no balance left, or isn't open (it may be paid, void, draft or retracted). Refresh to see its status.",
    retryable: false,
  },
  BILLING_IDEMPOTENCY_KEY_CONFLICT: {
    severity: "service",
    title: "The charge couldn't be queued",
    message: "Another charge was queued for this invoice at the same moment. Refresh, then try again if it's still unpaid.",
    retryable: true,
  },
  BILLING_INVOICE_ACCOUNT_MISMATCH: {
    severity: "service",
    title: "The invoice doesn't match this business",
    message: "This invoice is billed to a different customer than this business's billing account. Contact engineering.",
    retryable: false,
  },
  BILLING_CHARGE_ENQUEUE_FAILED: {
    severity: "service",
    title: "The charge couldn't be queued",
    message: "The charge couldn't be queued. Try again in a minute.",
    retryable: true,
  },

  // ── Platform pricing (pricing_config.set_feature_price) ──────────────
  // Raised by the SECURITY DEFINER RPC as P0001 with message = code, or by
  // PlatformPricingService's own guards; see
  // apps/api/components/routes/admin/platformPricing.ts.
  PRICE_VERSION_CONFLICT: {
    severity: "user_input",
    title: "This price changed since you loaded it",
    message:
      "Someone else changed this price a moment ago. Refresh to see the latest, then try again.",
    retryable: true,
    nextSteps: ["Refresh the page to see the latest price, then try again."],
  },
  INVALID_PRICE: {
    severity: "user_input",
    title: "That price isn't valid",
    message:
      "That price isn't valid. Check the amounts and the usage fields, then try again.",
    retryable: true,
    nextSteps: ["Check the amounts and the usage fields, then try again."],
  },
  HQ_BOOKS_NOT_CONFIGURED: {
    severity: "service",
    title: "HQ Books isn't set up",
    message:
      "HQ Books isn't set up on this server, so prices can't be saved. Contact engineering.",
    retryable: false,
  },
  // PlatformPricingService.setTierPrice refuses with this 409 BEFORE writing
  // the HQ product while feature_tiers lacks the tier-price columns
  // (supabase #512 not deployed), so no orphan product is left behind.
  TIER_PRICING_NOT_DEPLOYED: {
    severity: "service",
    title: "Tier pricing isn't on yet",
    message: "Tier pricing isn't switched on yet.",
    retryable: false,
  },
  // PlatformPricingService maps BookProductNameConflictError from the HQ
  // product write to this 409 (final whole-branch review m4).
  PLATFORM_PRODUCT_NAME_CONFLICT: {
    severity: "user_input",
    title: "Another HQ product has this name",
    message:
      "An HQ Books product already uses this feature's name. Rename that product, then save the price again.",
    retryable: true,
    nextSteps: ["Rename that product in HQ Books, then save the price again."],
  },

  // Raised by the admin feature-entitlements route itself (server-side
  // tenant/restaurant ownership check) before applyAction is ever called —
  // see apps/api/components/routes/admin/featureEntitlements.ts.
  RESTAURANT_NOT_FOUND: {
    severity: "user_input",
    title: "Location not found",
    message: "We couldn't find that location. Refresh and try again.",
    retryable: true,
    nextSteps: ["Refresh the page, then try again."],
  },
  // ── Location card payments (Site Admin, staff-only) ─────────────────
  // apps/api/components/routes/admin/location-card-payments.ts — the only
  // writer of a location's card-payment fields.
  CARD_PAYMENTS_CHANGED: {
    severity: "user_input",
    title: "This card setup changed",
    message:
      "Someone else changed this location’s card setup since you opened it. Your change was not saved.",
    retryable: true,
    nextSteps: ["Reload to see the current setup, then make your change again."],
  },
  CARD_PAYMENTS_INVALID: {
    severity: "user_input",
    title: "This card setup can't be saved",
    message:
      "The processor, credentials and settings don't fit together — for example, TRX selected without its credentials.",
    retryable: false,
    nextSteps: ["Fix the field named in the details, then save again."],
  },
  HOLD_REQUIRES_TRX: {
    severity: "user_input",
    title: "Holds need TRX",
    message:
      "A hold needs the charge to route to TRX. This location routes online card payments elsewhere.",
    retryable: false,
    nextSteps: [
      "Route online card payments to TRX first, or keep this surface on sale.",
    ],
  },
  SANDBOX_REQUIRES_TRX: {
    severity: "user_input",
    title: "Sandbox needs TRX",
    message: "Sandbox mode exists only on TRX. Set the processor to TRX first.",
    retryable: false,
    nextSteps: ["Set the default processor to TRX, then switch to sandbox."],
  },
  THREE_DS_NOT_AVAILABLE: {
    severity: "user_input",
    title: "3-D Secure isn't available",
    message:
      "3-D Secure is not built yet; turning it on would decline every card-not-present payment.",
    retryable: false,
    nextSteps: ["Leave 3-D Secure off."],
  },
  LOCATION_NOT_FOUND: {
    severity: "user_input",
    title: "Location not found",
    message: "No such location for this business. It may have been removed.",
    retryable: true,
    nextSteps: ["Refresh the page, then pick the location again."],
  },
  TENANT_RESTAURANT_MISMATCH: {
    severity: "user_input",
    title: "Location doesn't match",
    message:
      "That location doesn't belong to this business. Refresh and try again.",
    retryable: true,
    nextSteps: ["Refresh the page, then try again."],
  },
  // Nothing on file names the location's business (no denormalized
  // tenantGuid and no resolvable parent tenant doc) — a data problem, not a
  // mismatch the operator can fix by refreshing.
  RESTAURANT_TENANT_UNRESOLVED: {
    severity: "service",
    title: "Location has no business on file",
    message:
      "We couldn't tell which business this location belongs to. Contact engineering.",
    retryable: false,
  },
  // The route grandfathers an unrecorded location's current features before
  // a comp / end comp; if that could not complete, the write is refused so it
  // cannot switch the location's other features off.
  ENTITLEMENT_ENROLLMENT_INCOMPLETE: {
    severity: "service",
    title: "Features not recorded yet",
    message:
      "This location's current features couldn't be recorded yet. Try again in a moment.",
    retryable: true,
    nextSteps: ["Wait a moment, then try again."],
  },

  // ── Owner self-serve platform billing (apps/api/components/routes/billing/platformFeatures.ts) ──
  OWNER_REQUIRED: {
    severity: "auth",
    title: "Only the business owner can do this",
    message:
      "Only the owner of this business can start, stop or pay for OrderUp features. Ask your owner to make the change.",
    retryable: false,
  },
  PLATFORM_BILLING_IMPERSONATED_WRITE_DENIED: {
    severity: "auth",
    title: "Can't change billing while impersonating",
    message:
      "You're signed in as someone else, so you can look at this business's billing but not change it. Nothing was saved.",
    retryable: false,
    nextSteps: ["End the impersonation session. Staff changes to a business's features are made from HQ Books."],
  },
  DEFAULT_CARD_IN_USE: {
    severity: "user_input",
    title: "This card is paying for your features",
    message:
      "This is the card your features are billed to. Add another card and make it the default first, or cancel your paid features and trials.",
    retryable: false,
  },
  OWNER_ACTION_NOT_ALLOWED: {
    severity: "auth",
    title: "Only OrderUp can make this change",
    message: "This change can only be made by OrderUp. Contact OrderUp support.",
    retryable: false,
  },
  FEATURE_ENTITLEMENTS_DISABLED: {
    severity: "service",
    title: "Feature billing isn't available yet",
    message: "Starting and stopping OrderUp features from here isn't turned on yet.",
    retryable: false,
  },
  // A non-owner reads ONE location's features, so the page must name it.
  RESTAURANT_REQUIRED: {
    severity: "user_input",
    title: "Choose a location",
    message: "Choose a location first.",
    retryable: false,
  },

  // ── BAR BOTTLE REQUESTS (design 2026-09-27 §3.2) ────────────────────────────
  // A bottle request is refused until the close-match check is answered. The
  // 409 carries the matched rows in `details.matches`; the page renders them
  // with "Carry this instead", so the copy points at that action rather than
  // at "try again". The server sentence is written for whoever is reading it
  // (a manager filing, or a site admin approving), so it wins.
  BAR_BOTTLE_ALREADY_IN_CATALOG: {
    severity: "user_input",
    title: "This bottle is already in the catalog",
    message:
      "The catalog already has this bottle. Carry it instead of requesting it — choose \"Other size\" if your shelf size differs.",
    retryable: false,
    preferServerMessage: true,
  },
  BAR_BOTTLE_MATCHES_NOT_REVIEWED: {
    severity: "user_input",
    title: "Check these similar bottles first",
    message:
      "Similar bottles are already in the catalog. Look them over, and request a new one only if none of them is your bottle.",
    retryable: false,
    preferServerMessage: true,
  },
  BAR_BOTTLE_RETIRED: {
    severity: "user_input",
    title: "That bottle was retired",
    message:
      "This request's bottle was published and later retired. File a new request, or restore the bottle.",
    retryable: false,
    preferServerMessage: true,
  },
  // Approving an ingredient / allergy category that already exists. The 409's
  // `details.entityGuid` names the live row; the admin console offers "Link
  // to it" from there, which is the answer the sentence gives.
  PLATFORM_ENTITY_EXISTS: {
    severity: "user_input",
    title: "It already exists",
    message:
      "An active entry with this name already exists. Link the request to it instead of approving a duplicate.",
    retryable: false,
    preferServerMessage: true,
  },
  BAR_BOTTLE_PUBLISHED_DIFFERENTLY: {
    severity: "user_input",
    title: "Already published with different details",
    message:
      "This request was already published with different details. File a correction on that bottle instead of approving again.",
    retryable: false,
    preferServerMessage: true,
  },
  // "Through payroll" refused: no payroll_settings/default for the location.
  CASH_TIPS_PAYROLL_NOT_SET_UP: {
    severity: "onboarding",
    title: "Payroll is not set up",
    message:
      "Set up payroll first (Payroll \u2192 Settings), then choose Through payroll.",
    retryable: false,
  },
  // "Through payroll" refused by the availability switch (on since Part 3; kept for a rollback).
  CASH_TIPS_PAYROLL_NOT_AVAILABLE: {
    severity: "onboarding",
    title: "Through payroll is not available yet",
    message:
      "Through payroll becomes available once the till collects cash tips at checkout.",
    retryable: false,
  },
  // Card holds turned on at a location whose card processor cannot hold a card
  // on file (only TRX can: token re-auth + Reversal). docs/CARD_HOLDS.md.
  CARD_HOLDS_PROCESSOR_NOT_SUPPORTED: {
    severity: "onboarding",
    title: "Card holds need TRX",
    message:
      "Card holds work only when this location takes cards through TRX. Your settings were not changed.",
    retryable: false,
  },
  // The card-hold OPERATION codes (CARD_HOLD_* — docs/CARD_HOLDS.md §5a) are
  // deliberately NOT here. Only the POS (Dart, its own localized copy) calls
  // /payments/card-hold, and the server's `error.message` is plain-language.
  // This catalog ships in every web bundle (checkout's first download among
  // them), so copy no web surface can ever receive does not belong in it.
  // "Record cash tip-outs at clock-out" refused by the availability switch (on since Task 27; kept for a rollback; D10).
  CASH_TIPS_TIP_OUT_NOT_AVAILABLE: {
    severity: "onboarding",
    title: "Not available yet",
    message:
      "Recording cash tip-outs at clock-out becomes available once the till supports it.",
    retryable: false,
  },
  // A staff member changed direct deposit or a W-4 where payroll does not run on
  // OrderUp (`payroll_settings/default.runsOnOrderUp` is not true — NOT
  // `features.payroll`). Refused before anything was written or any manager told.
  PAYROLL_NOT_ON_ORDERUP: {
    severity: "user_input",
    title: "Payroll runs outside OrderUp here",
    message:
      "Your employer runs payroll outside OrderUp, so this change would not reach your paycheck. Nothing was changed.",
    retryable: false,
    nextSteps: ["Ask your manager how to change where your pay goes or how to update your tax withholding."],
  },
  // A manager decided a direct-deposit change that someone else (or the requester,
  // or a newer request) already settled. The page refreshes; nothing was changed.
  DIRECT_DEPOSIT_REQUEST_ALREADY_DECIDED: {
    severity: "user_input",
    title: "Already decided",
    message: "Another manager already decided this change.",
    retryable: false,
  },
  // Approve found the account a change names is no longer on file.
  DIRECT_DEPOSIT_ACCOUNT_NOT_ON_FILE: {
    severity: "user_input",
    title: "Account no longer on file",
    message:
      "The account this change names is no longer on file, so it was not applied. Reject the request and ask the employee to submit it again.",
    retryable: false,
  },
  // Approve would leave a direct-deposit employee with no active account.
  DIRECT_DEPOSIT_WOULD_LEAVE_NO_ACCOUNT: {
    severity: "user_input",
    title: "No account would be left",
    message:
      "Approving this would leave the employee with no active direct-deposit account, so it was not applied. Reject the request, or change how they are paid first.",
    retryable: false,
  },
  // ── Payroll tax deposit calendar (W7.2; docs/superpowers/plans/2026-10-31-payroll-deposit-calendar.md).
  // The two gate codes are `apps/api/components/routes/_shared/payrollMoneyGate.ts`'s; the
  // four record codes and their statuses are `PAYROLL_DEPOSIT_ERROR_STATUS` in
  // `packages/core/src/services/payroll/taxDeposits/payrollTaxDepositErrors.ts`. A refusal
  // there carries the server's own sentence, which names fields, so the catalog copy wins.
  // The same gate holds the net pay enrollment routes (paying employees through OrderUp), so
  // these two codes' copy names both, never tax deposits alone.
  // `initiative.payroll_money` is off for this tenant (403).
  PAYROLL_MONEY_NOT_ENABLED: {
    severity: "onboarding",
    title: "Not turned on yet",
    message: "Payroll tax deposits and paying employees through OrderUp aren't turned on for this business yet.",
    retryable: false,
  },
  // The flag could not be read (a missing flag service, no tenant guid, a throw): fail closed, 503.
  PAYROLL_MONEY_FLAG_UNAVAILABLE: {
    severity: "service",
    title: "Can't check right now",
    message:
      "We couldn't check whether payroll tax deposits and paying employees through OrderUp are on for this business.",
    retryable: true,
    nextSteps: ["Try again in a minute."],
  },
  // The deposit key is not in the calendar re-computed now (a later payroll changed it), or a
  // void named no live record at this location (404).
  PAYROLL_DEPOSIT_NOT_FOUND: {
    severity: "user_input",
    title: "This deposit has changed",
    message: "That deposit or record is no longer in the calendar, so nothing was changed.",
    retryable: false,
    nextSteps: ["Reload the page: a later payroll may have changed the deposit."],
  },
  // The deposit already has a live record (409).
  PAYROLL_DEPOSIT_ALREADY_RECORDED: {
    severity: "user_input",
    title: "Already recorded",
    message: "This deposit is already recorded as paid, so nothing was changed.",
    retryable: false,
    nextSteps: ["Reload the page to see the record."],
  },
  // The records table has not reached this database yet (503). The calendar still renders.
  PAYROLL_DEPOSIT_RECORDS_NOT_DEPLOYED: {
    severity: "service",
    title: "Recording deposits isn't available yet",
    message: "Deposits can't be recorded as paid here yet. The calendar still shows what is due, and nothing was changed.",
    retryable: false,
  },
  // A record the rules refuse: the date paid, an amount over $0, a 1-64 character
  // confirmation number, a 1-500 character void reason (400).
  PAYROLL_DEPOSIT_RECORD_INVALID: {
    severity: "user_input",
    title: "Deposit not recorded",
    message: "Part of this deposit record isn't valid, so nothing was changed.",
    retryable: false,
    nextSteps: [
      "Check the date paid, the amount paid (more than $0) and the confirmation number (up to 64 characters).",
      "To void a record, give a reason of up to 500 characters.",
    ],
  },
  // ── Paying employees through OrderUp: the net pay enrollment (payroll net pay design §5;
  // docs/superpowers/plans/2026-11-01-payroll-net-pay-enrollment.md). One entry per code of
  // `NET_PAY_ERROR_STATUS` (packages/core/src/services/payroll/netPay/netPayErrors.ts), plus
  // PAYROLL_NET_PAY_ADVANCE_FAILED: what the routes answer for an error that is not net pay's
  // own (R23), and what the opening sequence reports when it stopped for a reason it cannot
  // name. The server's sentences name wire fields and statuses, so the catalog copy wins.
  // The business reads most of these; staff read OWNER_DETAILS_MISSING, LIMIT_REQUIRED and
  // LIMIT_NEEDS_RESIGN; both read KYB_PENDING.
  // The tables have not reached this database yet (503). A read says "not available yet" instead.
  PAYROLL_NET_PAY_NOT_DEPLOYED: {
    severity: "service",
    title: "Not available yet",
    message: "Paying employees through OrderUp isn't available here yet.",
    retryable: false,
  },
  // The payroll money or money movement switch is off for the business, or the bank is not set up (409).
  PAYROLL_NET_PAY_NOT_AVAILABLE: {
    severity: "onboarding",
    title: "Not available yet",
    message: "Paying employees through OrderUp isn't available for this business yet.",
    retryable: false,
  },
  // The enrollment is not in the step this action needs, or another change landed first (409).
  PAYROLL_NET_PAY_STATE_CONFLICT: {
    severity: "user_input",
    title: "This enrollment has changed",
    message: "This enrollment is at a different step now, so nothing was changed.",
    retryable: false,
    nextSteps: ["Reload the page to see where it stands."],
  },
  // No enrollment with that id, or none for this location (404).
  PAYROLL_NET_PAY_NOT_FOUND: {
    severity: "user_input",
    title: "Enrollment not found",
    message: "We couldn't find a payroll enrollment for this location.",
    retryable: false,
    nextSteps: ["Reload the page."],
  },
  // An owner lacks a field the bank's identity check needs; details.owners names who and what (422).
  PAYROLL_NET_PAY_OWNER_DETAILS_MISSING: {
    severity: "user_input",
    title: "An owner's details are missing",
    message: "An owner is missing details the bank needs to verify the business, so nothing was sent for them.",
    retryable: false,
    nextSteps: ["Fill in what the owner is missing, then send them again."],
  },
  // The bank (Column) has not decided on the business yet (409). Not a fault: it can take days.
  PAYROLL_NET_PAY_KYB_PENDING: {
    severity: "service",
    title: "The bank is still verifying",
    message: "The bank is still verifying the business. The account opens once it finishes.",
    retryable: true,
    nextSteps: ["Check again later."],
  },
  // The account chosen in the bank link is not one the bank returned (422).
  PAYROLL_NET_PAY_BANK_ACCOUNT_NOT_FOUND: {
    severity: "user_input",
    title: "Account not found",
    message: "The account you chose wasn't among the ones your bank shared, so it wasn't linked.",
    retryable: false,
    nextSteps: ["Link your bank again and choose the account payroll is paid from."],
  },
  // Counsel has not published the payroll funding authorization, so nothing can be signed (409).
  PAYROLL_NET_PAY_AUTHORIZATION_NOT_PUBLISHED: {
    severity: "service",
    title: "The authorization isn't ready",
    message: "The payroll funding authorization isn't ready to sign yet.",
    retryable: false,
    nextSteps: ["Contact OrderUp."],
  },
  // Approve or a limit change without a payroll limit above $0, in whole cents (400).
  PAYROLL_NET_PAY_LIMIT_REQUIRED: {
    severity: "user_input",
    title: "Set a payroll limit",
    message: "Set a payroll limit above $0. Nothing was changed.",
    retryable: false,
  },
  // Staff turned two-day funding off for this location (409).
  PAYROLL_NET_PAY_TWO_DAY_LOCKED: {
    severity: "user_input",
    title: "Two-day funding is off",
    message: "OrderUp turned two-day funding off for this location, so it can't be turned on.",
    retryable: false,
    nextSteps: ["Contact OrderUp."],
  },
  // R11: the bank shares a tokenized account number, which stops working once the bank link
  // is removed (422). Word for word with NET_PAY_TOKENIZED_ACCOUNT_MESSAGE in core.
  PAYROLL_NET_PAY_TOKENIZED_ACCOUNT: {
    severity: "user_input",
    title: "Use a different account",
    message:
      "This bank shares an account number that only works while the bank stays connected to OrderUp. Link a different account, or contact OrderUp.",
    retryable: false,
  },
  // R13: the bank link did not prove the business owns the account (422).
  PAYROLL_NET_PAY_BANK_NOT_VERIFIED: {
    severity: "user_input",
    title: "Account not verified",
    message: "Your bank didn't confirm this account through the bank link, so it wasn't linked.",
    retryable: false,
    nextSteps: ["Link it again by signing in to your bank, or link a different account."],
  },
  // R29: the bank link named no bank, so the authorization could not say where it debits (422).
  PAYROLL_NET_PAY_BANK_NAME_UNKNOWN: {
    severity: "user_input",
    title: "Bank not recognized",
    message: "We couldn't tell which bank this account is at, so it wasn't linked.",
    retryable: false,
    nextSteps: ["Link a different account, or contact OrderUp."],
  },
  // Raised by the BROWSER, not the server: the bank link's widget finished with no account
  // shared, so there is nothing to bind (Payroll → Settings, the bank step).
  PAYROLL_NET_PAY_BANK_LINK_NO_ACCOUNTS: {
    severity: "user_input",
    title: "No account was shared",
    message: "Your bank didn't share an account with OrderUp, so nothing was linked.",
    retryable: false,
    nextSteps: ["Link your bank again and choose the account payroll is paid from."],
  },
  // The bank's record at the payroll bank is not active and verified, or is not this account (422).
  PAYROLL_NET_PAY_BANK_NOT_BINDABLE: {
    severity: "user_input",
    title: "This bank can't fund payroll",
    message: "This bank account can't be used for payroll right now.",
    retryable: false,
    nextSteps: ["Link it again, or contact OrderUp."],
  },
  // R27: the authorization the business accepted is not the one the server shows now (409).
  PAYROLL_NET_PAY_TERMS_CHANGED: {
    severity: "user_input",
    title: "The authorization changed",
    message: "The authorization changed after you opened it, so it wasn't signed.",
    retryable: false,
    nextSteps: ["Read the authorization again, then sign."],
  },
  // R28: the signed authorization names a lower limit (409). There is no re-sign step yet
  // (Plan 2), so the copy offers none.
  PAYROLL_NET_PAY_LIMIT_NEEDS_RESIGN: {
    severity: "user_input",
    title: "The limit can only go down",
    message:
      "This payroll limit can only be lowered after the authorization is signed. Contact OrderUp to raise it.",
    retryable: false,
  },
  // R45: OrderUp staff (a support or masquerade session, or a site admin who is not a member of
  // the business) asked for one of the business's own steps: its details, submit, the bank
  // link, the signature or two-day funding (403). Staff read it in the management app.
  PAYROLL_NET_PAY_BUSINESS_ONLY: {
    severity: "auth",
    title: "Only the business can do this",
    message:
      "Only the business can do this step, so OrderUp staff can't do it for them. Nothing was changed.",
    retryable: false,
    nextSteps: ["Ask an owner or admin of the business to do it while signed in as themselves."],
  },
  // R23: an error that was not net pay's own (a bank or provider failure, or one we cannot
  // name). The provider's own words are never shown: they could repeat what was sent.
  PAYROLL_NET_PAY_ADVANCE_FAILED: {
    severity: "service",
    title: "Something went wrong",
    message: "Something went wrong with this payroll enrollment.",
    retryable: true,
    nextSteps: ["Try again in a few minutes. If it keeps happening, contact OrderUp."],
  },
  // approvePayrollRun refused (cash tip modes R15): a cash tip was recorded,
  // edited or voided after the draft stubs were computed, so the stubs may not
  // include it. details.records names each one (employee, amount, guid).
  PAYROLL_CASH_TIPS_CHANGED: {
    severity: "user_input",
    title: "Cash tips changed",
    message:
      "A cash tip changed after this payroll was calculated \u2014 re-run payroll, then approve.",
    retryable: false,
  },
  // runPayroll refused (cash tip modes R16): a clock-out declaration is stamped
  // `total`, which payroll cannot count without paying that shift's check cash
  // tips twice. details.entries names each time entry.
  PAYROLL_CASH_TIP_DECLARATION_TOTAL: {
    severity: "user_input",
    title: "Cash tip declaration needs fixing",
    message:
      "A clock-out cash tip declaration was recorded as a total \u2014 fix it before running payroll.",
    retryable: false,
  },
  // ---- Cash tip Books postings (cash tip modes Part 3, supabase 20261002120200..120600).
  // Raised by the hand-in / clearing / sale-tip-clearing / tip-pool-funding RPCs that
  // payroll approval calls, the Unpaid tips ledger, and the tip-payout DOL fields.
  // Almost all are argument refusals from OUR caller, not something the operator
  // typed — so the copy says the payroll step was refused and nothing was posted,
  // and points at support rather than at a field the operator cannot see.
  BOOKS_CASH_TIP_TENANT_REQUIRED: {
    severity: "service",
    title: "Cash tips couldn't be posted",
    message:
      "The cash tip posting didn't say which restaurant it belongs to, so nothing was posted to Books.",
    retryable: false,
    nextSteps: ["Reload the page and approve payroll again.", "Contact support if it happens again."],
  },
  BOOKS_CASH_TIP_PAY_PERIOD_REQUIRED: {
    severity: "service",
    title: "Cash tips couldn't be posted",
    message:
      "The cash tip posting didn't name its pay period, so nothing was posted to Books.",
    retryable: false,
    nextSteps: ["Reload the payroll run and approve it again.", "Contact support if it happens again."],
  },
  BOOKS_CASH_TIP_EMPLOYEE_REQUIRED: {
    severity: "service",
    title: "Cash tips couldn't be posted",
    message:
      "A cash tip posting didn't name the employee it belongs to, so nothing was posted to Books.",
    retryable: false,
    nextSteps: ["Check every employee on this payroll run has a profile, then approve again.", "Contact support if it happens again."],
  },
  BOOKS_CASH_TIP_EMPLOYEE_NAME_REQUIRED: {
    severity: "service",
    title: "Cash tips couldn't be posted",
    message:
      "Tip pool funding needs the employee's name for its memo, and none was sent. Nothing was posted to Books.",
    retryable: false,
    nextSteps: ["Make sure the employee's profile has a name, then approve payroll again.", "Contact support if it happens again."],
  },
  BOOKS_CASH_TIP_NAN_AMOUNT: {
    severity: "service",
    title: "A cash tip amount wasn't a number",
    message:
      "A cash tip amount sent to Books wasn't a valid number, so nothing was posted.",
    retryable: false,
    nextSteps: ["Re-run payroll so the cash tips are recalculated, then approve.", "Contact support if it happens again."],
  },
  BOOKS_CASH_TIP_NEGATIVE_AMOUNT: {
    severity: "service",
    title: "A cash tip amount was below zero",
    message:
      "A cash tip amount below zero was refused, so nothing was posted to Books.",
    retryable: false,
    nextSteps: ["Check the cash tips recorded for this pay period, re-run payroll, then approve.", "Contact support if it happens again."],
  },
  BOOKS_CASH_TIP_NONPOSITIVE_AMOUNT: {
    severity: "service",
    title: "Tip pool funding must be more than zero",
    message:
      "Tip pool funding of zero or less was refused, so nothing was posted to Books.",
    retryable: false,
    nextSteps: ["Re-run payroll so the tip pool is recalculated, then approve.", "Contact support if it happens again."],
  },
  BOOKS_CASH_TIP_SUB_CENT_AMOUNT: {
    severity: "service",
    title: "A cash tip amount had fractions of a cent",
    message:
      "Books only posts whole cents, and a cash tip amount had more decimals than that. Nothing was posted.",
    retryable: false,
    nextSteps: ["Re-run payroll, then approve.", "Contact support if it happens again."],
  },
  BOOKS_CASH_TIP_DATE_REQUIRED: {
    severity: "service",
    title: "Cash tips couldn't be posted",
    message:
      "The cash tip posting had no date, so nothing was posted to Books.",
    retryable: false,
    nextSteps: ["Check the payroll run has a pay date, then approve again.", "Contact support if it happens again."],
  },
  BOOKS_CASH_TIP_REPLAY_AMOUNT_MISMATCH: {
    severity: "user_input",
    title: "These cash tips were already posted for a different amount",
    message:
      "Books already holds this pay period's cash tip posting at another amount. Posting again would change money that was already paid, so nothing was posted.",
    retryable: false,
    nextSteps: [
      "Check whether this pay period was already approved — its first posting stands.",
      "Correct a real difference with a journal entry, or contact support.",
    ],
  },
  BOOKS_CASH_TIP_TILL_EQUALS_TIPS_PAYABLE: {
    severity: "user_input",
    title: "Your cash account is set to Tips Payable",
    message:
      "Cash payments are mapped to the Tips Payable account, so a cash tip handed in would move money from Tips Payable to itself. Nothing was posted.",
    retryable: false,
    nextSteps: [
      "In Books → Settings, map cash payments to your till or cash account.",
      "Then approve payroll again.",
    ],
  },
  BOOKS_TIP_PAYOUT_INVALID_TIPPED_HOURS: {
    severity: "service",
    title: "Tipped hours weren't a valid number",
    message:
      "A tip payout's tipped hours weren't a finite number of hours, so it wasn't recorded.",
    retryable: false,
    nextSteps: ["Check the time entries for this pay period, re-run payroll, then approve.", "Contact support if it happens again."],
  },
  BOOKS_UNPAID_TIP_ROWS_NOT_ARRAY: {
    severity: "service",
    title: "Unpaid tips couldn't be recorded",
    message:
      "The list of unpaid tips sent to Books was malformed, so none were recorded.",
    retryable: false,
    nextSteps: ["Re-run payroll, then approve.", "Contact support if it happens again."],
  },
  BOOKS_UNPAID_TIP_INVALID_SOURCE: {
    severity: "service",
    title: "Unpaid tips couldn't be recorded",
    message:
      "An unpaid tip didn't say why it went unpaid, so none were recorded.",
    retryable: false,
    nextSteps: ["Re-run payroll, then approve.", "Contact support if it happens again."],
  },
  BOOKS_UNPAID_TIP_INVALID_AMOUNT: {
    severity: "service",
    title: "Unpaid tips couldn't be recorded",
    message:
      "An unpaid tip amount wasn't a whole number of cents above zero, so none were recorded.",
    retryable: false,
    nextSteps: ["Re-run payroll, then approve.", "Contact support if it happens again."],
  },
  BOOKS_UNPAID_TIP_INVALID_EMPLOYEE_ID: {
    severity: "service",
    title: "Unpaid tips couldn't be recorded",
    message:
      "An unpaid tip named an employee in a form Books couldn't read, so none were recorded.",
    retryable: false,
    nextSteps: ["Re-run payroll, then approve.", "Contact support if it happens again."],
  },
  BOOKS_UNPAID_TIP_EMPLOYEE_MISMATCH: {
    severity: "service",
    title: "Unpaid tips couldn't be recorded",
    message:
      "An unpaid tip's employee didn't match its reason — tips nobody earned name no employee, and the rest name one. None were recorded.",
    retryable: false,
    nextSteps: ["Re-run payroll, then approve.", "Contact support if it happens again."],
  },
  BOOKS_UNPAID_TIP_DUPLICATE_ROW: {
    severity: "service",
    title: "Unpaid tips couldn't be recorded",
    message:
      "The same unpaid tip was listed twice for one employee in this pay period, so none were recorded.",
    retryable: false,
    nextSteps: ["Re-run payroll, then approve.", "Contact support if it happens again."],
  },
  BOOKS_UNPAID_TIP_TENANT_MISMATCH: {
    severity: "service",
    title: "That unpaid tip belongs to another business",
    message:
      "An unpaid tip in this request belongs to a different business, so nothing was changed.",
    retryable: false,
    nextSteps: ["Reload the page and try again.", "Contact support if it happens again."],
  },
  BOOKS_UNPAID_TIP_RESOLVED_AMOUNT_MISMATCH: {
    severity: "user_input",
    title: "This unpaid tip was already settled at another amount",
    message:
      "An unpaid tip for this pay period was already assigned or paid at a different amount than payroll now reports. Nothing was changed, so Tips Payable still matches what was settled.",
    retryable: false,
    nextSteps: [
      "Check the Unpaid tips report for this pay period.",
      "Contact support to reconcile the difference.",
    ],
  },
  BOOKS_UNPAID_TIP_INVALID_STATUS: {
    severity: "service",
    title: "That isn't a valid unpaid-tip status",
    message:
      "An unpaid tip can be marked assigned or paid, and the request asked for something else. Nothing was changed.",
    retryable: false,
    nextSteps: ["Reload the Unpaid tips report and try again.", "Contact support if it happens again."],
  },
  BOOKS_UNPAID_TIP_NOTE_REQUIRED: {
    severity: "user_input",
    title: "Add a note",
    message:
      "Say how these tips were assigned or paid before marking them.",
    retryable: false,
    nextSteps: ["Enter a short note — who received the tips and how — then save again."],
  },
  BOOKS_UNPAID_TIP_NOT_FOUND: {
    severity: "user_input",
    title: "That unpaid tip no longer exists",
    message:
      "The unpaid tip you picked couldn't be found. It may have been removed by a payroll re-run.",
    retryable: false,
    nextSteps: ["Reload the Unpaid tips report."],
  },
  BOOKS_UNPAID_TIP_NOT_OPEN: {
    severity: "user_input",
    title: "That unpaid tip was already settled",
    message:
      "This unpaid tip was already marked by someone else. Paid tips stay paid, and assigned tips can only move to paid.",
    retryable: false,
    nextSteps: ["Reload the Unpaid tips report to see its current status."],
  },

  // ── ROADMAP (CRM → Issues → Roadmap) ───────────────────────────────────────
  // Raised by `ops.*_roadmap_*` RPCs (migration 20261003002031), surfaced by
  // `roadmapService.ts` and `routes/admin/roadmap.ts`. Staff-facing copy.
  OPS_ROADMAP_TITLE_REQUIRED: {
    severity: "user_input",
    title: "Give the item a title",
    message: "A roadmap item needs a title. Add one and save again.",
    retryable: false,
  },
  OPS_ROADMAP_PATCH_UNKNOWN_KEY: {
    severity: "user_input",
    title: "That field cannot be changed here",
    message:
      "The change included a field the roadmap does not accept. Reload the page and try the edit again.",
    retryable: false,
  },
  OPS_ROADMAP_ITEM_NOT_FOUND: {
    severity: "user_input",
    title: "Roadmap item not found",
    message:
      "That roadmap item no longer exists. It may have been removed — go back to the roadmap and pick it again.",
    retryable: false,
  },
  OPS_ROADMAP_TARGET_REASON_REQUIRED: {
    severity: "user_input",
    title: "Say why the target moved",
    message:
      "Moving a target date that was already set needs a reason. Add one and save again — it is kept in the item's history.",
    retryable: false,
  },
  OPS_ROADMAP_HEALTH_REQUIRED: {
    severity: "user_input",
    title: "Pick a health",
    message: "A check-in needs a health: on track, at risk or off track.",
    retryable: false,
  },
  OPS_ROADMAP_NOTE_REQUIRED: {
    severity: "user_input",
    title: "Add a note",
    message:
      "A check-in needs a short note saying why. Add one and check in again.",
    retryable: false,
  },
  OPS_ROADMAP_MILESTONE_NAME_REQUIRED: {
    severity: "user_input",
    title: "Name the milestone",
    message: "A milestone needs a name. Add one and save again.",
    retryable: false,
  },
  OPS_ROADMAP_MILESTONE_DUE_REQUIRED: {
    severity: "user_input",
    title: "Pick a due date",
    message: "A milestone needs a due date. Pick one and save again.",
    retryable: false,
  },
  OPS_ROADMAP_MILESTONE_NOT_FOUND: {
    severity: "user_input",
    title: "Milestone not found",
    message:
      "That milestone no longer exists on this item. Reload the page to see the current list.",
    retryable: false,
  },
  OPS_ROADMAP_MILESTONE_STATE_INVALID: {
    severity: "user_input",
    title: "That milestone change is not allowed",
    message:
      "A milestone can only be open, done or canceled. Reload the page and try again.",
    retryable: false,
  },
  OPS_ROADMAP_CALENDAR_RANGE_INVALID: {
    severity: "user_input",
    title: "Check the date range",
    message: "The end of the date range must be after its start.",
    retryable: false,
  },
  OPS_ROADMAP_CALENDAR_RANGE_TOO_WIDE: {
    severity: "user_input",
    title: "Date range too wide",
    message: "Pick a shorter date range — at most 400 days at a time.",
    retryable: false,
  },

  // ── CALENDAR FEEDS (subscribe to the roadmap in Google / Apple / Outlook) ──
  // Raised by migration 20261003002032 and `calendarFeedService.ts`.
  OPS_CALENDAR_FEED_OWNER_REQUIRED: {
    severity: "auth",
    title: "Sign in again",
    message:
      "We could not tell who owns this calendar feed. Sign out and back in, then try again.",
    retryable: false,
  },
  OPS_CALENDAR_FEED_LABEL_INVALID: {
    severity: "user_input",
    title: "Check the feed name",
    message: "Give the feed a name of 1 to 80 characters.",
    retryable: false,
  },
  OPS_CALENDAR_FEED_SCOPE_INVALID: {
    severity: "user_input",
    title: "Check what the feed includes",
    message:
      "Choose what this feed should include — for example your own work or one roadmap area — and try again.",
    retryable: false,
  },
  OPS_CALENDAR_FEED_TOKEN_HASH_INVALID: {
    severity: "service",
    title: "Could not create the feed",
    message:
      "Something went wrong creating the feed's private link. Try again; nothing was saved.",
    retryable: true,
  },
  OPS_CALENDAR_FEED_TOKEN_PREFIX_INVALID: {
    severity: "service",
    title: "Could not create the feed",
    message:
      "Something went wrong creating the feed's private link. Try again; nothing was saved.",
    retryable: true,
  },
  OPS_CALENDAR_FEED_LIMIT: {
    severity: "user_input",
    title: "Too many calendar feeds",
    message:
      "You already have 10 live calendar feeds. Revoke one you no longer use, then create this one.",
    retryable: false,
  },
  OPS_CALENDAR_FEED_REASON_TOO_LONG: {
    severity: "user_input",
    title: "Reason too long",
    message: "Keep the reason to 200 characters or fewer and try again.",
    retryable: false,
  },
  OPS_CALENDAR_FEED_NOT_FOUND: {
    severity: "user_input",
    title: "Calendar feed not found",
    message:
      "That calendar feed no longer exists or was already revoked. Reload the page to see your feeds.",
    retryable: false,
  },
  OPS_CALENDAR_FEED_RANGE_INVALID: {
    severity: "user_input",
    title: "Check the date range",
    message: "The end of the date range must be after its start.",
    retryable: false,
  },
  OPS_CALENDAR_FEED_RANGE_TOO_WIDE: {
    severity: "user_input",
    title: "Date range too wide",
    message: "Pick a shorter date range — at most 400 days at a time.",
    retryable: false,
  },
  // runPayroll / approvePayrollRun refused (ruling P12): the run's tax year (its
  // pay date's) is EARLIER than a year an employee's YTD totals already belong
  // to. details: runTaxYear, ytdTaxYear, employee.
  PAYROLL_YTD_YEAR_ORDER: {
    severity: "user_input",
    title: "Payroll year is out of order",
    message:
      "A later year's payroll is already approved for an employee \u2014 payrolls must be run and approved in pay-date order.",
    retryable: false,
  },
  // runPayroll refused: pre-flight raised a warning the manager had not acknowledged
  // (a new one since the preview). The server sentence names the codes, so it wins.
  // details: codes, employeeIds.
  PAYROLL_WARNINGS_NOT_ACKNOWLEDGED: {
    severity: "user_input",
    title: "Payroll has warnings to review",
    message: "Payroll has new warnings you have not reviewed. Preview the payroll again, review each warning, then run it.",
    retryable: false,
    preferServerMessage: true,
  },
  // runPayroll refused (payroll defects Task 5 I-1): payroll settings' state tax
  // jurisdiction is not a US state / DC / territory, so no home-state income tax
  // could be withheld from anyone. details.value is what is stored.
  SETTINGS_STATE_TAX_UNRECOGNISED: {
    severity: "user_input",
    title: "State tax jurisdiction not recognised",
    message:
      "The state tax jurisdiction in payroll settings is not a recognised US state or territory \u2014 correct it in payroll settings, then run payroll.",
    retryable: false,
  },
  // updateFormStatus refused (Task 9): a 941 with validation errors may not be
  // marked final or filed. The server sentence lists every error, so it wins.
  TAX_FORM_941_HAS_ERRORS: {
    severity: "user_input",
    title: "Form 941 has errors",
    message: "This Form 941 has errors and cannot be marked final or filed until they are fixed. Correct the pay stubs, then refresh the form.",
    retryable: false,
    preferServerMessage: true,
  },
  // ── PAYROLL ACTIONS THAT SUCCEEDED WITH NOTES ──────────────────────────────
  // Not refusals: the action happened. Management lists the server's specific
  // sentences under this heading (apps/management PayrollActionWarnings), so
  // `message` says only what kind of thing follows and what to do with it.
  PAYROLL_APPROVED_WITH_WARNINGS: {
    severity: "service",
    title: "Payroll approved \u2014 review these notes",
    message: "The payroll was approved. Review each note below before paying.",
    retryable: false,
  },
  PAYROLL_VOIDED_WITH_WARNINGS: {
    severity: "service",
    title: "Payroll voided \u2014 review what it could not reverse",
    message: "The payroll was voided, but some of it could not be reversed. Correct each item below by hand.",
    retryable: false,
  },
  PAYROLL_YTD_RECOMPUTED_WITH_WARNINGS: {
    severity: "service",
    title: "Year-to-date wages rebuilt \u2014 review these notes",
    message: "Year-to-date Social Security, Medicare and FUTA wages were rebuilt from approved pay stubs. Review each note below.",
    retryable: false,
  },
  PAYROLL_ACCUMULATION_REDRIVEN_WITH_WARNINGS: {
    severity: "service",
    title: "Pending year-to-date totals applied \u2014 review these notes",
    message: "Approved pay stubs whose year-to-date totals had not been applied were applied. Review each note below.",
    retryable: false,
  },

  // ── POS ROLLBACK (Site Admin → Devices → Releases) ──────────────────────────
  // A rollback dispatches a BUILD in order-up-pos; none of these mean a build
  // ran. REFUSED carries the specific reason (halted target, already newest,
  // unknown version), so the server sentence wins.
  POS_ROLLBACK_REFUSED: {
    severity: "user_input",
    title: "This release cannot be rolled back to",
    message: "Pick an older release that was not pulled.",
    retryable: false,
    preferServerMessage: true,
  },
  POS_ROLLBACK_UNAVAILABLE: {
    severity: "service",
    title: "Rollback is not set up on this server",
    message: "The release-dispatch GitHub App is not configured on this server, so nothing was started. Run the Roll back POS release workflow from GitHub instead.",
    retryable: false,
  },
  POS_ROLLBACK_DISPATCH_FAILED: {
    severity: "service",
    title: "The rollback build did not start",
    message: "GitHub did not accept the request, so nothing is building. Run the Roll back POS release workflow from GitHub, or try again.",
    retryable: true,
    preferServerMessage: true,
  },

  // ── ONLINE SERVICE FEE (management → Settings → Fees) ───────────────────────
  // The owner's acknowledgment of OrderUp's per-order charge
  // (routes/onlineServiceFee.ts). Accepting is an agreement to be billed, so
  // every refusal says exactly what happens next — never a generic 409/503.
  ONLINE_SERVICE_FEE_PRICE_CHANGED: {
    severity: "user_input",
    title: "OrderUp's per-order price changed",
    message: "The price changed while you were reviewing it. Nothing was accepted — review the new price and accept again.",
    retryable: false,
  },
  BILLING_ACCOUNT_REQUIRED: {
    severity: "user_input",
    title: "Set up billing first",
    message: "This business has no OrderUp billing account yet, so nothing was accepted. Set up billing (or ask OrderUp support to), then accept again.",
    retryable: false,
  },
  ONLINE_ORDER_FEE_BILLING_ACCOUNT_MISMATCH: {
    severity: "user_input",
    title: "Billed to a different account",
    message: "This location is already enrolled on a different billing account. Contact OrderUp support to move it — nothing was accepted.",
    retryable: false,
  },
  ONLINE_SERVICE_FEE_IMPERSONATED_WRITE_DENIED: {
    severity: "auth",
    title: "Only the owner can accept",
    message: "Only the owner can accept this — impersonation can't.",
    retryable: false,
  },
  // Billing enrolled; only the checkout's mirror is behind. Both halves are
  // idempotent at the same price, so the retry is safe.
  ONLINE_SERVICE_FEE_MIRROR_PENDING: {
    severity: "service",
    title: "Accepted — finishing setup",
    message: "Your acceptance is recorded, but turning the fee on didn't finish. Try again — you won't be charged twice.",
    retryable: true,
  },
  ONLINE_SERVICE_FEE_OWNER_REQUIRED: {
    severity: "auth",
    title: "Only the owner can accept",
    message: "Only the business owner can accept OrderUp's per-order charge.",
    retryable: false,
  },
  ONLINE_SERVICE_FEE_ENROLLMENT_UNAVAILABLE: {
    severity: "service",
    title: "Online-order fees can't be set up right now",
    message: "Online-order fees can't be set up right now. Nothing was accepted — try again later.",
    retryable: true,
  },

  // ── ONLINE ORDER FEES (Site Admin → Locations → Online order fees) ─────────
  // Staff "Billing risk tier" / cadence / Stop billing
  // (apps/api/components/routes/admin/online-order-fees.ts). The SQL raises the
  // code; the route maps it.
  ONLINE_ORDER_FEE_NOT_ENROLLED: {
    severity: "user_input",
    title: "Not enrolled in online-order fees",
    message: "This location's owner has not accepted OrderUp's per-order fee, so there is nothing to change. Nothing was saved.",
    retryable: false,
  },
  ONLINE_ORDER_FEE_CHANGED: {
    severity: "user_input",
    title: "This billing risk tier changed",
    message: "Someone else changed this location's billing risk tier since you opened it. Your change was not saved.",
    retryable: true,
    nextSteps: ["Reload to see the current tier, then make your change again."],
  },
  ONLINE_ORDER_FEE_OVERRIDE_REASON_REQUIRED: {
    severity: "user_input",
    title: "Give a reason for the cadence override",
    message: "A billing cadence that differs from the tier's own needs its own reason of at least 10 characters. Nothing was saved.",
    retryable: false,
  },
  ONLINE_ORDER_FEE_INVALID_RISK_TIER: {
    severity: "user_input",
    title: "Choose a billing risk tier",
    message: "Choose low, medium or high. Nothing was saved.",
    retryable: false,
  },
  ONLINE_ORDER_FEE_INVALID_CADENCE: {
    severity: "user_input",
    title: "Choose a billing cadence",
    message: "Choose monthly or daily. Nothing was saved.",
    retryable: false,
  },
  ONLINE_ORDER_FEE_IMPERSONATED_WRITE_DENIED: {
    severity: "auth",
    title: "Can't change billing while impersonating",
    message: "You're signed in as someone else, so you can look at this location's online-order fees but not change them. Nothing was saved.",
    retryable: false,
    nextSteps: ["End the impersonation session, then make the change as yourself."],
  },
  // Site Admin → Pricing → "Online order fee per plan"
  // (apps/api/components/routes/admin/pricing.ts). The route renames the
  // shared INVALID_PRICE to its own code: that copy names per-unit usage fields.
  ONLINE_ORDER_FEE_INVALID_PRICE: {
    severity: "user_input",
    title: "That price isn't valid",
    message: "Enter whole cents above $0.00, up to $999.99. To stop charging a location, use Stop billing on its Online order fees page. Nothing was saved.",
    retryable: false,
  },
  ONLINE_ORDER_FEE_PRICE_IMPERSONATED_WRITE_DENIED: {
    severity: "auth",
    title: "Can't change prices while impersonating",
    message: "You're signed in as someone else, so the online order fee price can't be changed. Nothing was saved.",
    retryable: false,
    nextSteps: ["End the impersonation session, then make the change as yourself."],
  },
  // Site Admin → Pricing → "Sales tax rates" (apps/api/components/routes/admin/pricing.ts):
  // the tax OrderUp charges on its own invoices, per state. The route renames
  // the SQL refusals of set/delete_hq_sales_tax_rate to these codes.
  HQ_SALES_TAX_INVALID_STATE: {
    severity: "user_input",
    title: "That isn't a state code",
    message: "Enter a 2-letter US state code, like CT. Nothing was saved.",
    retryable: false,
  },
  HQ_SALES_TAX_INVALID_RATE: {
    severity: "user_input",
    title: "That rate isn't valid",
    message: "Enter a rate above 0% and below 20%, with at most 3 decimals. To stop taxing a state, remove its rate instead. Nothing was saved.",
    retryable: false,
  },
  HQ_SALES_TAX_INVALID_TAX_NAME: {
    severity: "user_input",
    title: "Name the tax",
    message: "Enter the tax's name as it should print on invoices, like \"Connecticut sales tax\" (up to 100 characters). Nothing was saved.",
    retryable: false,
  },
  HQ_SALES_TAX_ACTOR_REQUIRED: {
    severity: "auth",
    title: "We couldn't tell who made this change",
    message: "Sales tax changes are recorded with who made them, and your sign-in didn't say. Nothing was saved.",
    retryable: true,
    nextSteps: ["Sign out and back in, then try again."],
  },
  HQ_SALES_TAX_IMPERSONATED_WRITE_DENIED: {
    severity: "auth",
    title: "Can't change sales tax while impersonating",
    message: "You're signed in as someone else, so sales tax rates can't be changed. Nothing was saved.",
    retryable: false,
    nextSteps: ["End the impersonation session, then make the change as yourself."],
  },
  HQ_SALES_TAX_RATES_UNAVAILABLE: {
    severity: "service",
    title: "Sales tax rates couldn't be loaded",
    message: "The sales tax rates couldn't be read right now. Invoices are not posted until they can be. Try again in a minute.",
    retryable: true,
  },
  UNKNOWN_PLAN_KEY: {
    severity: "user_input",
    title: "That plan doesn't exist",
    message: "That plan doesn't exist, so no price was saved. Reload the page and choose a plan from the list.",
    retryable: false,
    nextSteps: ["Reload the page and choose a plan from the list."],
  },
  DEFAULT_PRICE_REQUIRED: {
    severity: "user_input",
    title: "The default price can't be removed",
    message: "The default price applies to every plan without its own price, so it can't be removed. Change its amount instead.",
    retryable: false,
  },
  // Stop billing switches the guest fee off FIRST; when that fails nothing
  // else ran — billing is unchanged — so a retry is safe.
  ONLINE_ORDER_FEE_STOP_GUEST_FEE_PENDING: {
    severity: "service",
    title: "Billing not stopped",
    message: "The guest fee at checkout could not be switched off, so billing was not stopped. Nothing changed — try again.",
    retryable: true,
  },
  // Pricing tier (owner decision 2026-10-08): the route orders the guest-fee
  // and tier writes so a partial failure never bills more than guests pay.
  ONLINE_ORDER_FEE_TIER_UNAVAILABLE: {
    severity: "service",
    title: "Pricing tiers aren't available yet",
    message: "The database update that adds pricing tiers hasn't been applied yet. Nothing changed.",
    retryable: true,
    nextSteps: ["Try again after the next platform update."],
  },
  ONLINE_ORDER_FEE_TIER_NOT_CHANGED: {
    severity: "service",
    title: "Pricing tier not changed",
    message: "The pricing tier couldn't be changed. Nothing changed — try again.",
    retryable: true,
  },
  ONLINE_ORDER_FEE_TIER_INCOMPLETE: {
    severity: "service",
    title: "Pricing tier change half done",
    message: "Part of the pricing tier change was saved, but not all of it. The saved part never bills more than guests pay. Try again to finish it.",
    retryable: true,
  },
  ONLINE_ORDER_FEE_ACTOR_REQUIRED: {
    severity: "auth",
    title: "We couldn't tell who made this change",
    message: "Pricing tier changes are recorded with who made them, and your sign-in didn't say. Nothing was saved.",
    retryable: true,
    nextSteps: ["Sign out and back in, then try again."],
  },
  ONLINE_ORDER_FEE_INVALID_TIER: {
    severity: "user_input",
    title: "Choose a pricing tier",
    message: "Choose Standard or Free online ordering. Nothing was saved.",
    retryable: false,
  },
  ONLINE_ORDER_FEE_TIER_PRICE_REQUIRED: {
    severity: "user_input",
    title: "The free online ordering price can't be removed",
    message: "Every location on the free online ordering tier is billed this price, so it can't be removed. Change its amount instead.",
    retryable: false,
  },
};

/**
 * Frozen catalog. Keys are normalized codes (Firebase `code` strings,
 * `appCheck/*`, `onboarding/*`, AppError class names, and API-envelope code
 * constants). Resolution order lives in `translateError`.
 */
export const ERROR_CATALOG: Readonly<Record<string, CatalogEntry>> =
  Object.freeze(ENTRIES);
